import {
  EventBus,
  PDFLinkService,
  PDFViewer,
  RenderingStates,
} from "pdfjs-dist/legacy/web/pdf_viewer.mjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
} from "react-icons/fa";
import crossIcon from "../../assets/icons/cross.svg";
import notesIcon from "../../assets/icons/notes.svg";
import useReaderStore from "../../context/readerStore";
import {
  useIsMobile,
  useIsTablet,
  useIsTouchTablet,
} from "../../hooks/useMediaQuery";
import {
  apiFormatToStroke,
  saveFreehandDrawing,
} from "../../services/drawingService";
import {
  getCopyCharLimit,
  isCopyAllowed,
  isFeatureAllowed,
  isPageInViewableRange,
  shouldIsolateCoverInDoublePage,
  truncateForCopy,
} from "../../services/drmPolicyService";
import {
  createHighlight,
  editHighlight,
  refreshHighlightsFromApi,
  removeHighlightById,
} from "../../services/highlightService";
import {
  deleteFreehandDrawing as deleteDrawingRaw,
  getSessionTokens,
  getValidRstToken,
} from "../../services/igpService";
import {
  createNote,
  editNote,
  refreshNotesFromApi,
} from "../../services/noteService";
import {
  showCopyLimitExceededNotification,
  showCopyNotAllowedNotification,
  showFeatureNotAllowedNotification,
  showOperationFailedNotification,
} from "../../services/notificationService";
import {
  createPdfUnderline,
  deletePdfUnderline,
  refreshUnderlinesFromApi,
} from "../../services/pdfUnderlineService";
import {
  createTextbox,
  deleteTextbox,
  refreshTextboxesFromApi,
  updateTextbox,
} from "../../services/textboxService";
import { getHighlightColorOptions } from "../../constants/highlightColors";
import useSelectionTranslation, {
  buildTranslationAnchor,
} from "../../hooks/useSelectionTranslation";
import { getAiTargetLanguage } from "../../services/aiService";
import {
  getBasePageSize,
  normalizeRotation,
  rotateNormalizedPoint,
  rotateNormalizedRect,
  rotatePoint,
  rotateRect,
  unrotateNormalizedPoint,
  unrotateNormalizedRect,
  unrotatePoint,
  unrotateRect,
} from "../../utils/pdfRotation";
import ContextMenu from "../ContextMenu/ContextMenu";
import TranslationPopover from "../ContextMenu/TranslationPopover";
import Pullouts from "../Pullouts/Pullouts";
import "./PdfViewerNew.css";

const CHUNK_SIZE = 5;

/** Live PDF.js pagesRotation, falling back to store (session-only, always 0 on open). */
const readViewerRotation = (pdfViewer, storeRotation = 0) => {
  const live = pdfViewer?.pagesRotation;
  if (Number.isFinite(live)) return normalizeRotation(live);
  return normalizeRotation(storeRotation);
};

class ChunkedPdfProxy {
  constructor(baseDoc, totalPages, fetchChunk, drmPolicy = null) {
    this._baseDoc = baseDoc;
    this._fetchChunk = fetchChunk;
    this.numPages = totalPages;
    this.fingerprints = baseDoc?.fingerprints ?? [null, null];
    this._drmPolicy = drmPolicy;
  }

  // ── Core method PDF.js calls for every page it needs to render ────────────
  async getPage(pageNumber) {
    if (
      this._drmPolicy &&
      !isPageInViewableRange(this._drmPolicy, pageNumber)
    ) {
      throw new Error(
        `ChunkedPdfProxy: page ${pageNumber} is outside DRM viewable range`,
      );
    }
    const chunkIndex = Math.floor((pageNumber - 1) / CHUNK_SIZE);
    const relativePage = ((pageNumber - 1) % CHUNK_SIZE) + 1;

    const chunkDoc = await this._fetchChunk(chunkIndex);
    if (!chunkDoc) {
      throw new Error(
        `ChunkedPdfProxy: chunk ${chunkIndex} for page ${pageNumber} failed to load`,
      );
    }
    return chunkDoc.getPage(relativePage);
  }

  // ── Metadata methods — delegate to base doc (chunk 0) ────────────────────
  getDestinations() {
    return this._baseDoc?.getDestinations?.() ?? Promise.resolve({});
  }
  getOutline() {
    return this._baseDoc?.getOutline?.() ?? Promise.resolve(null);
  }
  getOpenAction() {
    return this._baseDoc?.getOpenAction?.() ?? Promise.resolve(null);
  }
  getPermissions() {
    return this._baseDoc?.getPermissions?.() ?? Promise.resolve(null);
  }
  getOptionalContentConfig(options) {
    return (
      this._baseDoc?.getOptionalContentConfig?.(options) ??
      Promise.resolve(null)
    );
  }
  getFieldObjects() {
    return this._baseDoc?.getFieldObjects?.() ?? Promise.resolve(null);
  }
  getCalculationOrderIds() {
    return this._baseDoc?.getCalculationOrderIds?.() ?? Promise.resolve(null);
  }
  getJSActions() {
    return this._baseDoc?.getJSActions?.() ?? Promise.resolve(null);
  }
  hasJSActions() {
    return this._baseDoc?.hasJSActions?.() ?? Promise.resolve(false);
  }
  get annotationStorage() {
    return this._baseDoc?.annotationStorage ?? null;
  }
  get loadingParams() {
    // Force PDF.js page-level lazy loading for chunked documents.
    // This prevents PDF.js from eager-fetching all pages/chunks when using a proxy.
    return {
      ...this._baseDoc?.loadingParams,
      disableAutoFetch: true,
    };
  }
  getMetadata() {
    return (
      this._baseDoc?.getMetadata?.() ??
      Promise.resolve({ info: {}, metadata: null })
    );
  }
  getMarkInfo() {
    return this._baseDoc?.getMarkInfo?.() ?? Promise.resolve(null);
  }
  getViewerPreferences() {
    return this._baseDoc?.getViewerPreferences?.() ?? Promise.resolve(null);
  }
  getPageLabels() {
    return this._baseDoc?.getPageLabels?.() ?? Promise.resolve(null);
  }
  getPageLayout() {
    return this._baseDoc?.getPageLayout?.() ?? Promise.resolve(null);
  }
  getPageMode() {
    return this._baseDoc?.getPageMode?.() ?? Promise.resolve(null);
  }
  getAttachments() {
    return this._baseDoc?.getAttachments?.() ?? Promise.resolve(null);
  }
  getJavaScript() {
    return this._baseDoc?.getJavaScript?.() ?? Promise.resolve(null);
  }
  getPageIndex(ref) {
    return this._baseDoc?.getPageIndex?.(ref) ?? Promise.resolve(0);
  }
  getData() {
    return this._baseDoc?.getData?.() ?? Promise.resolve(new Uint8Array(0));
  }
  getStats() {
    return { streamTypes: {}, fontTypes: {} };
  }
  cleanup(keepFonts) {
    return this._baseDoc?.cleanup?.(keepFonts);
  }

  // Do NOT destroy chunks — they are owned by the store
  destroy() {}
}

const PAGE_SYNC_EPSILON = 0.0001;
const PDF_CSS_UNITS = 96 / 72;
const UNDO_REDO_DEBOUNCE_MS = 400;
const NOTE_EDITOR_ESTIMATED_WIDTH = 300;
const NOTE_EDITOR_ESTIMATED_HEIGHT = 300;
const toApiUnderlineId = (underlineId) => {
  if (!underlineId || typeof underlineId !== "string") return underlineId;
  return underlineId.replace(/-rect-\d+$/, "") || underlineId;
};

const underlineRectsMatch = (a, b, tolerance = 2) =>
  Math.abs(Number(a?.left || 0) - Number(b?.left || 0)) <= tolerance &&
  Math.abs(Number(a?.top || 0) - Number(b?.top || 0)) <= tolerance &&
  Math.abs(Number(a?.width || 0) - Number(b?.width || 0)) <= tolerance &&
  Math.abs(Number(a?.height || 1) - Number(b?.height || 1)) <= tolerance;

const underlineRectsOverlap = (a, b, tolerance = 2) => {
  const aLeft = Number(a?.left || 0);
  const aRight = aLeft + Number(a?.width || 0);
  const bLeft = Number(b?.left || 0);
  const bRight = bLeft + Number(b?.width || 0);
  const horizontal = aLeft <= bRight + tolerance && bLeft <= aRight + tolerance;

  const aBaseline = Number(a?.top || 0) + Number(a?.height || 1);
  const bBaseline = Number(b?.top || 0) + Number(b?.height || 1);
  const vertical = Math.abs(aBaseline - bBaseline) <= tolerance;

  return horizontal && vertical;
};

const mergeAdjacentHighlightRects = (rects, lineThreshold = 5) => {
  if (!Array.isArray(rects) || rects.length <= 1) return rects || [];

  const sorted = [...rects].sort((a, b) => {
    const topDiff = Number(a?.top || 0) - Number(b?.top || 0);
    if (Math.abs(topDiff) > lineThreshold) return topDiff;
    return Number(a?.left || 0) - Number(b?.left || 0);
  });

  const merged = [];
  let current = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i += 1) {
    const next = sorted[i];
    const currentRight = Number(current.left || 0) + Number(current.width || 0);
    const nextRight = Number(next.left || 0) + Number(next.width || 0);
    const currentBottom =
      Number(current.top || 0) + Number(current.height || 0);
    const nextBottom = Number(next.top || 0) + Number(next.height || 0);

    const sameLine =
      Math.abs(Number(current.top || 0) - Number(next.top || 0)) <=
      lineThreshold;
    const adjacent = Number(next.left || 0) <= currentRight + 2;

    if (sameLine && adjacent) {
      const left = Math.min(Number(current.left || 0), Number(next.left || 0));
      const right = Math.max(currentRight, nextRight);
      const top = Math.min(Number(current.top || 0), Number(next.top || 0));
      const bottom = Math.max(currentBottom, nextBottom);
      current = {
        ...current,
        left,
        top,
        width: right - left,
        height: bottom - top,
      };
    } else {
      merged.push(current);
      current = { ...next };
    }
  }

  merged.push(current);
  return merged;
};

/**
 * Screen-px slack for "true overlap" checks.
 * Keep tiny so adjacent words separated by a normal space are NOT treated as
 * overlapping (case: double-click "digital" then "consumer").
 */
const HIGHLIGHT_TRUE_OVERLAP_TOLERANCE_PX = 1;

const highlightRectsOverlapWithScale = (a, b, tolerancePx = 3, scale = 1) => {
  const safeScale = Number(scale) > 0 ? Number(scale) : 1;
  const aLeftPx = Number(a?.left || 0) * safeScale;
  const aRightPx = (Number(a?.left || 0) + Number(a?.width || 0)) * safeScale;
  const bLeftPx = Number(b?.left || 0) * safeScale;
  const bRightPx = (Number(b?.left || 0) + Number(b?.width || 0)) * safeScale;

  const horizontal =
    aLeftPx <= bRightPx + tolerancePx && bLeftPx <= aRightPx + tolerancePx;

  const aBaselinePx =
    (Number(a?.top || 0) + Number(a?.height || 0)) * safeScale;
  const bBaselinePx =
    (Number(b?.top || 0) + Number(b?.height || 0)) * safeScale;
  const vertical =
    Math.abs(aBaselinePx - bBaselinePx) <= Math.max(2, tolerancePx);

  return horizontal && vertical;
};

const highlightRectFullyCoversWithScale = (
  cover,
  target,
  tolerancePx = 1,
  scale = 1,
) => {
  const safeScale = Number(scale) > 0 ? Number(scale) : 1;
  const coverLeft = Number(cover?.left || 0) * safeScale;
  const coverRight =
    (Number(cover?.left || 0) + Number(cover?.width || 0)) * safeScale;
  const targetLeft = Number(target?.left || 0) * safeScale;
  const targetRight =
    (Number(target?.left || 0) + Number(target?.width || 0)) * safeScale;

  const horizOK =
    coverLeft <= targetLeft + tolerancePx &&
    coverRight >= targetRight - tolerancePx;

  const coverBaseline =
    (Number(cover?.top || 0) + Number(cover?.height || 0)) * safeScale;
  const targetBaseline =
    (Number(target?.top || 0) + Number(target?.height || 0)) * safeScale;
  const vertOK =
    Math.abs(coverBaseline - targetBaseline) <= Math.max(2, tolerancePx);

  return horizOK && vertOK;
};

const mergeHighlightRects = (a, b, options = {}) => {
  const left = Math.min(Number(a?.left || 0), Number(b?.left || 0));
  const right = Math.max(
    Number(a?.left || 0) + Number(a?.width || 0),
    Number(b?.left || 0) + Number(b?.width || 0),
  );
  const top = Math.min(Number(a?.top || 0), Number(b?.top || 0));
  const bottom = Math.max(
    Number(a?.top || 0) + Number(a?.height || 0),
    Number(b?.top || 0) + Number(b?.height || 0),
  );

  const preferColor = options?.preferColor;
  let color = preferColor || a?.color || b?.color || "#FFF59D";
  if (!preferColor) {
    const timeA = Date.parse(a?.timestamp || "") || 0;
    const timeB = Date.parse(b?.timestamp || "") || 0;
    if (timeB > timeA) {
      color = b?.color || a?.color || "#FFF59D";
    } else if (timeA > timeB) {
      color = a?.color || b?.color || "#FFF59D";
    }
  }

  const timestamp =
    (Date.parse(a?.timestamp || "") || 0) >= (Date.parse(b?.timestamp || "") || 0)
      ? a?.timestamp || b?.timestamp || null
      : b?.timestamp || a?.timestamp || null;

  return {
    id: a?.id || b?.id || `highlight-${Date.now()}-${Math.random()}`,
    parentId: a?.parentId || b?.parentId || a?.id || b?.id,
    left,
    top,
    width: right - left,
    height: Math.max(1, bottom - top),
    text: a?.text || b?.text || "",
    color,
    timestamp,
    pageNumber: a?.pageNumber || b?.pageNumber,
  };
};

/**
 * Best-effort content merge when overlapping highlights are replaced.
 * Prefers containment, then suffix/prefix overlap join (EPUB-like union text).
 */
const mergeHighlightContentStrings = (primary, others = []) => {
  let result = String(primary || "").trim();

  (Array.isArray(others) ? others : []).forEach((raw) => {
    const other = String(raw || "").trim();
    if (!other) return;
    if (!result) {
      result = other;
      return;
    }
    if (result.includes(other)) return;
    if (other.includes(result)) {
      result = other;
      return;
    }

    const maxOverlap = Math.min(result.length, other.length);
    let prefixOverlap = 0;
    for (let n = maxOverlap; n > 0; n -= 1) {
      if (other.slice(-n) === result.slice(0, n)) {
        prefixOverlap = n;
        break;
      }
    }
    if (prefixOverlap > 0) {
      result = other + result.slice(prefixOverlap);
      return;
    }

    let suffixOverlap = 0;
    for (let n = maxOverlap; n > 0; n -= 1) {
      if (result.slice(-n) === other.slice(0, n)) {
        suffixOverlap = n;
        break;
      }
    }
    if (suffixOverlap > 0) {
      result = result + other.slice(suffixOverlap);
    }
  });

  return result;
};

const normalizeHighlightsByPage = (highlights) => {
  const byPage = new Map();
  (Array.isArray(highlights) ? highlights : []).forEach((hl) => {
    const pageNumber = Number(hl?.page_number || 1);
    const pageRects = byPage.get(pageNumber) || [];
    const coords = Array.isArray(hl?.coordinates)
      ? hl.coordinates
      : hl?.coordinates
        ? [hl.coordinates]
        : [];

    coords.forEach((coord) => {
      pageRects.push({
        left: Number(coord?.left || 0),
        top: Number(coord?.top || 0),
        width: Number(coord?.width || 0),
        height: Number(coord?.height || 0),
        color: hl?.color || "#FFF59D",
        text: hl?.content || hl?.text || "",
        id: hl?.id || null,
        parentId: hl?.parentId || hl?.id || null,
        timestamp: hl?.timestamp || null,
      });
    });

    byPage.set(pageNumber, pageRects);
  });
  return byPage;
};

const normalizeUnderlinesByPage = (underlines) => {
  const byPage = new Map();
  (Array.isArray(underlines) ? underlines : []).forEach((ul) => {
    const pageNumber = Number(ul?.page_number || 1);
    const pageRects = byPage.get(pageNumber) || [];
    const coords = Array.isArray(ul?.coordinates)
      ? ul.coordinates
      : ul?.coordinates
        ? [ul.coordinates]
        : [];

    coords.forEach((coord) => {
      pageRects.push({
        left: Number(coord?.left || 0),
        top: Number(coord?.top || 0),
        width: Number(coord?.width || 0),
        height: Number(coord?.height || 1),
        text: ul?.content || ul?.text || "",
        id: ul?.id || null,
        parentId: ul?.parentId || ul?.id || null,
      });
    });

    byPage.set(pageNumber, pageRects);
  });
  return byPage;
};

const normalizeNotesByPage = (notes) => {
  const byPage = new Map();
  (Array.isArray(notes) ? notes : []).forEach((note) => {
    const pageNumber = Number(note?.page_number || 1);
    byPage.set(pageNumber, (byPage.get(pageNumber) || 0) + 1);
  });
  return byPage;
};

const mapNotesByPage = (notes) => {
  const byPage = new Map();
  (Array.isArray(notes) ? notes : []).forEach((note) => {
    const pageNumber = Number(note?.page_number || 1);
    byPage.set(pageNumber, [...(byPage.get(pageNumber) || []), note]);
  });
  return byPage;
};

const normalizeDrawingsByPage = (drawings) => {
  const byPage = new Map();
  (Array.isArray(drawings) ? drawings : []).forEach((item) => {
    const stroke = apiFormatToStroke(item);
    const pageNumber = Number(stroke?.page_number || item?.page_number || 1);
    if (!stroke?.points?.length) return;
    byPage.set(pageNumber, [
      ...(byPage.get(pageNumber) || []),
      {
        id: stroke.id,
        points: stroke.points,
        lineWidth: 1,
        color: "#ff0000",
      },
    ]);
  });
  return byPage;
};

const getSafeViewportBounds = () => {
  const viewport = window.visualViewport;
  const viewportWidth = viewport?.width ?? window.innerWidth;
  const viewportHeight = viewport?.height ?? window.innerHeight;
  const viewportTopInset = viewport?.offsetTop ?? 0;
  const margin = 12;

  const getVisibleElementHeight = (selector, isBottom = false) => {
    const el = document.querySelector(selector);
    if (!el) return 0;
    const style = window.getComputedStyle(el);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      Number(style.opacity) === 0
    ) {
      return 0;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return 0;
    if (isBottom) {
      if (rect.top >= viewportHeight) return 0;
      return Math.max(0, viewportHeight - Math.max(rect.top, 0));
    }
    if (rect.bottom <= 0) return 0;
    return Math.max(
      0,
      Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
    );
  };

  const getVisibleElementWidth = (selector) => {
    const el = document.querySelector(selector);
    if (!el) return 0;
    const style = window.getComputedStyle(el);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      Number(style.opacity) === 0
    ) {
      return 0;
    }
    const rect = el.getBoundingClientRect();
    return rect.width > 0 ? rect.width : 0;
  };

  const reservedTop = Math.max(
    getVisibleElementHeight(".mobile-top-bar"),
    getVisibleElementHeight(".toolbar"),
  );
  const reservedBottom = getVisibleElementHeight(".mobile-bottom-bar", true);
  const viewportScrollbarWidth = Math.max(
    0,
    window.innerWidth - document.documentElement.clientWidth,
  );
  const tocOpenWidth = getVisibleElementWidth(
    ".reader-content > .table-of-contents.open",
  );
  // Desktop/tablet: TOC opens from the left. Mobile: TOC still opens from the right.
  const tocOpensFromLeft = window.innerWidth > 768;

  const reservedRight =
    getVisibleElementWidth(".right-sidebar") +
    [
      ".reader-content > .pages-thumbnails.open",
      ".reader-content > .bookmarks.open",
      ".reader-content > .notes.open",
      ".reader-content > .search-sidebar.open",
      ".reader-content > .reading-assistant.open",
      ".reader-content > .publication-details.open",
    ].reduce((total, selector) => total + getVisibleElementWidth(selector), 0) +
    (tocOpensFromLeft ? 0 : tocOpenWidth) +
    viewportScrollbarWidth +
    8;

  const reservedLeft = tocOpensFromLeft ? tocOpenWidth : 0;

  const minX = reservedLeft + margin;
  const minY = viewportTopInset + reservedTop + margin;
  const maxX = viewportWidth - reservedRight - margin;
  const maxY = viewportHeight - reservedBottom - margin;

  return { minX, minY, maxX, maxY };
};

const isEditableKeyboardTarget = (target) => {
  if (!target || !(target instanceof Element)) return false;
  if (target.isContentEditable) return true;
  return Boolean(
    target.closest("input, textarea, select, [contenteditable='true']"),
  );
};

const isNodeInsidePdfTextLayer = (node) => {
  if (!node) return false;
  const element =
    node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  return Boolean(element?.closest?.(".textLayer"));
};

/** True when any selection endpoint lives under `root` (PDF viewer / container). */
const isSelectionAnchoredIn = (selection, root) => {
  if (!selection || !root) return false;
  for (const node of [selection.anchorNode, selection.focusNode]) {
    if (!node) continue;
    const element =
      node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    if (element && root.contains(element)) return true;
  }
  return false;
};

const isTouchCapableDevice = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

/**
 * Keep PDF.js `.endOfContent` as a static non-selectable barrier at the end of
 * the text layer (Legacy touch behavior). PDF.js otherwise repositions/expands
 * it on selectionchange, which pans zoomed mobile overflow containers.
 */
const hardenTextLayerEndOfContent = (textLayerDiv) => {
  if (!textLayerDiv) return;
  const end =
    textLayerDiv.querySelector(":scope > .endOfContent") ||
    textLayerDiv.querySelector(".endOfContent");
  if (!end) return;

  if (textLayerDiv.lastElementChild !== end) {
    textLayerDiv.appendChild(end);
  }

  end.style.width = "";
  end.style.height = "";
  end.style.userSelect = "none";
  end.style.webkitUserSelect = "none";
  end.style.webkitTouchCallout = "none";
  textLayerDiv.classList.remove("selecting");
};

const hardenAllTextLayerEndOfContent = (viewerRoot) => {
  viewerRoot?.querySelectorAll?.(".textLayer")?.forEach((layer) => {
    hardenTextLayerEndOfContent(layer);
  });
};

const getPageNumberFromDomNode = (node) => {
  if (!node) return null;
  const element = node instanceof Element ? node : node?.parentElement || null;
  if (!element) return null;
  const pageEl = element.closest?.(".page[data-page-number]");
  if (!pageEl) return null;
  const pageNumber = Number(pageEl.getAttribute("data-page-number") || 0);
  return pageNumber > 0 ? pageNumber : null;
};

const getPdfTextLayerForPage = (pdfViewer, viewerEl, pageNumber) => {
  if (!pageNumber) return null;
  const pageView = pdfViewer?.getPageView?.(pageNumber - 1);
  const fromPageView = pageView?.textLayer?.div;
  if (fromPageView) return fromPageView;
  if (!viewerEl) return null;
  const pageEl = viewerEl.querySelector(
    `.page[data-page-number="${pageNumber}"]`,
  );
  return pageEl?.querySelector?.(".textLayer") || null;
};

const resolveSelectAllPageNumber = ({
  pdfViewer,
  evt,
  lastInteractionPage,
  currentPageFallback,
}) => {
  const fromFocus = getPageNumberFromDomNode(document.activeElement);
  if (fromFocus) return fromFocus;

  const fromTarget = getPageNumberFromDomNode(evt?.target);
  if (fromTarget) return fromTarget;

  const selection = window.getSelection();
  if (selection?.anchorNode) {
    const fromSelection = getPageNumberFromDomNode(selection.anchorNode);
    if (fromSelection) return fromSelection;
  }

  if (Number.isInteger(lastInteractionPage) && lastInteractionPage > 0) {
    return lastInteractionPage;
  }

  return (
    Number(pdfViewer?.currentPageNumber) || Number(currentPageFallback) || 1
  );
};

const buildSelectAllRangeForTextLayer = (textLayer) => {
  if (!textLayer) return null;

  const walker = document.createTreeWalker(textLayer, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (!parent || parent.classList.contains("endOfContent")) {
        return NodeFilter.FILTER_REJECT;
      }
      const marked = parent.closest?.(".markedContent");
      if (marked?.getAttribute?.("aria-hidden") === "true") {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const firstText = walker.nextNode();
  if (!firstText) return null;

  let lastText = firstText;
  for (let next = walker.nextNode(); next; next = walker.nextNode()) {
    lastText = next;
  }

  const range = document.createRange();
  range.setStart(firstText, 0);
  range.setEnd(lastText, lastText.nodeValue?.length || 0);
  return range;
};

const applySelectAllOnPdfTextLayer = (textLayer) => {
  const range = buildSelectAllRangeForTextLayer(textLayer);
  if (!range) return false;

  const selection = window.getSelection();
  if (!selection) return false;

  textLayer
    .closest?.(".pdfViewer")
    ?.querySelectorAll?.(".textLayer")
    ?.forEach((layer) => layer.classList.remove("selecting"));
  textLayer.classList.add("selecting");

  selection.removeAllRanges();
  selection.addRange(range);

  if (typeof textLayer.focus === "function") {
    try {
      textLayer.focus({ preventScroll: true });
    } catch {
      textLayer.focus();
    }
  }

  return !selection.isCollapsed;
};

const PdfViewerNew = () => {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const pdfViewerRef = useRef(null);
  const eventBusRef = useRef(null);
  const linkServiceRef = useRef(null);
  const selectionOverlayRafRef = useRef(0);
  const renderLiveSelectionOverlayRef = useRef(() => {});
  const syncPageRef = useRef(false);
  const rotationSettlingRef = useRef(false);
  const syncScaleRef = useRef(false);
  const currentPageRef = useRef(1);
  const lastInteractionPdfPageRef = useRef(null);
  const scaleRef = useRef(1);
  const renderReadOnlyOverlaysRef = useRef(() => {});
  const lastAppliedSpreadModeRef = useRef(null);
  const lastAppliedSelectionRef = useRef({ signature: "", ts: 0 });
  const latestSelectionPayloadRef = useRef(null);
  const suppressNotesClickUntilRef = useRef(0);
  const didApplyInitialFitModeRef = useRef(false);
  const zoomFocalPointRef = useRef(null);
  const activeDrawingRef = useRef(null);
  const pendingDrawingsRef = useRef({});
  const hiddenDrawingIdsRef = useRef(new Set());
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const [selectionRect, setSelectionRect] = useState(null);
  const [noteEditorVisible, setNoteEditorVisible] = useState(false);
  const [noteEditorPosition, setNoteEditorPosition] = useState({ x: 0, y: 0 });
  const [noteEditorText, setNoteEditorText] = useState("");
  const [noteEditorPayload, setNoteEditorPayload] = useState(null);
  const [noteEditorMode, setNoteEditorMode] = useState("create");
  const [noteEditorNoteId, setNoteEditorNoteId] = useState(null);
  const [noteEditorPageNumber, setNoteEditorPageNumber] = useState(null);
  const [noteEditorSelectedText, setNoteEditorSelectedText] = useState("");
  const [expandedSelectedText, setExpandedSelectedText] = useState(false);
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const [pdfPagesReady, setPdfPagesReady] = useState(false);
  const noteTextareaRef = useRef(null);
  const noteEditorPanelRef = useRef(null);
  const noteEditorPlacementRef = useRef(null);
  const [selectedTextboxId, setSelectedTextboxId] = useState(null);
  const [editingTextboxId, setEditingTextboxId] = useState(null);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [selectedUnderline, setSelectedUnderline] = useState(null);
  const selectedHighlightRef = useRef(null);
  const selectedUnderlineRef = useRef(null);
  // After selecting an existing annotation, ignore selection-pipeline closes briefly
  const suppressSelectionMenuCloseUntilRef = useRef(0);
  const textboxDraftRef = useRef("");
  const activeTextboxDragRef = useRef(null);
  const suppressTextboxCreateUntilRef = useRef(0);
  const { t } = useTranslation();
  const { translationState, closeTranslationPopover, runTranslation } =
    useSelectionTranslation();

  useEffect(() => {
    selectedHighlightRef.current = selectedHighlight;
  }, [selectedHighlight]);

  useEffect(() => {
    selectedUnderlineRef.current = selectedUnderline;
  }, [selectedUnderline]);

  const isSafariSelectionGuardEnabled = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const vendor = navigator.vendor || "";
    const isAppleVendor = /Apple/i.test(vendor);
    const isSafariEngine =
      /Safari/i.test(ua) &&
      !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|SamsungBrowser/i.test(ua);
    return isAppleVendor && isSafariEngine;
  }, []);

  const syncPageLoadingIndicators = useCallback(() => {
    const pdfViewer = pdfViewerRef.current;
    if (!pdfViewer) return;

    const pageCount = Number(pdfViewer.pagesCount || 0);
    if (!pageCount) return;

    // Performance: only check pages near the viewport for large documents.
    // Off-screen pages aren't visible so their loading indicators don't matter
    // until the user scrolls to them (which triggers this function again).
    const INDICATOR_BUFFER = 10;
    const visiblePageIndex = Math.max(
      0,
      (Number(pdfViewer.currentPageNumber) || 1) - 1,
    );
    const startIndex = Math.max(0, visiblePageIndex - INDICATOR_BUFFER);
    const endIndex = Math.min(
      pageCount,
      visiblePageIndex + INDICATOR_BUFFER + 1,
    );

    for (let pageIndex = startIndex; pageIndex < endIndex; pageIndex += 1) {
      const pageView = pdfViewer.getPageView(pageIndex);
      const pageElement = pageView?.div;
      if (!pageElement) continue;

      const isLoaded = pageElement.hasAttribute("data-loaded");
      const shouldShowLoading =
        !isLoaded && pageView?.renderingState !== RenderingStates.FINISHED;

      pageElement.classList.toggle("loadingIcon", shouldShowLoading);
      pageElement.classList.toggle("loading", shouldShowLoading);
    }
  }, []);

  const {
    document: pdfDocument,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    scale,
    setScale,
    pdfRotation,
    documentType,
    readerViewMode,
    pageViewMode,
    setReaderViewMode,
    setSearchQuery,
    searchQuery,
    searchResults,
    setSearchSidebarOpen,
    isChunked,
    annotations,
    drawingEnabled: drawingEnabledFromStore,
    notesEnabled,
    setNotesEnabled,
    isTocSidebarOpen,
    isBookmarksSidebarOpen,
    isPagesSidebarOpen,
    isNotesSidebarOpen,
    isSearchSidebarOpen,
    isReadingAssistantSidebarOpen,
    isPublicationDetailsSidebarOpen,
    textboxEnabled,
    highlightEnabled,
    underlineEnabled,
    highlightColor,
    drmPolicy,
    metadata, // ← ADD
    documentId,
    initialViewStartPage,
    clearInitialViewStartPage,
    setTextboxEnabled,
    setHighlightColor,
    openReadingAssistantAskAi,
  } = useReaderStore();

  // Drawing is temporarily disabled in this viewer.
  void drawingEnabledFromStore;
  const drawingEnabled = false;

  const fetchChunk = useCallback(async (chunkIndex) => {
    // Read live state directly so we never capture a stale loadedChunks snapshot
    const state = useReaderStore.getState();

    if (state.loadedChunks?.[chunkIndex]) {
      return state.loadedChunks[chunkIndex];
    }

    if (state.chunkLoadPromises?.[chunkIndex]) {
      return state.chunkLoadPromises[chunkIndex];
    }

    try {
      const doc = await state.loadChunk?.(chunkIndex);
      return doc ?? null;
    } catch (err) {
      console.error(`ChunkedPdfProxy: failed to load chunk ${chunkIndex}`, err);
      return null;
    }
  }, []); // stable — reads store directly at call-time

  const effectivePdfDocument = useMemo(() => {
    if (!pdfDocument) return null;

    if (!isChunked || !metadata) return pdfDocument;

    // Resolve the true total page count from whichever field the store uses
    const knownTotal =
      metadata?.totalPages ??
      metadata?.total_pages ??
      metadata?.numPages ??
      totalPages ??
      0;

    if (!knownTotal || knownTotal <= pdfDocument.numPages) {
      // Metadata not ready yet, or single-chunk book — use raw document
      return pdfDocument;
    }

    return new ChunkedPdfProxy(pdfDocument, knownTotal, fetchChunk, drmPolicy);
  }, [pdfDocument, isChunked, metadata, fetchChunk, drmPolicy, totalPages]);

  useEffect(() => {
    if (!isChunked || !metadata || !effectivePdfDocument) return;

    const currentChunk = Math.floor((currentPage - 1) / CHUNK_SIZE);
    const totalPagesCount =
      metadata?.total_pages ?? metadata?.totalPages ?? totalPages;
    const totalChunks = Math.ceil(totalPagesCount / CHUNK_SIZE);
    const chunksToWarm = [currentChunk, currentChunk + 1].filter(
      (idx) => idx >= 0 && idx < totalChunks,
    );

    chunksToWarm.forEach((idx) => {
      const state = useReaderStore.getState();
      if (!state.loadedChunks?.[idx]) {
        // Fire-and-forget — don't await, don't block renders
        state.loadChunk?.(idx).catch(() => {});
      }
    });
  }, [currentPage, isChunked, metadata, effectivePdfDocument]);

  // ── localStorage: reset restore flag when document changes ─────────────
  const hasRestoredFromStorageRef = useRef(false);
  useEffect(() => {
    hasRestoredFromStorageRef.current = false;
  }, [pdfDocument]);

  // ── localStorage: restore saved page on first load ──────────────────────
  useEffect(() => {
    if (!pdfDocument || !documentId || hasRestoredFromStorageRef.current)
      return;
    // App initial_view already merges localStorage — don't fight it.
    if (initialViewStartPage != null) {
      hasRestoredFromStorageRef.current = true;
      return;
    }
    hasRestoredFromStorageRef.current = true;
    try {
      const raw = localStorage.getItem(`igp_reader_state_${documentId}`);
      if (!raw) return;
      const savedPage = Number(JSON.parse(raw)?.currentPage);
      if (!Number.isInteger(savedPage) || savedPage <= 1) return;
      currentPageRef.current = savedPage;
      setCurrentPage(savedPage);
    } catch (_) {}
  }, [pdfDocument, documentId, setCurrentPage, initialViewStartPage]);

  // ── localStorage: persist page + scale with 1s debounce ─────────────────
  useEffect(() => {
    if (!documentId) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(
          `igp_reader_state_${documentId}`,
          JSON.stringify({
            currentPage,
            scale,
            documentId,
            lastUpdated: Date.now(),
          }),
        );
      } catch (_) {}
    }, 1000);
    return () => clearTimeout(timer);
  }, [currentPage, scale, documentId]);

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isTouchTablet = useIsTouchTablet();
  const allowDoublePage = !isMobile && !isTablet && !isTouchTablet;
  const isDoublePageMode = allowDoublePage && pageViewMode === "double";
  // DRM viewing_rules.first_page: "cover" → PDF.js EVEN (page 1 alone, then 2–3…)
  // "default" / missing → ODD (1–2, 3–4…) — current behavior
  const isolateCoverInDoublePage = shouldIsolateCoverInDoublePage(drmPolicy);
  const minScale = isTouchTablet ? 1.0 : 0.5;
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const undoRedoInProgressRef = useRef(false);
  const lastUndoRedoRunRef = useRef({ undo: 0, redo: 0 });

  const silentDeleteDrawing = useCallback(async (drawingId) => {
    try {
      const { grantId, agtToken } = getSessionTokens();
      const rstToken = await getValidRstToken(grantId, agtToken);
      await deleteDrawingRaw(grantId, rstToken, drawingId);
    } catch (err) {
      console.error("Undo/Redo: silent delete drawing failed", err);
    }
  }, []);

  const createUnderlineNoRefresh = useCallback(
    async ({ id, pageNumber, content, coordinates }) => {
      try {
        createPdfUnderline({
          id,
          page_number: pageNumber,
          content: content || "",
          coordinates: Array.isArray(coordinates)
            ? coordinates.map((coord) => ({
                left: Number(coord?.left || 0),
                top: Number(coord?.top || 0),
                width: Number(coord?.width || 0),
                height: Number(coord?.height || 1),
              }))
            : [],
        });
        return true;
      } catch (err) {
        console.error("Undo/Redo: fast create underline failed", err);
        return false;
      }
    },
    [],
  );

  const silentCreateUnderline = useCallback(
    async (pageNumber, underline) => {
      if (!underline) return false;
      const coordinates = Array.isArray(underline.coordinates)
        ? underline.coordinates
        : [
            {
              left: underline.left,
              top: underline.top,
              width: underline.width,
              height: underline.height,
            },
          ];

      return createUnderlineNoRefresh({
        id:
          underline.parentId ||
          underline.id ||
          `underline-${Date.now()}-${Math.random()}-${pageNumber}`,
        pageNumber,
        content: underline.text || "",
        coordinates,
      });
    },
    [createUnderlineNoRefresh],
  );

  const highlightsByPage = useMemo(
    () => normalizeHighlightsByPage(annotations?.highlights?.data),
    [annotations?.highlights?.data],
  );
  const underlinesByPage = useMemo(
    () => normalizeUnderlinesByPage(annotations?.underlines?.data),
    [annotations?.underlines?.data],
  );
  const notesByPage = useMemo(
    () => normalizeNotesByPage(annotations?.notes?.data),
    [annotations?.notes?.data],
  );
  const notesListByPage = useMemo(
    () => mapNotesByPage(annotations?.notes?.data),
    [annotations?.notes?.data],
  );
  const drawingsByPage = useMemo(() => new Map(), []);
  const textboxes = useMemo(
    () =>
      Array.isArray(annotations?.textboxes?.data)
        ? annotations.textboxes.data
        : [],
    [annotations?.textboxes?.data],
  );
  const textboxesByPage = useMemo(() => {
    const byPage = new Map();
    textboxes.forEach((tb) => {
      const pageNumber = Number(tb?.page_number || 1);
      byPage.set(pageNumber, [...(byPage.get(pageNumber) || []), tb]);
    });
    return byPage;
  }, [textboxes]);
  const textboxesById = useMemo(() => {
    const map = new Map();
    textboxes.forEach((tb) => {
      if (tb?.id) {
        map.set(tb.id, tb);
      }
    });
    return map;
  }, [textboxes]);
  const searchResultsByPage = useMemo(() => {
    const byPage = new Map();
    (Array.isArray(searchResults) ? searchResults : []).forEach((result) => {
      const pageNumber = Number(result?.pageNumber || result?.page_number || 0);
      if (!pageNumber) return;
      byPage.set(pageNumber, [...(byPage.get(pageNumber) || []), result]);
    });
    return byPage;
  }, [searchResults]);

  const noteEditorPageNotes = useMemo(() => {
    if (!noteEditorPageNumber) return [];
    return notesListByPage.get(noteEditorPageNumber) || [];
  }, [noteEditorPageNumber, notesListByPage]);
  const noteEditorCurrentIndex = useMemo(() => {
    if (!noteEditorNoteId) return -1;
    return noteEditorPageNotes.findIndex((n) => n?.id === noteEditorNoteId);
  }, [noteEditorNoteId, noteEditorPageNotes]);
  const noteEditorSelectedPreview = useMemo(
    () => noteEditorSelectedText || noteEditorPayload?.selectedText || "",
    [noteEditorPayload?.selectedText, noteEditorSelectedText],
  );

  const getClampedNoteEditorPosition = useCallback((anchor, options = {}) => {
    if (!anchor) return { x: 0, y: 0 };

    const panelEl = noteEditorPanelRef.current;
    const panelWidth =
      Number(options.width) ||
      Number(panelEl?.offsetWidth) ||
      NOTE_EDITOR_ESTIMATED_WIDTH;
    const panelHeight =
      Number(options.height) ||
      Number(panelEl?.offsetHeight) ||
      NOTE_EDITOR_ESTIMATED_HEIGHT;
    const centerOnX = options.centerOnX !== false;
    const offsetY = Number(options.offsetY || 0);
    const bounds = getSafeViewportBounds();
    const xBase = centerOnX ? anchor.x - panelWidth / 2 : anchor.x;
    const yBase = anchor.y + offsetY;
    const maxLeft = Math.max(bounds.minX, bounds.maxX - panelWidth);
    const maxTop = Math.max(bounds.minY, bounds.maxY - panelHeight);

    return {
      x: Math.min(Math.max(xBase, bounds.minX), maxLeft),
      y: Math.min(Math.max(yBase, bounds.minY), maxTop),
    };
  }, []);

  const placeNoteEditor = useCallback(
    (anchor, options = {}) => {
      if (!anchor) return;

      const placement = {
        anchor,
        centerOnX: options.centerOnX !== false,
        offsetY: Number(options.offsetY || 0),
      };
      noteEditorPlacementRef.current = placement;
      setNoteEditorPosition(getClampedNoteEditorPosition(anchor, placement));
    },
    [getClampedNoteEditorPosition],
  );

  useEffect(() => {
    if (!noteEditorVisible) return;

    const updatePosition = () => {
      const placement = noteEditorPlacementRef.current;
      if (!placement?.anchor) return;

      const next = getClampedNoteEditorPosition(placement.anchor, placement);
      setNoteEditorPosition((prev) => {
        if (Math.abs(prev.x - next.x) < 0.5 && Math.abs(prev.y - next.y) < 0.5)
          return prev;
        return next;
      });
    };

    const rafId = window.requestAnimationFrame(updatePosition);
    const viewport = window.visualViewport;
    window.addEventListener("resize", updatePosition);
    viewport?.addEventListener("resize", updatePosition);
    viewport?.addEventListener("scroll", updatePosition);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updatePosition);
      viewport?.removeEventListener("resize", updatePosition);
      viewport?.removeEventListener("scroll", updatePosition);
    };
  }, [
    getClampedNoteEditorPosition,
    noteEditorCurrentIndex,
    noteEditorMode,
    noteEditorPageNotes.length,
    noteEditorSelectedPreview,
    isTocSidebarOpen,
    isBookmarksSidebarOpen,
    isPagesSidebarOpen,
    isNotesSidebarOpen,
    isSearchSidebarOpen,
    isReadingAssistantSidebarOpen,
    isPublicationDetailsSidebarOpen,
    noteEditorVisible,
  ]);

  const getSelectionRectsForPage = (range, pageEl) => {
    const canvas = pageEl.querySelector("canvas");
    if (!canvas) return [];

    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return [];

    const walker = document.createTreeWalker(
      pageEl,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          if (!node.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
          return range.intersectsNode(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      },
      false,
    );

    const selectedRects = [];
    let textNode = walker.nextNode();

    while (textNode) {
      const textRange = document.createRange();
      textRange.selectNodeContents(textNode);

      if (textNode === range.startContainer) {
        textRange.setStart(textNode, range.startOffset);
      }

      if (textNode === range.endContainer) {
        textRange.setEnd(textNode, range.endOffset);
      }

      Array.from(textRange.getClientRects()).forEach((rect) => {
        const left = Math.max(rect.left, bounds.left);
        const right = Math.min(rect.right, bounds.right);
        const top = Math.max(rect.top, bounds.top);
        const bottom = Math.min(rect.bottom, bounds.bottom);
        const width = right - left;
        const height = bottom - top;

        if (width <= 0.5 || height <= 0.5) return;
        selectedRects.push({
          left: left - bounds.left,
          top: top - bounds.top,
          right: right - bounds.left,
          bottom: bottom - bounds.top,
          width,
          height,
        });
      });

      textRange.detach?.();
      textNode = walker.nextNode();
    }

    return selectedRects;
  };

  const getSelectionCandidatePages = useCallback((selection, viewer) => {
    if (!selection || !viewer) return [];

    const pages = Array.from(
      viewer.querySelectorAll(".page[data-page-number]"),
    );
    if (pages.length === 0) return [];

    const getPageNumberFromNode = (node) => {
      if (!node) return null;
      const element =
        node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      const pageEl = element?.closest?.(".page[data-page-number]");
      if (!pageEl) return null;
      const pageNumber = Number(pageEl.getAttribute("data-page-number") || 0);
      return pageNumber > 0 ? pageNumber : null;
    };

    const anchorPageNumber = getPageNumberFromNode(selection.anchorNode);
    const focusPageNumber = getPageNumberFromNode(selection.focusNode);

    if (!anchorPageNumber && !focusPageNumber) {
      return pages;
    }

    const startPage = Math.min(
      anchorPageNumber ?? focusPageNumber,
      focusPageNumber ?? anchorPageNumber,
    );
    const endPage = Math.max(
      anchorPageNumber ?? focusPageNumber,
      focusPageNumber ?? anchorPageNumber,
    );

    const filtered = pages.filter((pageEl) => {
      const pageNumber = Number(pageEl.getAttribute("data-page-number") || 0);
      return pageNumber >= startPage && pageNumber <= endPage;
    });

    return filtered.length > 0 ? filtered : pages;
  }, []);

  const getSelectionFragments = useCallback(
    (selection, viewer) => {
      if (!selection || selection.rangeCount === 0 || !viewer) return [];

      const range = selection.getRangeAt(0);
      const pages = getSelectionCandidatePages(selection, viewer);
      // Read live PDF.js scale when available to avoid stale store scale during
      // fast zoom/fit transitions.
      const liveScale = Number(pdfViewerRef.current?.currentScale || 0);
      const scaleValue =
        liveScale > 0
          ? liveScale
          : Number(scaleRef.current) > 0
            ? Number(scaleRef.current)
            : 1;
      const rotation = normalizeRotation(
        pdfViewerRef.current?.pagesRotation ?? pdfRotation,
      );
      const byPage = [];

      pages.forEach((pageEl) => {
        const pageNumber = Number(pageEl.getAttribute("data-page-number") || 0);
        if (!pageNumber) return;

        const rectsForPage = getSelectionRectsForPage(range, pageEl);
        if (rectsForPage.length === 0) return;

        const { width: baseW, height: baseH } = getBasePageSize({
          pdfViewer: pdfViewerRef.current,
          pageNumber,
          pageEl,
          scale: scaleValue,
          rotation: rotation,
        });

        // Selection rects are in the current (possibly rotated) display space.
        // Persist in unrotated scale-1 coordinates so overlays track rotation.
        const toStoredRect = (displayRect) =>
          unrotateRect(displayRect, baseW, baseH, rotation);

        byPage.push({
          pageNumber,
          highlightCoordinates: rectsForPage.map((r) =>
            toStoredRect({
              left: r.left / scaleValue,
              top: r.top / scaleValue,
              width: r.width / scaleValue,
              height: r.height / scaleValue,
            }),
          ),
          underlineCoordinates: rectsForPage.map((r) =>
            toStoredRect({
              left: r.left / scaleValue,
              top: Math.max(0, r.bottom - 1) / scaleValue,
              width: r.width / scaleValue,
              height: Math.max(1 / scaleValue, 1),
            }),
          ),
        });
      });

      return byPage;
    },
    [getSelectionCandidatePages, pdfRotation],
  );

  const isSelectionInsidePdfTextLayer = useCallback((selection) => {
    if (!selection) return false;
    return (
      isNodeInsidePdfTextLayer(selection.anchorNode) &&
      isNodeInsidePdfTextLayer(selection.focusNode)
    );
  }, []);

  const renderLiveSelectionOverlay = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    viewer.querySelectorAll(".igp-live-selection-layer").forEach((el) => {
      el.innerHTML = "";
    });

    // Live selection paint breaks under rotation — skip while rotated.
    if (readViewerRotation(pdfViewerRef.current, pdfRotation) !== 0) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return;
    }

    const selectedText = selection.toString().trim();
    if (!selectedText) return;

    let range;
    try {
      range = selection.getRangeAt(0);
    } catch {
      return;
    }

    // Outside the PDF text layer (AI sidebar, notes, search, etc.) — leave alone.
    if (!isSelectionInsidePdfTextLayer(selection)) {
      return;
    }

    const pages = getSelectionCandidatePages(selection, viewer);
    pages.forEach((pageEl) => {
      const pageNumber = Number(pageEl.getAttribute("data-page-number") || 0);
      if (!pageNumber) return;

      const rectsForPage = getSelectionRectsForPage(range, pageEl);
      if (rectsForPage.length === 0) return;

      const sortedRects = [...rectsForPage].sort((a, b) => {
        const topDiff = Number(a.top || 0) - Number(b.top || 0);
        return Math.abs(topDiff) < 3
          ? Number(a.left || 0) - Number(b.left || 0)
          : topDiff;
      });

      const mergedRects = [];
      sortedRects.forEach((rect) => {
        const rectTop = Number(rect.top || 0);
        const rectBottom = Number(
          rect.bottom || rectTop + Number(rect.height || 0),
        );
        const rectMidY = (rectTop + rectBottom) / 2;

        const existingLine = mergedRects.find((line) => {
          const lineTop = Number(line.top || 0);
          const lineBottom = Number(
            line.bottom || lineTop + Number(line.height || 0),
          );
          const lineHeight = Math.max(1, lineBottom - lineTop);
          const lineMidY = (lineTop + lineBottom) / 2;
          return Math.abs(rectMidY - lineMidY) <= lineHeight * 0.6;
        });

        if (existingLine) {
          existingLine.left = Math.min(
            Number(existingLine.left || 0),
            Number(rect.left || 0),
          );
          existingLine.right = Math.max(
            Number(existingLine.right || 0),
            Number(
              rect.right || Number(rect.left || 0) + Number(rect.width || 0),
            ),
          );
          existingLine.top = Math.min(Number(existingLine.top || 0), rectTop);
          existingLine.bottom = Math.max(
            Number(
              existingLine.bottom ||
                Number(existingLine.top || 0) +
                  Number(existingLine.height || 0),
            ),
            rectBottom,
          );
          existingLine.width = Math.max(
            0,
            existingLine.right - existingLine.left,
          );
          existingLine.height = Math.max(
            1,
            existingLine.bottom - existingLine.top,
          );
        } else {
          mergedRects.push({
            ...rect,
            right: Number(
              rect.right || Number(rect.left || 0) + Number(rect.width || 0),
            ),
            bottom: rectBottom,
          });
        }
      });

      if (mergedRects.length === 0) return;

      let overlayLayer = pageEl.querySelector(".igp-live-selection-layer");
      if (!overlayLayer) {
        overlayLayer = document.createElement("div");
        overlayLayer.className = "igp-live-selection-layer";
        const annotationLayer = pageEl.querySelector(
          ".igp-readonly-annotation-layer",
        );
        if (annotationLayer) {
          pageEl.insertBefore(overlayLayer, annotationLayer);
        } else {
          pageEl.appendChild(overlayLayer);
        }
      }

      overlayLayer.innerHTML = "";

      mergedRects.forEach((rect) => {
        const left = Math.floor(Number(rect.left || 0));
        const top = Math.floor(Number(rect.top || 0));
        const width = Math.ceil(Number(rect.width || 0));
        const height = Math.ceil(Number(rect.height || 0));
        if (width <= 0 || height <= 0) return;

        const highlight = document.createElement("div");
        highlight.className = "igp-live-selection-rect";
        highlight.style.left = `${left}px`;
        highlight.style.top = `${top}px`;
        highlight.style.width = `${width}px`;
        highlight.style.height = `${height}px`;
        overlayLayer.appendChild(highlight);
      });
    });
  }, [getSelectionCandidatePages, isSelectionInsidePdfTextLayer, pdfRotation]);

  const getCurrentSelectionPayload = useCallback(() => {
    const viewer = viewerRef.current;
    const selection = window.getSelection();
    if (!viewer || !selection || selection.isCollapsed) return null;

    const selectedText = selection.toString().trim();
    if (!selectedText) return null;

    const range = selection.getRangeAt(0);
    if (!isSelectionInsidePdfTextLayer(selection)) {
      return null;
    }
    const commonNode = range.commonAncestorContainer;
    const commonElement =
      commonNode?.nodeType === Node.ELEMENT_NODE
        ? commonNode
        : commonNode?.parentElement;

    if (!commonElement || !viewer.contains(commonElement)) {
      return null;
    }

    const fragments = getSelectionFragments(selection, viewer);
    if (fragments.length === 0) return null;

    return { selectedText, fragments };
  }, [getSelectionFragments, isSelectionInsidePdfTextLayer]);

  useEffect(() => {
    renderLiveSelectionOverlayRef.current = renderLiveSelectionOverlay;
  }, [renderLiveSelectionOverlay]);

  const emitUndoRedoCapabilities = useCallback(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("reader:can-undo", {
        detail: {
          canUndo: undoStackRef.current.length > 0,
          canRedo: redoStackRef.current.length > 0,
        },
      }),
    );
  }, []);

  const pushAction = useCallback(
    (pageNumber, action) => {
      if (undoRedoInProgressRef.current) return;
      undoStackRef.current.push({ ...action, pageNumber });
      redoStackRef.current = [];
      emitUndoRedoCapabilities();
    },
    [emitUndoRedoCapabilities],
  );

  const resolveHighlightApiId = useCallback(
    ({
      resolvedHighlights,
      pageNumber,
      content,
      color,
      coordinates,
      fallbackId,
    }) => {
      const list = Array.isArray(resolvedHighlights) ? resolvedHighlights : [];
      const expectedCoords = Array.isArray(coordinates) ? coordinates : [];
      const expectedColor = (color || "#FFF59D").toLowerCase();
      const normalizeCoord = (coord) => {
        const left = Number(coord?.left || 0);
        const top = Number(coord?.top || 0);
        const width = Number(coord?.width || 0);
        const height = Number(coord?.height || 0);
        return `${Math.round(left * 100)}:${Math.round(top * 100)}:${Math.round(width * 100)}:${Math.round(height * 100)}`;
      };

      const expectedCoordSet = expectedCoords
        .map((coord) => normalizeCoord(coord))
        .sort()
        .join("|");

      const matchingHighlight = list.find((highlight) => {
        if (Number(highlight?.page_number || 0) !== Number(pageNumber || 0)) {
          return false;
        }
        if ((highlight?.content || "") !== (content || "")) {
          return false;
        }
        if ((highlight?.color || "#FFF59D").toLowerCase() !== expectedColor) {
          return false;
        }

        const coords = Array.isArray(highlight?.coordinates)
          ? highlight.coordinates
          : [];
        if (coords.length !== expectedCoords.length) return false;

        const actualCoordSet = coords
          .map((coord) => normalizeCoord(coord))
          .sort()
          .join("|");

        return actualCoordSet === expectedCoordSet;
      });

      return matchingHighlight?.id || fallbackId || null;
    },
    [],
  );

  const resolveUnderlineApiId = useCallback(
    ({ resolvedUnderlines, pageNumber, content, coordinates, fallbackId }) => {
      const list = Array.isArray(resolvedUnderlines) ? resolvedUnderlines : [];
      const expectedCoords = Array.isArray(coordinates) ? coordinates : [];

      const normalizeCoord = (coord) => {
        const left = Number(coord?.left || 0);
        const top = Number(coord?.top || 0);
        const width = Number(coord?.width || 0);
        const height = Number(coord?.height || 1);
        return `${Math.round(left * 100)}:${Math.round(top * 100)}:${Math.round(width * 100)}:${Math.round(height * 100)}`;
      };

      const expectedCoordSet = expectedCoords
        .map((coord) => normalizeCoord(coord))
        .sort()
        .join("|");

      const matchingUnderline = list.find((underline) => {
        if (Number(underline?.page_number || 0) !== Number(pageNumber || 0)) {
          return false;
        }
        if ((underline?.content || "") !== (content || "")) {
          return false;
        }

        const coords = Array.isArray(underline?.coordinates)
          ? underline.coordinates
          : underline?.coordinates
            ? [underline.coordinates]
            : [];
        if (coords.length !== expectedCoords.length) return false;

        const actualCoordSet = coords
          .map((coord) => normalizeCoord(coord))
          .sort()
          .join("|");

        return actualCoordSet === expectedCoordSet;
      });

      return toApiUnderlineId(matchingUnderline?.id || fallbackId || null);
    },
    [],
  );

  useEffect(() => {
    if (undoRedoInProgressRef.current) return;

    const resolvedHighlights = Array.isArray(annotations?.highlights?.data)
      ? annotations.highlights.data
      : [];
    if (resolvedHighlights.length === 0) return;

    const syncAction = (action) => {
      if (!action || action.type !== "add-highlight") return;

      const payload = action.payload;
      const group = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.highlights)
          ? payload.highlights
          : [];

      const coordinates =
        group.length > 0
          ? group.map((rect) => ({
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
            }))
          : Array.isArray(payload?.coordinates)
            ? payload.coordinates
            : [];

      if (coordinates.length === 0) return;

      const first = group[0];
      const pageNumber = action.pageNumber || payload?.pageNumber || 1;
      const content = first?.text || payload?.content || "";
      const color = first?.color || payload?.color || "#FFF59D";
      const fallbackId = Array.isArray(payload)
        ? first?.parentId || first?.id
        : payload?.apiId || first?.parentId || first?.id;

      const resolvedApiId = resolveHighlightApiId({
        resolvedHighlights,
        pageNumber,
        content,
        color,
        coordinates,
        fallbackId,
      });

      if (!resolvedApiId) return;

      if (Array.isArray(payload)) {
        payload.forEach((rect) => {
          if (rect && typeof rect === "object") {
            rect.id = resolvedApiId;
            rect.parentId = resolvedApiId;
          }
        });
      } else if (payload && typeof payload === "object") {
        payload.apiId = resolvedApiId;
        if (Array.isArray(payload.highlights)) {
          payload.highlights.forEach((rect) => {
            if (rect && typeof rect === "object") {
              rect.id = resolvedApiId;
              rect.parentId = resolvedApiId;
            }
          });
        }
      }
    };

    const stacks = [undoStackRef.current, redoStackRef.current];
    stacks.forEach((stack) => stack.forEach(syncAction));
  }, [annotations?.highlights?.data, resolveHighlightApiId]);

  const scrollToPage = useCallback((pageNumber) => {
    const container = containerRef.current;
    const viewer = viewerRef.current;
    if (!container || !viewer || !pageNumber) return;

    const pageEl = viewer.querySelector(
      `.page[data-page-number="${pageNumber}"]`,
    );
    if (!pageEl) return;

    container.scrollTo({
      top: Math.max(0, pageEl.offsetTop),
      left: container.scrollLeft,
      behavior: "auto",
    });
  }, []);

  // ── Initial view start page from API/DRM ────────────────────────────────
  const initialViewAppliedRef = useRef(false);

  useEffect(() => {
    initialViewAppliedRef.current = false;
  }, [pdfDocument]);

  useEffect(() => {
    if (!pdfDocument || !initialViewStartPage || initialViewAppliedRef.current)
      return;
    initialViewAppliedRef.current = true;

    const target = Number(initialViewStartPage);
    if (!Number.isInteger(target) || target <= 0) return;

    currentPageRef.current = target;
    setCurrentPage(target);

    const delays = [150, 450, 900];
    const timers = delays.map((delay) =>
      setTimeout(() => {
        const pdfViewer = pdfViewerRef.current;
        const pageCount = pdfViewer?.pagesCount || 0;
        if (!pageCount || target <= pageCount) {
          if (pdfViewer) pdfViewer.currentPageNumber = target;
          scrollToPage(target);
        }
      }, delay),
    );

    clearInitialViewStartPage();
    return () => timers.forEach(clearTimeout);
  }, [
    pdfDocument,
    initialViewStartPage,
    clearInitialViewStartPage,
    setCurrentPage,
    scrollToPage,
  ]);

  const buildSelectionSignature = useCallback(
    (tool, selectedText, fragments) => {
      return `${tool}|${selectedText}|${fragments
        .map(
          (f) =>
            `${f.pageNumber}:${f.highlightCoordinates
              .map(
                (c) =>
                  `${Math.round(c.left * 100)}-${Math.round(c.top * 100)}-${Math.round(c.width * 100)}-${Math.round(c.height * 100)}`,
              )
              .join(";")}`,
        )
        .join("|")}`;
    },
    [],
  );

  const applySelectionTool = useCallback(
    async (tool, payload, options = {}) => {
      if (!payload || !payload.selectedText || !payload.fragments?.length)
        return;

      if (readViewerRotation(pdfViewerRef.current, pdfRotation) !== 0) return;

      if (tool === "highlight" && !isFeatureAllowed(drmPolicy, "highlights")) {
        showFeatureNotAllowedNotification("highlights");
        return;
      }

      if (tool === "underline" && !isFeatureAllowed(drmPolicy, "underline")) {
        showFeatureNotAllowedNotification("underline");
        return;
      }

      const colorToUse =
        options.color ||
        useReaderStore.getState().highlightColor ||
        highlightColor;

      const signature = buildSelectionSignature(
        tool,
        payload.selectedText,
        payload.fragments,
      );
      const now = Date.now();

      if (
        lastAppliedSelectionRef.current.signature === signature &&
        now - lastAppliedSelectionRef.current.ts < 800
      ) {
        return;
      }

      if (tool === "highlight") {
        for (const fragment of payload.fragments) {
          const apiId = `highlight-${Date.now()}-${Math.random()}-${fragment.pageNumber}`;

          const liveScale = Number(pdfViewerRef.current?.currentScale || 0);
          const currentScale =
            liveScale > 0
              ? liveScale
              : Number(scaleRef.current) > 0
                ? Number(scaleRef.current)
                : 1;
          let mergedFromSelection = mergeAdjacentHighlightRects(
            (fragment.highlightCoordinates || []).map((coord) => ({
              left: Number(coord?.left || 0),
              top: Number(coord?.top || 0),
              width: Number(coord?.width || 0),
              height: Number(coord?.height || 0),
              id: apiId,
              parentId: apiId,
              text: payload.selectedText,
              color: colorToUse,
              pageNumber: fragment.pageNumber,
            })),
          );

          let changed = true;
          while (changed) {
            changed = false;
            const newMerged = [];
            const used = new Set();

            for (let i = 0; i < mergedFromSelection.length; i += 1) {
              if (used.has(i)) continue;

              let current = mergedFromSelection[i];
              for (let j = i + 1; j < mergedFromSelection.length; j += 1) {
                if (used.has(j)) continue;

                if (
                  highlightRectsOverlapWithScale(
                    current,
                    mergedFromSelection[j],
                    5,
                    currentScale,
                  )
                ) {
                  current = mergeHighlightRects(
                    current,
                    mergedFromSelection[j],
                  );
                  used.add(j);
                  changed = true;
                }
              }

              newMerged.push(current);
              used.add(i);
            }

            mergedFromSelection = newMerged;
          }

          const existingHighlights =
            highlightsByPage.get(fragment.pageNumber) || [];

          // EPUB-style: skip when an existing highlight fully covers the new selection.
          const isFullyCovered = mergedFromSelection.every((newHl) =>
            existingHighlights.some((existing) =>
              highlightRectFullyCoversWithScale(
                existing,
                newHl,
                5,
                currentScale,
              ),
            ),
          );
          if (isFullyCovered && mergedFromSelection.length > 0) {
            const activeSelection = window.getSelection();
            if (activeSelection) {
              activeSelection.removeAllRanges();
            }
            latestSelectionPayloadRef.current = null;
            setSelectionRect(null);
            continue;
          }

          // True partial overlap only → replace overlapping annotations.
          // Do NOT use a large tolerance here: adjacent words (space gap) must stay separate.
          const overlappingExisting = existingHighlights.filter((existing) =>
            mergedFromSelection.some((newHl) =>
              highlightRectsOverlapWithScale(
                existing,
                newHl,
                HIGHLIGHT_TRUE_OVERLAP_TOLERANCE_PX,
                currentScale,
              ),
            ),
          );

          const overlappingParentKeys = new Set(
            overlappingExisting
              .map((rect) => String(rect?.parentId || rect?.id || ""))
              .filter(Boolean),
          );

          const replacedRects = existingHighlights.filter((rect) => {
            const key = String(rect?.parentId || rect?.id || "");
            return key && overlappingParentKeys.has(key);
          });

          const storeHighlights =
            useReaderStore.getState().annotations?.highlights?.data || [];
          const replacedAnnotations = storeHighlights.filter((hl) => {
            const key = String(hl?.id || "");
            return (
              key &&
              overlappingParentKeys.has(key) &&
              Number(hl?.page_number || 0) === Number(fragment.pageNumber)
            );
          });

          let finalHighlights = mergedFromSelection.map((rect) => ({
            ...rect,
            color: colorToUse,
          }));

          if (replacedRects.length > 0) {
            finalHighlights = [...finalHighlights];
            replacedRects.forEach((existing) => {
              let mergedInto = false;
              finalHighlights = finalHighlights.map((current) => {
                if (
                  !mergedInto &&
                  highlightRectsOverlapWithScale(
                    current,
                    existing,
                    HIGHLIGHT_TRUE_OVERLAP_TOLERANCE_PX,
                    currentScale,
                  )
                ) {
                  mergedInto = true;
                  return mergeHighlightRects(current, existing, {
                    preferColor: colorToUse,
                  });
                }
                return current;
              });
              if (!mergedInto) {
                finalHighlights.push({
                  ...existing,
                  color: colorToUse,
                  id: apiId,
                  parentId: apiId,
                });
              }
            });

            // Collapse any rects that now overlap after absorbing replacements.
            let changedMerge = true;
            while (changedMerge) {
              changedMerge = false;
              const collapsed = [];
              const used = new Set();
              for (let i = 0; i < finalHighlights.length; i += 1) {
                if (used.has(i)) continue;
                let current = finalHighlights[i];
                for (let j = i + 1; j < finalHighlights.length; j += 1) {
                  if (used.has(j)) continue;
                  if (
                    highlightRectsOverlapWithScale(
                      current,
                      finalHighlights[j],
                      HIGHLIGHT_TRUE_OVERLAP_TOLERANCE_PX,
                      currentScale,
                    )
                  ) {
                    current = mergeHighlightRects(
                      current,
                      finalHighlights[j],
                      { preferColor: colorToUse },
                    );
                    used.add(j);
                    changedMerge = true;
                  }
                }
                collapsed.push(current);
                used.add(i);
              }
              finalHighlights = collapsed;
            }
          }

          if (finalHighlights.length === 0) {
            continue;
          }

          const mergedContent = mergeHighlightContentStrings(
            payload.selectedText,
            replacedAnnotations.map((hl) => hl?.content || hl?.text || ""),
          );

          const highlightGroup = finalHighlights.map((rect) => ({
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            id: apiId,
            parentId: apiId,
            text: mergedContent,
            color: colorToUse,
            pageNumber: fragment.pageNumber,
          }));

          const activeSelection = window.getSelection();
          if (activeSelection) {
            activeSelection.removeAllRanges();
          }
          latestSelectionPayloadRef.current = null;
          setSelectionRect(null);

          // Create first, then delete replaced (same order as PDF underlines).
          createHighlight({
            id: apiId,
            page_number: fragment.pageNumber,
            content: mergedContent,
            color: colorToUse,
            coordinates: highlightGroup.map((rect) => ({
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
            })),
          });

          overlappingParentKeys.forEach((id) => {
            removeHighlightById(id);
          });

          highlightGroup.forEach((rect) => {
            rect.id = apiId;
            rect.parentId = apiId;
          });
          if (highlightGroup.length > 0) {
            highlightGroup[0].pageNumber = fragment.pageNumber;
          }
          pushAction(fragment.pageNumber, {
            type: "add-highlight",
            payload: {
              apiId,
              content: mergedContent,
              color: colorToUse,
              coordinates: highlightGroup.map((rect) => ({
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
              })),
              highlights: highlightGroup,
              removed: replacedAnnotations,
              pageNumber: fragment.pageNumber,
            },
          });
        }
      } else if (tool === "underline") {
        const pendingUnderlineActions = [];
        for (const fragment of payload.fragments) {
          const existingUnderlines =
            underlinesByPage.get(fragment.pageNumber) || [];
          const sourceCoords = Array.isArray(fragment.underlineCoordinates)
            ? fragment.underlineCoordinates
            : [];

          const exactUniqueCoords = sourceCoords.filter(
            (coord) =>
              !existingUnderlines.some((existing) =>
                underlineRectsMatch(existing, coord),
              ),
          );

          if (exactUniqueCoords.length === 0) {
            continue;
          }

          const overlappingParentKeys = new Set();
          existingUnderlines.forEach((existing) => {
            const hasOverlap = exactUniqueCoords.some((coord) =>
              underlineRectsOverlap(existing, coord),
            );
            if (hasOverlap) {
              const key = existing?.parentId || existing?.id;
              if (key) overlappingParentKeys.add(String(key));
            }
          });

          const replacedUnderlines = existingUnderlines.filter((rect) => {
            const key = rect?.parentId || rect?.id;
            return key && overlappingParentKeys.has(String(key));
          });

          const mergedCoords = [...exactUniqueCoords];
          replacedUnderlines.forEach((rect) => {
            mergedCoords.push({
              left: Number(rect?.left || 0),
              top: Number(rect?.top || 0),
              width: Number(rect?.width || 0),
              height: Number(rect?.height || 1),
            });
          });

          const dedupeCoords = [];
          const dedupeSet = new Set();
          mergedCoords.forEach((coord) => {
            const key = `${Math.round(Number(coord?.left || 0) * 100)}:${Math.round(Number(coord?.top || 0) * 100)}:${Math.round(Number(coord?.width || 0) * 100)}:${Math.round(Number(coord?.height || 1) * 100)}`;
            if (dedupeSet.has(key)) return;
            dedupeSet.add(key);
            dedupeCoords.push({
              left: Number(coord?.left || 0),
              top: Number(coord?.top || 0),
              width: Number(coord?.width || 0),
              height: Number(coord?.height || 1),
            });
          });

          if (dedupeCoords.length === 0) {
            continue;
          }

          const apiId = `underline-${Date.now()}-${Math.random()}-${fragment.pageNumber}`;
          const underlineGroup = dedupeCoords.map((coord) => ({
            left: coord.left,
            top: coord.top,
            width: coord.width,
            height: coord.height,
            id: apiId,
            parentId: apiId,
            text: payload.selectedText,
            pageNumber: fragment.pageNumber,
          }));

          const replacedApiIds = Array.from(
            new Set(
              replacedUnderlines
                .map((underline) =>
                  toApiUnderlineId(
                    String(underline?.parentId || underline?.id || ""),
                  ),
                )
                .filter(Boolean),
            ),
          );

          const activeSelection = window.getSelection();
          if (activeSelection) {
            activeSelection.removeAllRanges();
          }
          latestSelectionPayloadRef.current = null;
          setSelectionRect(null);

          createUnderlineNoRefresh({
            id: apiId,
            pageNumber: fragment.pageNumber,
            content: payload.selectedText,
            coordinates: dedupeCoords,
          });

          if (replacedApiIds.length > 0) {
            replacedApiIds.forEach((id) => deletePdfUnderline(id));
          }

          pendingUnderlineActions.push({
            pageNumber: fragment.pageNumber,
            content: payload.selectedText,
            coordinates: dedupeCoords,
            group: underlineGroup,
            removed: replacedUnderlines,
            fallbackId: apiId,
          });
        }

        if (pendingUnderlineActions.length > 0) {
          pendingUnderlineActions.forEach((entry) => {
            entry.group.forEach((rect) => {
              rect.id = entry.fallbackId;
              rect.parentId = entry.fallbackId;
            });

            pushAction(entry.pageNumber, {
              type: "add-underline",
              payload: {
                apiId: entry.fallbackId,
                apiCreatedIds: [entry.fallbackId],
                content: entry.content,
                coordinates: entry.coordinates,
                underlines: entry.group,
                added: entry.group,
                removed: entry.removed,
                redoRects: entry.group,
                wasUndone: false,
                pageNumber: entry.pageNumber,
              },
            });
          });
        }
      }

      lastAppliedSelectionRef.current = { signature, ts: now };
    },
    [
      buildSelectionSignature,
      drmPolicy,
      highlightColor,
      createUnderlineNoRefresh,
      pushAction,
      highlightsByPage,
      underlinesByPage,
      pdfRotation,
    ],
  );

  const createNoteFromSelection = useCallback(
    async (payload, options = {}) => {
      if (!payload || !payload.selectedText || !payload.fragments?.length)
        return;

      if (!isFeatureAllowed(drmPolicy, "notes")) {
        showFeatureNotAllowedNotification("notes");
        return;
      }

      setContextMenuVisible(false);
      const anchorRect = options?.anchorRect;
      const anchor = anchorRect
        ? {
            x: Number(anchorRect.left || 0) + Number(anchorRect.width || 0) / 2,
            y: Number(anchorRect.bottom || 0) + 8,
          }
        : selectionRect
          ? {
              x: selectionRect.left + selectionRect.width / 2,
              y: selectionRect.bottom + 8,
            }
          : { x: contextMenuPosition.x, y: contextMenuPosition.y + 8 };

      let coordinates = null;
      if (payload.fragments?.[0]?.pageNumber) {
        const pageEl = document.querySelector(
          `.page[data-page-number="${payload.fragments[0].pageNumber}"]`,
        );
        if (pageEl) {
          const rect = pageEl.getBoundingClientRect();
          const rotation = normalizeRotation(
            pdfViewerRef.current?.pagesRotation ?? pdfRotation,
          );
          const sourceRect = anchorRect || selectionRect;
          let displayCoords = null;
          if (sourceRect) {
            displayCoords = {
              x: (sourceRect.left - rect.left) / rect.width,
              y: (sourceRect.top - rect.top) / rect.height,
            };
          } else if (contextMenuPosition) {
            displayCoords = {
              x: (contextMenuPosition.x - rect.left) / rect.width,
              y: (contextMenuPosition.y - rect.top) / rect.height,
            };
          }
          if (displayCoords) {
            coordinates = unrotateNormalizedPoint(
              displayCoords.x,
              displayCoords.y,
              rotation,
            );
          }
        }
      }

      placeNoteEditor(anchor, { centerOnX: true, offsetY: 0 });
      setNoteEditorText("");
      setNoteEditorPayload({ ...payload, coordinates });
      setNoteEditorMode("create");
      setNoteEditorNoteId(null);
      setNoteEditorPageNumber(payload.fragments[0]?.pageNumber || null);
      setNoteEditorSelectedText(payload.selectedText || "");
      setExpandedSelectedText(false);
      setNoteEditorVisible(true);
    },
    [
      contextMenuPosition.x,
      contextMenuPosition.y,
      drmPolicy,
      pdfRotation,
      placeNoteEditor,
      selectionRect,
    ],
  );

  const handleNoteEditorClose = useCallback(() => {
    noteEditorPlacementRef.current = null;
    setNoteEditorVisible(false);
    setNoteEditorText("");
    setNoteEditorPayload(null);
    setNoteEditorMode("create");
    setNoteEditorNoteId(null);
    setNoteEditorPageNumber(null);
    setNoteEditorSelectedText("");
    setExpandedSelectedText(false);
    setIsNoteSaving(false);
  }, []);

  const executeTextboxCommit = useCallback(
    async (textboxId) => {
      if (!textboxId) return;

      const trimmed = (textboxDraftRef.current || "").trim();
      try {
        if (!trimmed) {
          await deleteTextbox(textboxId);
        } else {
          await updateTextbox(textboxId, { content: trimmed });
        }
      } catch (err) {
        console.error("Error saving textbox content:", err);
      }

      setSelectedTextboxId(null);
      setEditingTextboxId(null);
      textboxDraftRef.current = "";
      setTextboxEnabled(false);
      renderReadOnlyOverlaysRef.current();
    },
    [setTextboxEnabled],
  );

  const executeTextboxDelete = useCallback(
    async (textboxId) => {
      if (!textboxId) return;
      try {
        await deleteTextbox(textboxId);
      } catch (err) {
        console.error("Error deleting textbox:", err);
      }
      if (editingTextboxId === textboxId) {
        setEditingTextboxId(null);
        textboxDraftRef.current = "";
      }
      if (selectedTextboxId === textboxId) {
        setSelectedTextboxId(null);
      }
      renderReadOnlyOverlaysRef.current();
    },
    [editingTextboxId, selectedTextboxId],
  );

  const handleNoteSave = useCallback(async () => {
    if (isNoteSaving) return;
    const text = noteEditorText.trim();
    if (!text) return;

    const createPageNumber = noteEditorPayload?.fragments?.[0]?.pageNumber;
    const pageNumber =
      noteEditorMode === "edit" ? noteEditorPageNumber : createPageNumber;
    if (!pageNumber) return;

    setIsNoteSaving(true);
    try {
      // OPTIMISTIC UPDATE: Call service functions but DON'T await them
      // The service functions now update the store immediately and fire API in background
      if (noteEditorMode === "edit" && noteEditorNoteId) {
        editNote(noteEditorNoteId, {
          page_number: pageNumber,
          content: text,
          selected_text: noteEditorSelectedText || undefined,
          coordinates: noteEditorPayload?.coordinates || undefined,
        });
        // Fire API async - don't wait
      } else {
        createNote({
          page_number: pageNumber,
          content: text,
          selected_text: noteEditorPayload?.selectedText || "",
          coordinates: noteEditorPayload?.coordinates,
        });
        // Fire API async - don't wait
      }

      // IMMEDIATE: Close UI and clear state (no blocking)
      setCurrentPage(pageNumber);
      const selection = window.getSelection();
      if (selection) selection.removeAllRanges();
      latestSelectionPayloadRef.current = null;
      setSelectionRect(null);
      handleNoteEditorClose();
      setNotesEnabled(false);
      setIsNoteSaving(false);
    } catch (err) {
      // This shouldn't happen since we're not awaiting the service call
      console.error("Unexpected error in handleNoteSave:", err);
      setIsNoteSaving(false);
    }
  }, [
    handleNoteEditorClose,
    setNotesEnabled,
    isNoteSaving,
    noteEditorMode,
    noteEditorNoteId,
    noteEditorPageNumber,
    noteEditorPayload,
    noteEditorSelectedText,
    noteEditorText,
    setCurrentPage,
  ]);

  const openEditorForExistingNote = useCallback(
    (note, pageNumber, anchor) => {
      if (!note || !pageNumber) return;
      const selectedText = note?.selected_text || note?.selectedText || "";

      placeNoteEditor(anchor, { centerOnX: true, offsetY: 8 });
      setNoteEditorMode("edit");
      setNoteEditorNoteId(note?.id || null);
      setNoteEditorPageNumber(pageNumber);
      setNoteEditorSelectedText(selectedText);
      setNoteEditorText(note?.content || "");
      setNoteEditorPayload({
        selectedText,
        fragments: [{ pageNumber }],
        coordinates: note?.coordinates,
      });
      setExpandedSelectedText(false);
      setNoteEditorVisible(true);
    },
    [placeNoteEditor],
  );

  const handleNavigateNote = useCallback(
    (direction) => {
      if (!noteEditorPageNotes.length || noteEditorCurrentIndex < 0) return;
      const nextIndex =
        (noteEditorCurrentIndex + direction + noteEditorPageNotes.length) %
        noteEditorPageNotes.length;
      const nextNote = noteEditorPageNotes[nextIndex];
      if (!nextNote) return;

      const selectedText =
        nextNote?.selected_text || nextNote?.selectedText || "";
      setNoteEditorNoteId(nextNote?.id || null);
      setNoteEditorSelectedText(selectedText);
      setNoteEditorText(nextNote?.content || "");
      setNoteEditorPayload({
        selectedText,
        fragments: [{ pageNumber: noteEditorPageNumber }],
        coordinates: nextNote?.coordinates,
      });
      setExpandedSelectedText(false);
    },
    [noteEditorCurrentIndex, noteEditorPageNotes, noteEditorPageNumber],
  );

  const handleNoteEditorKeyDown = useCallback(
    (evt) => {
      if (evt.key === "Enter" && !evt.shiftKey) {
        const isTouchDevice =
          typeof window !== "undefined" &&
          ("ontouchstart" in window || navigator.maxTouchPoints > 0);
        if (isTouchDevice) return;
        evt.preventDefault();
        if (isNoteSaving) return;
        handleNoteSave();
      } else if (evt.key === "Escape") {
        evt.preventDefault();
        if (isNoteSaving) return;
        handleNoteEditorClose();
      }
    },
    [handleNoteEditorClose, handleNoteSave, isNoteSaving],
  );

  const clearAnnotationSelection = useCallback(() => {
    setContextMenuVisible(false);
    setSelectionRect(null);
    latestSelectionPayloadRef.current = null;
    setSelectedDrawing(null);
    setSelectedHighlight(null);
    setSelectedUnderline(null);
  }, []);

  const contextCapabilities = useMemo(
    () => ({
      canHighlight: isFeatureAllowed(drmPolicy, "highlights"),
      canUnderline: isFeatureAllowed(drmPolicy, "underline"),
      canNote: isFeatureAllowed(drmPolicy, "notes"),
      canCopy: isCopyAllowed(drmPolicy),
    }),
    [drmPolicy],
  );

  const copyTextWithFallback = useCallback(async (text) => {
    const value = (text || "").trim();
    if (!value) return false;

    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = value;
      textArea.style.position = "fixed";
      textArea.style.top = "-9999px";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      let copied = false;
      try {
        copied = document.execCommand("copy");
      } catch {
        copied = false;
      }
      document.body.removeChild(textArea);
      return copied;
    }
  }, []);

  useEffect(() => {
    const isSelectionInsideViewer = () => {
      const viewer = viewerRef.current;
      if (!viewer) return false;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return false;

      const anchorElement =
        selection.anchorNode?.nodeType === Node.ELEMENT_NODE
          ? selection.anchorNode
          : selection.anchorNode?.parentElement;
      const focusElement =
        selection.focusNode?.nodeType === Node.ELEMENT_NODE
          ? selection.focusNode
          : selection.focusNode?.parentElement;

      if (
        (anchorElement && viewer.contains(anchorElement)) ||
        (focusElement && viewer.contains(focusElement))
      ) {
        return true;
      }

      const range = selection.getRangeAt(0);
      const commonNode = range.commonAncestorContainer;
      const commonElement =
        commonNode?.nodeType === Node.ELEMENT_NODE
          ? commonNode
          : commonNode?.parentElement;

      return Boolean(commonElement && viewer.contains(commonElement));
    };

    const evaluateCopyPolicy = () => {
      if (!isCopyAllowed(drmPolicy)) {
        return { blocked: true, reason: "not-allowed" };
      }

      const limit = getCopyCharLimit(drmPolicy);
      if (limit === -1) {
        return { blocked: false };
      }

      const selection = window.getSelection();
      const text = selection ? selection.toString().trim() : "";
      if (text.length <= limit) {
        return { blocked: false };
      }

      return {
        blocked: true,
        reason: "limit",
        limit,
        textToCopy: truncateForCopy(text, limit),
      };
    };

    const handleCopy = (evt) => {
      if (!isSelectionInsideViewer()) return;

      const decision = evaluateCopyPolicy();
      if (!decision.blocked) return;

      evt.preventDefault();
      if (decision.reason === "not-allowed") {
        showCopyNotAllowedNotification();
        return;
      }

      showCopyLimitExceededNotification(decision.limit);
      if (evt.clipboardData) {
        evt.clipboardData.setData("text/plain", decision.textToCopy);
      }
    };

    const handleCopyShortcut = async (evt) => {
      const isCopyCombo =
        (evt.metaKey || evt.ctrlKey) && evt.key?.toLowerCase() === "c";
      if (!isCopyCombo) return;
      if (!isSelectionInsideViewer()) return;

      const decision = evaluateCopyPolicy();
      if (!decision.blocked) return;

      evt.preventDefault();
      if (decision.reason === "not-allowed") {
        evt.preventDefault();
        showCopyNotAllowedNotification();
        return;
      }

      showCopyLimitExceededNotification(decision.limit);
      const copied = await copyTextWithFallback(decision.textToCopy);
      if (!copied) {
        showOperationFailedNotification(t("notifications.operations.copyText"));
      }
    };

    document.addEventListener("copy", handleCopy);
    document.addEventListener("keydown", handleCopyShortcut, true);
    return () => {
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("keydown", handleCopyShortcut, true);
    };
  }, [copyTextWithFallback, drmPolicy, t]);

  useEffect(() => {
    const isEditableTarget = (target) => {
      if (!target || !(target instanceof Element)) return false;
      if (target.isContentEditable) return true;
      const editableEl = target.closest(
        "input, textarea, select, [contenteditable='true']",
      );
      return Boolean(editableEl);
    };

    const handleArrowPageNavigation = (evt) => {
      if (evt.key !== "ArrowRight" && evt.key !== "ArrowLeft") return;
      if (isEditableTarget(evt.target)) return;

      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable)
      ) {
        return;
      }

      const current = Number(currentPageRef.current || 0);
      if (!Number.isInteger(current) || current <= 0) return;

      const pageLimit = Number(totalPages || pdfDocument?.numPages || 0);
      const step = isDoublePageMode ? 2 : 1;
      const delta = evt.key === "ArrowRight" ? step : -step;
      const requestedPage = current + delta;
      const targetPage =
        pageLimit > 0
          ? Math.max(1, Math.min(pageLimit, requestedPage))
          : Math.max(1, requestedPage);

      if (targetPage === current) return;

      evt.preventDefault();
      evt.stopPropagation();
      window.dispatchEvent(
        new CustomEvent("reader:go-to-page", { detail: { page: targetPage } }),
      );
    };

    document.addEventListener("keydown", handleArrowPageNavigation, true);
    return () => {
      document.removeEventListener("keydown", handleArrowPageNavigation, true);
    };
  }, [isDoublePageMode, pdfDocument, totalPages]);

  useEffect(() => {
    if (!noteEditorVisible) return;
    const t = setTimeout(() => {
      if (noteTextareaRef.current) {
        noteTextareaRef.current.focus();
      }
    }, 0);
    return () => clearTimeout(t);
  }, [noteEditorVisible]);

  useEffect(() => {
    if (!editingTextboxId) return;
    const t = setTimeout(() => {
      const textarea = viewerRef.current?.querySelector?.(
        `.igp-ro-textbox-textarea[data-textbox-id="${editingTextboxId}"]`,
      );
      if (!(textarea instanceof HTMLTextAreaElement)) return;
      if (document.activeElement !== textarea) {
        textarea.focus();
        textarea.setSelectionRange(
          textarea.value.length,
          textarea.value.length,
        );
      }
    }, 0);
    return () => clearTimeout(t);
  }, [editingTextboxId]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const onBadgeClick = (evt) => {
      const badge = evt.target?.closest?.(
        ".igp-ro-note-indicator, .note-indicator",
      );
      if (!badge) return;

      const pageNumber = Number(badge.getAttribute("data-page-number") || 0);
      if (!pageNumber) return;

      const pageNotes = notesListByPage.get(pageNumber) || [];
      if (!pageNotes.length) return;

      const noteId = badge.getAttribute("data-note-id");
      let note = pageNotes[0];
      if (noteId) {
        const found = pageNotes.find((n) => String(n.id) === noteId);
        if (found) note = found;
      }

      setContextMenuVisible(false);
      setSelectionRect(null);
      latestSelectionPayloadRef.current = null;

      openEditorForExistingNote(note, pageNumber, {
        x: evt.clientX,
        y: evt.clientY,
      });
    };

    viewer.addEventListener("click", onBadgeClick);
    return () => viewer.removeEventListener("click", onBadgeClick);
  }, [notesListByPage, openEditorForExistingNote]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const handleTextboxInteraction = (evt) => {
      const deleteBtn = evt.target?.closest?.(".igp-ro-textbox-delete");
      if (deleteBtn) {
        evt.preventDefault();
        evt.stopPropagation();
        const textboxId = deleteBtn.getAttribute("data-textbox-id") || "";
        if (textboxId) {
          executeTextboxDelete(textboxId);
        }
        return;
      }

      const saveBtn = evt.target?.closest?.(".igp-ro-textbox-save");
      if (saveBtn) {
        evt.preventDefault();
        evt.stopPropagation();
        const textboxId = saveBtn.getAttribute("data-textbox-id") || "";
        if (textboxId) {
          executeTextboxCommit(textboxId);
        }
        return;
      }

      if (evt.target?.closest?.(".igp-ro-textbox-drag-handle")) {
        return;
      }
      if (evt.target?.closest?.(".igp-ro-textbox-textarea")) {
        return;
      }

      const textboxNode = evt.target?.closest?.(".igp-ro-textbox");
      if (!textboxNode) return;

      evt.preventDefault();
      evt.stopPropagation();

      const textboxId = textboxNode.getAttribute("data-textbox-id") || "";
      if (!textboxId) return;

      const pageNumber = Number(
        textboxNode.getAttribute("data-page-number") || currentPageRef.current,
      );
      setCurrentPage(pageNumber);
      setContextMenuVisible(false);
      setSelectionRect(null);
      latestSelectionPayloadRef.current = null;

      const existing = textboxesById.get(textboxId);
      if (!existing) return;

      if (editingTextboxId === textboxId) {
        return;
      }

      if (selectedTextboxId === textboxId) {
        setEditingTextboxId(textboxId);
        textboxDraftRef.current = existing.content || "";
        setSelectedTextboxId(null);
      } else {
        setSelectedTextboxId(textboxId);
      }
    };

    const handleTextboxInput = (evt) => {
      const input = evt.target?.closest?.(".igp-ro-textbox-textarea");
      if (!input) return;
      const textboxId = input.getAttribute("data-textbox-id") || "";
      if (!textboxId || editingTextboxId !== textboxId) return;
      textboxDraftRef.current = input.value || "";
    };

    const handleTextboxKeyDown = (evt) => {
      const input = evt.target?.closest?.(".igp-ro-textbox-textarea");
      if (!input) return;
      const textboxId = input.getAttribute("data-textbox-id") || "";
      if (!textboxId) return;

      if (evt.key === "Escape") {
        evt.preventDefault();
        executeTextboxCommit(textboxId);
      } else if (evt.key === "Enter" && !evt.shiftKey) {
        evt.preventDefault();
        executeTextboxCommit(textboxId);
      }
    };

    const handleTextboxFocusOut = (evt) => {
      const input = evt.target?.closest?.(".igp-ro-textbox-textarea");
      if (!input) return;
      const textboxId = input.getAttribute("data-textbox-id") || "";
      if (!textboxId) return;

      const nextFocus = evt.relatedTarget;
      if (
        nextFocus instanceof Element &&
        nextFocus.closest?.(`.igp-ro-textbox[data-textbox-id="${textboxId}"]`)
      ) {
        return;
      }

      executeTextboxCommit(textboxId);
    };

    viewer.addEventListener("click", handleTextboxInteraction);
    viewer.addEventListener("input", handleTextboxInput);
    viewer.addEventListener("keydown", handleTextboxKeyDown);
    viewer.addEventListener("focusout", handleTextboxFocusOut);
    return () => {
      viewer.removeEventListener("click", handleTextboxInteraction);
      viewer.removeEventListener("input", handleTextboxInput);
      viewer.removeEventListener("keydown", handleTextboxKeyDown);
      viewer.removeEventListener("focusout", handleTextboxFocusOut);
    };
  }, [
    editingTextboxId,
    executeTextboxCommit,
    executeTextboxDelete,
    selectedTextboxId,
    setCurrentPage,
    textboxesById,
  ]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

    const finishTextboxDrag = async (evt) => {
      const drag = activeTextboxDragRef.current;
      if (!drag) return;

      window.removeEventListener("pointermove", handleTextboxDragMove);
      window.removeEventListener("pointerup", finishTextboxDrag);
      window.removeEventListener("pointercancel", finishTextboxDrag);

      suppressTextboxCreateUntilRef.current = Date.now() + 350;
      activeTextboxDragRef.current = null;
      drag.node.classList.remove("is-dragging");

      if (!drag.hasMoved) return;

      const clientX = Number(
        evt?.clientX || drag.lastClientX || drag.startClientX,
      );
      const clientY = Number(
        evt?.clientY || drag.lastClientY || drag.startClientY,
      );
      const dx = clientX - drag.startClientX;
      const dy = clientY - drag.startClientY;
      const nextX = clamp(
        drag.origX + dx / Math.max(1, drag.pageWidth),
        0,
        Math.max(0, 1 - drag.width),
      );
      const nextY = clamp(
        drag.origY + dy / Math.max(1, drag.pageHeight),
        0,
        Math.max(0, 1 - drag.height),
      );
      const baseRect = unrotateNormalizedRect(
        nextX,
        nextY,
        drag.width,
        drag.height,
        drag.rotation || 0,
      );

      try {
        await updateTextbox(drag.id, {
          x: baseRect.x,
          y: baseRect.y,
          width: baseRect.width,
          height: baseRect.height,
        });
        requestAnimationFrame(() => {
          renderReadOnlyOverlaysRef.current();
        });
      } catch (err) {
        console.error("Error updating textbox position:", err);
        requestAnimationFrame(() => {
          renderReadOnlyOverlaysRef.current();
        });
      }
    };

    function handleTextboxDragMove(evt) {
      const drag = activeTextboxDragRef.current;
      if (!drag) return;

      evt.preventDefault();
      const dx = evt.clientX - drag.startClientX;
      const dy = evt.clientY - drag.startClientY;

      if (!drag.hasMoved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
      drag.hasMoved = true;
      drag.lastClientX = evt.clientX;
      drag.lastClientY = evt.clientY;

      const nextX = clamp(
        drag.origX + dx / Math.max(1, drag.pageWidth),
        0,
        Math.max(0, 1 - drag.width),
      );
      const nextY = clamp(
        drag.origY + dy / Math.max(1, drag.pageHeight),
        0,
        Math.max(0, 1 - drag.height),
      );

      drag.node.style.left = `${nextX * drag.pageWidth}px`;
      drag.node.style.top = `${nextY * drag.pageHeight}px`;
    }

    const handleTextboxDragStart = (evt) => {
      const handle = evt.target?.closest?.(".igp-ro-textbox-drag-handle");
      if (!handle) return;

      const textboxNode = handle.closest(".igp-ro-textbox");
      if (!textboxNode) return;
      if (!textboxNode.classList.contains("is-active")) return;

      evt.preventDefault();
      evt.stopPropagation();

      const textboxId = textboxNode.getAttribute("data-textbox-id") || "";
      if (!textboxId) return;
      const textbox = textboxesById.get(textboxId);
      if (!textbox) return;

      const pageNumber = Number(
        textboxNode.getAttribute("data-page-number") ||
          textbox.page_number ||
          1,
      );
      const pageEl = viewer.querySelector(
        `.page[data-page-number="${pageNumber}"]`,
      );
      const canvas = pageEl?.querySelector("canvas");
      if (!canvas) return;

      const pageWidth = canvas.clientWidth || canvas.width || 1;
      const pageHeight = canvas.clientHeight || canvas.height || 1;
      const width = Number(textbox.width ?? textbox.coordinates?.width ?? 0.12);
      const height = Number(
        textbox.height ?? textbox.coordinates?.height ?? 0.025,
      );
      const baseX = Number(textbox.x ?? textbox.coordinates?.left ?? 0);
      const baseY = Number(textbox.y ?? textbox.coordinates?.top ?? 0);
      const rotation = normalizeRotation(
        pdfViewerRef.current?.pagesRotation ?? pdfRotation,
      );
      const displayRect = rotateNormalizedRect(
        baseX,
        baseY,
        width,
        height,
        rotation,
      );

      suppressTextboxCreateUntilRef.current = Date.now() + 350;
      setCurrentPage(pageNumber);
      textboxNode.classList.add("is-dragging");

      activeTextboxDragRef.current = {
        id: textboxId,
        node: textboxNode,
        startClientX: evt.clientX,
        startClientY: evt.clientY,
        lastClientX: evt.clientX,
        lastClientY: evt.clientY,
        pageWidth,
        pageHeight,
        origX: displayRect.x,
        origY: displayRect.y,
        width: displayRect.width,
        height: displayRect.height,
        rotation,
        hasMoved: false,
      };

      window.addEventListener("pointermove", handleTextboxDragMove, {
        passive: false,
      });
      window.addEventListener("pointerup", finishTextboxDrag);
      window.addEventListener("pointercancel", finishTextboxDrag);
    };

    viewer.addEventListener("pointerdown", handleTextboxDragStart);
    return () => {
      viewer.removeEventListener("pointerdown", handleTextboxDragStart);
      window.removeEventListener("pointermove", handleTextboxDragMove);
      window.removeEventListener("pointerup", finishTextboxDrag);
      window.removeEventListener("pointercancel", finishTextboxDrag);
      if (activeTextboxDragRef.current?.node) {
        activeTextboxDragRef.current.node.classList.remove("is-dragging");
      }
      activeTextboxDragRef.current = null;
    };
  }, [pdfRotation, setCurrentPage, textboxesById]);

  useEffect(() => {
    if (!selectedTextboxId && !editingTextboxId) return;

    const handleDocumentPointerDown = (evt) => {
      if (editingTextboxId) return;

      const target = evt.target;
      if (!(target instanceof Element)) {
        setSelectedTextboxId(null);
        setTextboxEnabled(false);
        return;
      }

      if (
        target.closest(".igp-ro-textbox") ||
        target.closest(".igp-ro-textbox-editor") ||
        target.closest(".igp-textbox-editor")
      ) {
        return;
      }

      setSelectedTextboxId(null);
      setTextboxEnabled(false);
      suppressTextboxCreateUntilRef.current = Date.now() + 350;
    };

    document.addEventListener("pointerdown", handleDocumentPointerDown, true);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handleDocumentPointerDown,
        true,
      );
    };
  }, [editingTextboxId, selectedTextboxId, setTextboxEnabled]);

  useEffect(() => {
    const handleOpenNote = (ev) => {
      try {
        const incomingNote = ev?.detail?.note;
        if (!incomingNote) return;

        const pageNumber = Number(
          incomingNote.page_number || currentPageRef.current || 1,
        );
        const notesForPage = notesListByPage.get(pageNumber) || [];
        const resolvedNote =
          notesForPage.find((n) => n?.id && n.id === incomingNote?.id) ||
          incomingNote;

        currentPageRef.current = pageNumber;
        setCurrentPage(pageNumber);
        scrollToPage(pageNumber);

        setContextMenuVisible(false);
        setSelectionRect(null);
        latestSelectionPayloadRef.current = null;

        const openEditor = () => {
          const pageEl = viewerRef.current?.querySelector?.(
            `.page[data-page-number="${pageNumber}"]`,
          );
          const rect = pageEl?.getBoundingClientRect?.();
          const anchor = rect
            ? { x: rect.right - 24, y: rect.top + 24 }
            : { x: window.innerWidth * 0.7, y: window.innerHeight * 0.3 };

          openEditorForExistingNote(resolvedNote, pageNumber, anchor);
        };

        openEditor();
      } catch (err) {
        console.error(
          "Error handling reader:open-note in selection core:",
          err,
        );
      }
    };

    window.addEventListener("reader:open-note", handleOpenNote);
    return () => window.removeEventListener("reader:open-note", handleOpenNote);
  }, [
    notesListByPage,
    openEditorForExistingNote,
    scrollToPage,
    setCurrentPage,
  ]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !textboxEnabled) return;

    const handlePageTextboxCreate = async (evt) => {
      if (Date.now() < suppressTextboxCreateUntilRef.current) return;
      if (evt.defaultPrevented) return;
      if (readViewerRotation(pdfViewerRef.current, pdfRotation) !== 0) return;
      const target = evt.target;
      if (target?.closest?.(".igp-ro-textbox")) return;
      if (target?.closest?.(".igp-note-editor, .igp-textbox-editor")) return;
      if (target?.closest?.(".igp-ro-note-indicator")) return;

      const pageEl = target?.closest?.(".page[data-page-number]");
      if (!pageEl || !viewer.contains(pageEl)) return;

      const pageNumber = Number(pageEl.getAttribute("data-page-number") || 0);
      if (!pageNumber) return;

      if (!isFeatureAllowed(drmPolicy, "textbox")) {
        showFeatureNotAllowedNotification("text_box");
        return;
      }

      const canvas = pageEl.querySelector("canvas");
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const displayX = Math.max(
        0,
        Math.min((evt.clientX - rect.left) / rect.width, 0.95),
      );
      const displayY = Math.max(
        0,
        Math.min((evt.clientY - rect.top) / rect.height, 0.95),
      );
      const rotation = normalizeRotation(
        pdfViewerRef.current?.pagesRotation ?? pdfRotation,
      );
      const { x: fracX, y: fracY } = unrotateNormalizedPoint(
        displayX,
        displayY,
        rotation,
      );

      setContextMenuVisible(false);
      setSelectionRect(null);
      latestSelectionPayloadRef.current = null;
      handleNoteEditorClose();
      setSelectedTextboxId(null);

      if (editingTextboxId) {
        await executeTextboxCommit(editingTextboxId);
      }

      createTextbox({ page_number: pageNumber, x: fracX, y: fracY })
        .then((tb) => {
          if (!tb) return;
          setCurrentPage(pageNumber);
          setEditingTextboxId(tb.id);
          textboxDraftRef.current = tb.content || "";
          requestAnimationFrame(() => {
            renderReadOnlyOverlaysRef.current();
          });
        })
        .catch((err) => {
          console.error("Error creating textbox:", err);
        });
    };

    viewer.addEventListener("click", handlePageTextboxCreate);
    return () => viewer.removeEventListener("click", handlePageTextboxCreate);
  }, [
    drmPolicy,
    editingTextboxId,
    executeTextboxCommit,
    handleNoteEditorClose,
    pdfRotation,
    setCurrentPage,
    textboxEnabled,
  ]);

  useEffect(() => {
    if (!pdfDocument) return;

    const run = async () => {
      await Promise.allSettled([
        refreshHighlightsFromApi(),
        refreshUnderlinesFromApi(),
        refreshNotesFromApi(),
        /* refreshDrawingsFromApi(), */
        refreshTextboxesFromApi(),
      ]);
    };

    run();
  }, [pdfDocument]);

  const getCombinedDrawingsForPage = useCallback(
    (pageNumber) => {
      const fromApi = drawingsByPage.get(pageNumber) || [];
      const pending = pendingDrawingsRef.current?.[pageNumber] || [];
      const hiddenIds = hiddenDrawingIdsRef.current;
      const combined = [...fromApi, ...pending];
      const deduped = [];
      const seen = new Set();
      combined.forEach((stroke) => {
        if (!stroke?.id) return;
        if (hiddenIds.has(stroke.id)) return;
        if (seen.has(stroke.id)) return;
        seen.add(stroke.id);
        deduped.push(stroke);
      });
      return deduped;
    },
    [drawingsByPage],
  );

  const addPendingStroke = useCallback((pageNumber, stroke) => {
    if (!pageNumber || !stroke?.id) return;
    const currentPending = pendingDrawingsRef.current;
    const pageStrokes = currentPending[pageNumber] || [];
    if (pageStrokes.some((s) => s.id === stroke.id)) return;
    pendingDrawingsRef.current = {
      ...currentPending,
      [pageNumber]: [...pageStrokes, stroke],
    };
  }, []);

  const removePendingStroke = useCallback((pageNumber, strokeId) => {
    if (!pageNumber || !strokeId) return;
    const latestPending = pendingDrawingsRef.current;
    const remainingPageStrokes = (latestPending[pageNumber] || []).filter(
      (s) => s.id !== strokeId,
    );
    const nextPending = { ...latestPending };
    if (remainingPageStrokes.length) {
      nextPending[pageNumber] = remainingPageStrokes;
    } else {
      delete nextPending[pageNumber];
    }
    pendingDrawingsRef.current = nextPending;
  }, []);

  const setStrokeHidden = useCallback((strokeId, hidden) => {
    if (!strokeId) return;
    if (hidden) {
      hiddenDrawingIdsRef.current.add(strokeId);
    } else {
      hiddenDrawingIdsRef.current.delete(strokeId);
    }
  }, []);

  const getActiveScale = useCallback(() => {
    const liveScale = Number(pdfViewerRef.current?.currentScale || 0);
    if (liveScale > 0) return liveScale;
    return Number(scaleRef.current) > 0 ? Number(scaleRef.current) : 1;
  }, []);

  const getActivePagesRotation = useCallback(
    () => readViewerRotation(pdfViewerRef.current, pdfRotation),
    [pdfRotation],
  );

  // Session-only: store resets to 0 on setDocument. While rotated, show
  // annotation paint but disable selection chrome (menu / frame / text select).
  const isPdfRotated = normalizeRotation(pdfRotation) !== 0;
  const isSelectionUiEnabled = useCallback(
    () => getActivePagesRotation() === 0,
    [getActivePagesRotation],
  );

  const clearCustomOverlays = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer
      .querySelectorAll(
        ".igp-live-selection-layer, .igp-highlight-layer, .igp-readonly-annotation-layer",
      )
      .forEach((el) => {
        el.innerHTML = "";
      });
  }, []);

  const distanceToSegment = useCallback((px, py, x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
      return Math.hypot(px - x1, py - y1);
    }
    const t = Math.max(
      0,
      Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)),
    );
    const projX = x1 + t * dx;
    const projY = y1 + t * dy;
    return Math.hypot(px - projX, py - projY);
  }, []);

  const findStrokeAtPoint = useCallback(
    (pageNumber, localX, localY, toleranceMultiplier = 1) => {
      const strokes = getCombinedDrawingsForPage(pageNumber);
      const currentScale = getActiveScale();
      const rotation = getActivePagesRotation();
      const tolerance = Math.max(6, 10 * toleranceMultiplier);

      const pageEl = viewerRef.current?.querySelector?.(
        `.page[data-page-number="${pageNumber}"]`,
      );
      const { width: baseW, height: baseH } = getBasePageSize({
        pdfViewer: pdfViewerRef.current,
        pageNumber,
        pageEl,
        scale: currentScale,
        rotation: rotation,
      });

      const toDisplay = (point) => {
        const rotated = rotatePoint(point.x, point.y, baseW, baseH, rotation);
        return {
          x: rotated.x * currentScale,
          y: rotated.y * currentScale,
        };
      };

      for (let i = strokes.length - 1; i >= 0; i -= 1) {
        const stroke = strokes[i];
        const points = stroke?.points || [];
        if (points.length === 0) continue;

        let closestDistance = Infinity;

        if (points.length === 1) {
          const p = toDisplay(points[0]);
          closestDistance = Math.hypot(localX - p.x, localY - p.y);
        } else {
          for (let j = 0; j < points.length - 1; j += 1) {
            const start = toDisplay(points[j]);
            const end = toDisplay(points[j + 1]);
            const distance = distanceToSegment(
              localX,
              localY,
              start.x,
              start.y,
              end.x,
              end.y,
            );
            if (distance < closestDistance) {
              closestDistance = distance;
            }
          }
        }

        if (closestDistance <= tolerance) {
          return stroke;
        }
      }

      return null;
    },
    [
      distanceToSegment,
      getActivePagesRotation,
      getActiveScale,
      getCombinedDrawingsForPage,
    ],
  );

  const selectedDrawingDetails = useMemo(() => {
    if (!selectedDrawing) return null;
    const strokes = getCombinedDrawingsForPage(selectedDrawing.pageNumber);
    const stroke = strokes.find(
      (item) => item?.id === selectedDrawing.strokeId,
    );
    if (!stroke?.points?.length) return null;

    const currentScale = getActiveScale();
    const rotation = getActivePagesRotation();
    const pageEl = viewerRef.current?.querySelector?.(
      `.page[data-page-number="${selectedDrawing.pageNumber}"]`,
    );
    const { width: baseW, height: baseH } = getBasePageSize({
      pdfViewer: pdfViewerRef.current,
      pageNumber: selectedDrawing.pageNumber,
      pageEl,
      scale: currentScale,
      rotation: rotation,
    });
    const points = stroke.points.map((p) => {
      const rotated = rotatePoint(p.x, p.y, baseW, baseH, rotation);
      return {
        x: rotated.x * currentScale,
        y: rotated.y * currentScale,
      };
    });
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const pad = Math.max(6, Number(stroke.lineWidth || 1) * currentScale + 4);

    return {
      pageNumber: selectedDrawing.pageNumber,
      stroke,
      bounds: {
        left: Math.max(0, minX - pad),
        top: Math.max(0, minY - pad),
        width: Math.max(0, maxX - minX + pad * 2),
        height: Math.max(0, maxY - minY + pad * 2),
      },
    };
  }, [
    getActivePagesRotation,
    getActiveScale,
    getCombinedDrawingsForPage,
    selectedDrawing,
  ]);

  const selectedHighlightDetails = useMemo(() => {
    if (!selectedHighlight) return null;
    const pageHighlights =
      highlightsByPage.get(selectedHighlight.pageNumber) || [];
    const groupRects = pageHighlights.filter((rect) => {
      const key = rect?.parentId || rect?.id;
      return key && key === selectedHighlight.highlightId;
    });
    if (!groupRects.length) return null;

    const currentScale = getActiveScale();
    const rotation = getActivePagesRotation();
    const pageEl = viewerRef.current?.querySelector?.(
      `.page[data-page-number="${selectedHighlight.pageNumber}"]`,
    );
    const { width: baseW, height: baseH } = getBasePageSize({
      pdfViewer: pdfViewerRef.current,
      pageNumber: selectedHighlight.pageNumber,
      pageEl,
      scale: currentScale,
      rotation: rotation,
    });
    let minLeft = Infinity;
    let minTop = Infinity;
    let maxRight = -Infinity;
    let maxBottom = -Infinity;

    groupRects.forEach((rect) => {
      const displayRect = rotateRect(rect, baseW, baseH, rotation);
      minLeft = Math.min(minLeft, displayRect.left * currentScale);
      minTop = Math.min(minTop, displayRect.top * currentScale);
      maxRight = Math.max(
        maxRight,
        (displayRect.left + displayRect.width) * currentScale,
      );
      maxBottom = Math.max(
        maxBottom,
        (displayRect.top + displayRect.height) * currentScale,
      );
    });

    if (!Number.isFinite(minLeft) || !Number.isFinite(minTop)) return null;

    const pad = 2;
    return {
      pageNumber: selectedHighlight.pageNumber,
      highlightId: selectedHighlight.highlightId,
      highlights: groupRects,
      bounds: {
        left: Math.max(0, minLeft - pad),
        top: Math.max(0, minTop - pad),
        width: Math.max(0, maxRight - minLeft + pad * 2),
        height: Math.max(0, maxBottom - minTop + pad * 2),
      },
    };
  }, [
    getActivePagesRotation,
    getActiveScale,
    highlightsByPage,
    selectedHighlight,
  ]);

  const selectedUnderlineDetails = useMemo(() => {
    if (!selectedUnderline) return null;
    const pageUnderlines =
      underlinesByPage.get(selectedUnderline.pageNumber) || [];
    const groupRects = pageUnderlines.filter((rect) => {
      const key = rect?.parentId || rect?.id;
      return key && key === selectedUnderline.underlineId;
    });
    if (!groupRects.length) return null;

    const currentScale = getActiveScale();
    const rotation = getActivePagesRotation();
    const pageEl = viewerRef.current?.querySelector?.(
      `.page[data-page-number="${selectedUnderline.pageNumber}"]`,
    );
    const { width: baseW, height: baseH } = getBasePageSize({
      pdfViewer: pdfViewerRef.current,
      pageNumber: selectedUnderline.pageNumber,
      pageEl,
      scale: currentScale,
      rotation: rotation,
    });
    let minLeft = Infinity;
    let minTop = Infinity;
    let maxRight = -Infinity;
    let maxBottom = -Infinity;

    groupRects.forEach((rect) => {
      const displayRect = rotateRect(rect, baseW, baseH, rotation);
      minLeft = Math.min(minLeft, displayRect.left * currentScale);
      minTop = Math.min(minTop, displayRect.top * currentScale);
      maxRight = Math.max(
        maxRight,
        (displayRect.left + displayRect.width) * currentScale,
      );
      maxBottom = Math.max(
        maxBottom,
        (displayRect.top + displayRect.height) * currentScale,
      );
    });

    if (!Number.isFinite(minLeft) || !Number.isFinite(minTop)) return null;

    const horizontalPad = 4;
    const topPad = 12;
    const bottomPad = 6;
    return {
      pageNumber: selectedUnderline.pageNumber,
      underlineId: selectedUnderline.underlineId,
      underlines: groupRects,
      bounds: {
        left: Math.max(0, minLeft - horizontalPad),
        top: Math.max(0, minTop - topPad),
        width: Math.max(0, maxRight - minLeft + horizontalPad * 2),
        height: Math.max(0, maxBottom - minTop + topPad + bottomPad),
      },
    };
  }, [
    getActivePagesRotation,
    getActiveScale,
    selectedUnderline,
    underlinesByPage,
  ]);

  const clearSelectionUi = useCallback(() => {
    const selection = window.getSelection();
    if (selection) selection.removeAllRanges();
    latestSelectionPayloadRef.current = null;
    setSelectionRect(null);
    setContextMenuVisible(false);
  }, []);

  const getSelectionActionText = useCallback(() => {
    if (selectedHighlightDetails?.highlights?.[0]?.text) {
      return String(selectedHighlightDetails.highlights[0].text || "").trim();
    }
    if (selectedUnderlineDetails?.underlines?.[0]?.text) {
      return String(selectedUnderlineDetails.underlines[0].text || "").trim();
    }
    const payload =
      latestSelectionPayloadRef.current || getCurrentSelectionPayload();
    return (payload?.selectedText || "").trim();
  }, [
    getCurrentSelectionPayload,
    selectedHighlightDetails,
    selectedUnderlineDetails,
  ]);

  const handleAskAiFromSelection = useCallback(() => {
    const text = getSelectionActionText();
    if (!text) return;
    openReadingAssistantAskAi(text);
    clearSelectionUi();
    setSelectedHighlight(null);
    setSelectedUnderline(null);
  }, [clearSelectionUi, getSelectionActionText, openReadingAssistantAskAi]);

  const handleTranslateFromSelection = useCallback(
    async (uiLanguageCode) => {
      const text = getSelectionActionText();
      if (!text) return;

      const position = buildTranslationAnchor(
        selectionRect,
        contextMenuPosition,
      );

      clearSelectionUi();
      setSelectedHighlight(null);
      setSelectedUnderline(null);

      await runTranslation({ text, uiLanguageCode, position });
    },
    [
      clearSelectionUi,
      contextMenuPosition,
      getSelectionActionText,
      runTranslation,
      selectionRect,
    ],
  );

  const handleHighlightColorAction = useCallback(
    async (hex) => {
      if (!isFeatureAllowed(drmPolicy, "highlights")) {
        showFeatureNotAllowedNotification("highlights");
        return;
      }

      setHighlightColor(hex);

      // Recolor an already-selected highlight
      if (selectedHighlight?.highlightId) {
        editHighlight(selectedHighlight.highlightId, { color: hex });
        return;
      }

      const payload =
        latestSelectionPayloadRef.current || getCurrentSelectionPayload();
      if (!payload) return;
      await applySelectionTool("highlight", payload, { color: hex });
      clearSelectionUi();
    },
    [
      applySelectionTool,
      clearSelectionUi,
      drmPolicy,
      getCurrentSelectionPayload,
      selectedHighlight,
      setHighlightColor,
    ],
  );

  const handleUnderlineAction = useCallback(async () => {
    if (!isFeatureAllowed(drmPolicy, "underline")) {
      showFeatureNotAllowedNotification("underline");
      return;
    }
    // Existing underline: treat as already applied (no convert)
    if (selectedUnderline?.underlineId) {
      return;
    }
    const payload =
      latestSelectionPayloadRef.current || getCurrentSelectionPayload();
    if (!payload) return;
    await applySelectionTool("underline", payload);
    clearSelectionUi();
    setSelectedHighlight(null);
  }, [
    applySelectionTool,
    clearSelectionUi,
    drmPolicy,
    getCurrentSelectionPayload,
    selectedUnderline,
  ]);

  const handleDeleteAnnotationAction = useCallback(async () => {
    try {
      if (selectedHighlight?.highlightId) {
        const annotationId = selectedHighlight.highlightId;
        const actionPayload = selectedHighlightDetails
          ? {
              apiId: annotationId,
              highlights: selectedHighlightDetails.highlights || [],
            }
          : null;
        if (actionPayload?.highlights?.length) {
          pushAction(selectedHighlightDetails.pageNumber, {
            type: "delete-highlight",
            payload: actionPayload,
          });
        }
        removeHighlightById(annotationId);
        setSelectedHighlight(null);
        setContextMenuVisible(false);
        return;
      }

      if (selectedUnderline?.underlineId) {
        const annotationId = selectedUnderline.underlineId;
        const actionPayload = selectedUnderlineDetails
          ? {
              apiId: toApiUnderlineId(annotationId),
              underlines: selectedUnderlineDetails.underlines || [],
            }
          : null;
        deletePdfUnderline(annotationId);
        if (actionPayload?.underlines?.length) {
          pushAction(selectedUnderlineDetails.pageNumber, {
            type: "delete-underline",
            payload: actionPayload,
          });
        }
        setSelectedUnderline(null);
        setContextMenuVisible(false);
      }
    } catch (err) {
      console.error("Error deleting selected annotation:", err);
    }
  }, [
    pushAction,
    selectedHighlight,
    selectedHighlightDetails,
    selectedUnderline,
    selectedUnderlineDetails,
  ]);

  const handleCopyAction = useCallback(async () => {
    const rawText = getSelectionActionText();
    if (!rawText) return;

    const limit = getCopyCharLimit(drmPolicy);
    const textToCopy = truncateForCopy(rawText, limit);
    if (limit !== -1 && rawText.length > limit) {
      showCopyLimitExceededNotification(limit);
    }

    const copied = await copyTextWithFallback(textToCopy);
    if (!copied) {
      showOperationFailedNotification(t("notifications.operations.copyText"));
      return;
    }

    clearSelectionUi();
    setSelectedHighlight(null);
    setSelectedUnderline(null);
  }, [
    clearSelectionUi,
    copyTextWithFallback,
    drmPolicy,
    getSelectionActionText,
    t,
  ]);

  const handleAddNoteAction = useCallback(async () => {
    // Annotation-selected note: create from highlight/underline text if no live selection
    if (selectedHighlight || selectedUnderline) {
      const text = getSelectionActionText();
      if (!text) return;
      if (!isFeatureAllowed(drmPolicy, "notes")) {
        showFeatureNotAllowedNotification("notes");
        return;
      }
      const pageNumber =
        selectedHighlightDetails?.pageNumber ||
        selectedUnderlineDetails?.pageNumber ||
        currentPageRef.current ||
        1;
      const bounds =
        selectedHighlightDetails?.bounds || selectedUnderlineDetails?.bounds;
      // Build a minimal payload so createNoteFromSelection can place the editor
      const payload = {
        selectedText: text,
        fragments: [
          {
            pageNumber,
            highlightCoordinates: [],
            underlineCoordinates: [],
          },
        ],
      };
      await createNoteFromSelection(payload, {
        anchorRect: bounds
          ? {
              left: bounds.left,
              top: bounds.top,
              width: bounds.width,
              height: bounds.height,
              bottom: bounds.top + bounds.height,
            }
          : undefined,
      });
      setSelectedHighlight(null);
      setSelectedUnderline(null);
      setContextMenuVisible(false);
      return;
    }

    const payload =
      latestSelectionPayloadRef.current || getCurrentSelectionPayload();
    if (!payload) return;
    await createNoteFromSelection(payload);
    clearSelectionUi();
  }, [
    clearSelectionUi,
    createNoteFromSelection,
    drmPolicy,
    getCurrentSelectionPayload,
    getSelectionActionText,
    selectedHighlight,
    selectedHighlightDetails,
    selectedUnderline,
    selectedUnderlineDetails,
  ]);

  const selectionToolbar = useMemo(() => {
    const activeColor =
      selectedHighlightDetails?.highlights?.[0]?.color ||
      highlightColor ||
      "#FFF59D";
    const canHighlight = Boolean(contextCapabilities.canHighlight);
    const canUnderline = Boolean(contextCapabilities.canUnderline);
    const canCopy = Boolean(contextCapabilities.canCopy);
    const canNote = Boolean(contextCapabilities.canNote);
    const hasHighlight = Boolean(selectedHighlight?.highlightId);
    const hasUnderline = Boolean(selectedUnderline?.underlineId);
    const canDelete =
      (hasHighlight && canHighlight) || (hasUnderline && canUnderline);

    return {
      colors: getHighlightColorOptions(t),
      activeColor,
      canHighlight,
      canUnderline,
      canDelete,
      underlineActive: hasUnderline,
      deleteLabel: hasUnderline
        ? t("contextMenu.removeUnderline")
        : t("contextMenu.removeHighlight"),
      onUnderline: canUnderline ? handleUnderlineAction : null,
      onHighlightColor: canHighlight ? handleHighlightColorAction : null,
      onDelete: canDelete ? handleDeleteAnnotationAction : null,
      actions: {
        askAi: { onClick: handleAskAiFromSelection },
        copy: {
          onClick: canCopy ? handleCopyAction : null,
          disabled: !canCopy,
        },
        addNote: {
          onClick: canNote ? handleAddNoteAction : null,
          disabled: !canNote,
        },
        translate: {
          onSelectLanguage: handleTranslateFromSelection,
          languageCode: getAiTargetLanguage(),
        },
      },
    };
  }, [
    contextCapabilities.canCopy,
    contextCapabilities.canHighlight,
    contextCapabilities.canNote,
    contextCapabilities.canUnderline,
    handleAddNoteAction,
    handleAskAiFromSelection,
    handleCopyAction,
    handleDeleteAnnotationAction,
    handleHighlightColorAction,
    handleTranslateFromSelection,
    handleUnderlineAction,
    highlightColor,
    selectedHighlight,
    selectedHighlightDetails,
    selectedUnderline,
    t,
  ]);

  // Show selection toolbar when an existing highlight/underline is selected
  const openAnnotationMenuAtPageBounds = useCallback((pageNumber, bounds) => {
    if (!isSelectionUiEnabled()) return false;
    if (!bounds || !pageNumber) return false;
    const pageEl = document.querySelector(
      `.page[data-page-number="${pageNumber}"]`,
    );
    if (!pageEl) return false;

    const pageRect = pageEl.getBoundingClientRect();
    const left = pageRect.left + bounds.left;
    const top = pageRect.top + bounds.top;
    const width = bounds.width;
    const height = bounds.height;
    const selectionBounds = {
      left,
      top,
      width,
      height,
      right: left + width,
      bottom: top + height,
    };

    suppressSelectionMenuCloseUntilRef.current = Date.now() + 500;
    setSelectionRect(selectionBounds);
    setContextMenuPosition({
      x: left + width / 2 - 140,
      y: top + height,
    });
    setContextMenuVisible(true);
    return true;
  }, [isSelectionUiEnabled]);

  useEffect(() => {
    if (!selectedHighlightDetails && !selectedUnderlineDetails) {
      return;
    }

    const details = selectedHighlightDetails || selectedUnderlineDetails;
    let attempts = 0;

    const tryOpen = () => {
      const opened = openAnnotationMenuAtPageBounds(
        details.pageNumber,
        details.bounds,
      );
      if (opened || attempts >= 8) return;
      attempts += 1;
      window.requestAnimationFrame(tryOpen);
    };

    tryOpen();
  }, [
    openAnnotationMenuAtPageBounds,
    selectedHighlightDetails,
    selectedUnderlineDetails,
  ]);

  useEffect(() => {
    if (!selectedDrawing) return;
    const strokes = getCombinedDrawingsForPage(selectedDrawing.pageNumber);
    const exists = strokes.some(
      (stroke) => stroke?.id === selectedDrawing.strokeId,
    );
    if (!exists) {
      setSelectedDrawing(null);
    }
  }, [getCombinedDrawingsForPage, selectedDrawing]);

  useEffect(() => {
    if (!selectedHighlight) return;
    const pageHighlights =
      highlightsByPage.get(selectedHighlight.pageNumber) || [];
    const exists = pageHighlights.some((rect) => {
      const key = rect?.parentId || rect?.id;
      return key && key === selectedHighlight.highlightId;
    });
    if (!exists) {
      setSelectedHighlight(null);
    }
  }, [highlightsByPage, selectedHighlight]);

  useEffect(() => {
    if (!selectedUnderline) return;
    const pageUnderlines =
      underlinesByPage.get(selectedUnderline.pageNumber) || [];
    const exists = pageUnderlines.some((rect) => {
      const key = rect?.parentId || rect?.id;
      return key && key === selectedUnderline.underlineId;
    });
    if (!exists) {
      setSelectedUnderline(null);
    }
  }, [selectedUnderline, underlinesByPage]);

  // Deselect any active annotation when scale changes (zoom, fit-to-page, fit-to-width, etc.)
  useEffect(() => {
    setSelectedHighlight(null);
    setSelectedUnderline(null);
    setSelectedDrawing(null);
  }, [scale]);

  const drawStrokeOnCanvas = useCallback(
    (
      ctx,
      stroke,
      currentScale,
      baseWidth = 0,
      baseHeight = 0,
      rotation = 0,
    ) => {
      if (!ctx || !stroke?.points?.length) return;
      ctx.strokeStyle = stroke.color || "#ff0000";
      ctx.lineWidth = stroke.lineWidth || 1;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      const toDisplay = (point) => {
        const rotated = rotatePoint(
          point.x,
          point.y,
          baseWidth,
          baseHeight,
          rotation,
        );
        return {
          x: rotated.x * currentScale,
          y: rotated.y * currentScale,
        };
      };

      ctx.beginPath();
      const first = toDisplay(stroke.points[0]);
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < stroke.points.length; i += 1) {
        const p = toDisplay(stroke.points[i]);
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    },
    [],
  );

  useEffect(() => {
    const getFloatingToolbarOverlapWidth = (viewerElem) => {
      const toolbar = document.querySelector(".floating-toolbar:not(.hidden)");
      if (!toolbar || !viewerElem) return 0;

      const viewerRect = viewerElem.getBoundingClientRect();
      const toolbarRect = toolbar.getBoundingClientRect();
      const overlap =
        Math.min(viewerRect.right, toolbarRect.right) -
        Math.max(viewerRect.left, toolbarRect.left);

      if (overlap <= 0) return 0;
      // Keep a small gutter so the page doesn't visually touch floating chrome.
      return overlap + 8;
    };

    // Desktop reserves chrome gutters; mobile uses real padding only so
    // fit-to-width fills the screen instead of leaving gray side bands.
    const getFitAvailableSize = () => {
      const viewerElem =
        containerRef.current ||
        document.querySelector(".viewer-container") ||
        document.documentElement;
      const pdfViewEl = document.querySelector(".pdfjs-selection-viewer");
      const style = pdfViewEl ? window.getComputedStyle(pdfViewEl) : null;
      const fallbackH = isMobile ? 0 : 20;
      const fallbackV = isMobile ? 12 : 20;
      const pad = (prop, fallback) =>
        style ? parseFloat(style[prop]) || 0 : fallback;

      const leftPad = pad("paddingLeft", fallbackH);
      const rightPad = pad("paddingRight", fallbackH);
      const topPad = pad("paddingTop", fallbackV);
      const bottomPad = pad("paddingBottom", fallbackV);
      const safeMargin = isMobile ? 0 : 12;
      const contentFrameExtra = isMobile ? 0 : isDoublePageMode ? 80 : 35;
      const floatingToolbarOverlap = isMobile
        ? 0
        : getFloatingToolbarOverlapWidth(viewerElem);

      return {
        availableWidth: Math.max(
          0,
          (viewerElem.clientWidth || window.innerWidth) -
            (leftPad +
              rightPad +
              floatingToolbarOverlap +
              safeMargin +
              contentFrameExtra),
        ),
        availableHeight: Math.max(
          0,
          (viewerElem.clientHeight || window.innerHeight) -
            (topPad + bottomPad + safeMargin),
        ),
      };
    };

    const getContentWidth = (pageWidth) =>
      isDoublePageMode ? pageWidth * 2 + 32 : pageWidth;

    const clampScale = (value) => Math.max(minScale, Math.min(5.0, value));

    const calculateFitToWidthScale = async () => {
      if (!pdfDocument) return null;

      const page = await pdfDocument.getPage(1);
      const { width: pageWidth } = page.getViewport({ scale: PDF_CSS_UNITS });
      const { availableWidth } = getFitAvailableSize();
      return clampScale(availableWidth / getContentWidth(pageWidth));
    };

    const calculateFitToPageScale = async () => {
      if (!pdfDocument) return null;

      const page = await pdfDocument.getPage(1);
      const { width: pageWidth, height: pageHeight } = page.getViewport({
        scale: PDF_CSS_UNITS,
      });
      const { availableWidth, availableHeight } = getFitAvailableSize();
      return clampScale(
        Math.min(
          availableWidth / getContentWidth(pageWidth),
          availableHeight / pageHeight,
        ),
      );
    };

    const handleFitToWidth = async () => {
      setReaderViewMode("fit-to-width");
      const newScale = await calculateFitToWidthScale();
      if (Number.isFinite(newScale) && newScale > 0) {
        setScale(newScale);
      }
    };

    const handleFitToPage = async () => {
      setReaderViewMode("fit-to-page");
      const newScale = await calculateFitToPageScale();
      if (Number.isFinite(newScale) && newScale > 0) {
        setScale(newScale);
      }
    };

    window.addEventListener("reader:fit-to-width", handleFitToWidth);
    window.addEventListener("reader:fit-to-page", handleFitToPage);

    return () => {
      window.removeEventListener("reader:fit-to-width", handleFitToWidth);
      window.removeEventListener("reader:fit-to-page", handleFitToPage);
    };
  }, [
    isDoublePageMode,
    isMobile,
    pdfDocument,
    minScale,
    setReaderViewMode,
    setScale,
  ]);

  useEffect(() => {
    let pendingRafId = 0;
    let pendingTimeoutId = null;

    const handleGoToPage = (ev) => {
      try {
        const requestedPage = Number(ev?.detail?.page || 0);
        if (!Number.isInteger(requestedPage) || requestedPage <= 0) return;

        const pageLimit = Number(totalPages || pdfDocument?.numPages || 0);
        const nextPage = pageLimit
          ? Math.min(requestedPage, pageLimit)
          : requestedPage;

        const pdfViewer = pdfViewerRef.current;
        const viewerLimit = Number(pdfViewer?.pagesCount || 0);
        const viewerPage = viewerLimit
          ? Math.min(nextPage, viewerLimit)
          : nextPage;

        const scrollContainerToPage = (pageNumber) => {
          const container = containerRef.current;
          const viewer = viewerRef.current;
          if (!container || !viewer) return;

          const pageEl = viewer.querySelector(
            `.page[data-page-number="${pageNumber}"]`,
          );
          if (!pageEl) return;

          const targetTop = pageEl.offsetTop;
          container.scrollTo({
            top: Math.max(0, targetTop),
            left: container.scrollLeft,
            behavior: "auto",
          });
        };

        if (pdfViewer && viewerPage > 0) {
          pdfViewer.currentPageNumber = viewerPage;
          try {
            if (typeof pdfViewer.scrollPageIntoView === "function") {
              pdfViewer.scrollPageIntoView({ pageNumber: viewerPage });
            }
          } catch {
            // currentPageNumber is the primary navigation path.
          }

          // Fallback for cases where PDF.js navigation updates page state
          // but doesn't move the scroll container immediately.
          if (pendingRafId) cancelAnimationFrame(pendingRafId);
          if (pendingTimeoutId) clearTimeout(pendingTimeoutId);

          pendingRafId = requestAnimationFrame(() => {
            scrollContainerToPage(viewerPage);
          });
          pendingTimeoutId = setTimeout(() => {
            scrollContainerToPage(viewerPage);
          }, 80);
        }

        currentPageRef.current = viewerPage || nextPage;
        setCurrentPage(viewerPage || nextPage);
      } catch (err) {
        console.error("Error handling reader:go-to-page event:", err);
      }
    };

    window.addEventListener("reader:go-to-page", handleGoToPage);
    return () => {
      if (pendingRafId) cancelAnimationFrame(pendingRafId);
      if (pendingTimeoutId) clearTimeout(pendingTimeoutId);
      window.removeEventListener("reader:go-to-page", handleGoToPage);
    };
  }, [pdfDocument, setCurrentPage, totalPages]);

  useEffect(() => {
    currentPageRef.current = currentPage;
    setContextMenuVisible(false);
    latestSelectionPayloadRef.current = null;
    handleNoteEditorClose();
    setSelectedTextboxId(null);
    setEditingTextboxId(null);
    textboxDraftRef.current = "";
  }, [currentPage, handleNoteEditorClose]);

  useEffect(() => {
    scaleRef.current = scale;
    setContextMenuVisible(false);
    latestSelectionPayloadRef.current = null;
    handleNoteEditorClose();
    setSelectedTextboxId(null);
    setEditingTextboxId(null);
    textboxDraftRef.current = "";
  }, [scale, handleNoteEditorClose]);

  const renderReadOnlyOverlays = useCallback(() => {
    const viewer = viewerRef.current;
    const liveScale = Number(pdfViewerRef.current?.currentScale || 0);
    const currentScale =
      liveScale > 0 ? liveScale : Number(scale) > 0 ? Number(scale) : 1;
    if (!viewer) return;

    // While pagesRotation is settling, skip paint so rotateRect never runs
    // against a canvas that still has the previous orientation.
    if (rotationSettlingRef.current) {
      clearCustomOverlays();
      return;
    }
    const rotation = getActivePagesRotation();

    const appendSelectionDeleteButton = (layer, type, id, bounds) => {
      if (!layer || !type || !id || !bounds) return;
      const delBtn = document.createElement("button");
      delBtn.type = "button";
      delBtn.className = "igp-ro-annotation-delete";
      delBtn.style.pointerEvents = "auto";
      delBtn.setAttribute("data-annotation-type", type);
      delBtn.setAttribute("data-annotation-id", id);
      const labelKey =
        type === "highlight"
          ? "contextMenu.removeHighlight"
          : type === "underline"
            ? "contextMenu.removeUnderline"
            : "common.remove";
      delBtn.setAttribute("aria-label", t(labelKey));
      // Legacy-like placement: slightly outside top-right corner of the frame.
      delBtn.style.left = `${bounds.left + bounds.width - 11}px`;
      delBtn.style.top = `${bounds.top - 11}px`;

      const icon = document.createElement("img");
      icon.className = "igp-ro-annotation-delete-icon";
      icon.src = crossIcon;
      icon.alt = "";
      icon.setAttribute("aria-hidden", "true");
      delBtn.appendChild(icon);
      layer.appendChild(delBtn);
    };

    // Performance: only process overlay layers for pages near the current
    // viewport.  For a 3000-page document this reduces per-render work from
    // O(3000) DOM mutations to O(~10).  Pages outside this window keep their
    // previously-rendered overlays (which remain correct until annotations
    // change), and are refreshed when they enter the viewport via the
    // pagerendered / textlayerrendered events that re-invoke this function.
    const _overlayCurrentPage = Number(
      pdfViewerRef.current?.currentPageNumber || currentPageRef.current || 1,
    );
    const _OVERLAY_BUFFER = 5;
    const _overlayMinPage = Math.max(1, _overlayCurrentPage - _OVERLAY_BUFFER);
    const _overlayMaxPage = _overlayCurrentPage + _OVERLAY_BUFFER;

    const pageEls = viewer.querySelectorAll(".page[data-page-number]");
    pageEls.forEach((pageEl) => {
      const pageNumber = Number(pageEl.getAttribute("data-page-number") || 0);
      if (!pageNumber) return;
      // Skip pages far from the viewport
      if (pageNumber < _overlayMinPage || pageNumber > _overlayMaxPage) return;

      let highlightLayer = pageEl.querySelector(".igp-highlight-layer");
      if (!highlightLayer) {
        highlightLayer = document.createElement("div");
        highlightLayer.className = "igp-highlight-layer";
        highlightLayer.style.pointerEvents = "none";
        pageEl.appendChild(highlightLayer);
      }
      highlightLayer.innerHTML = "";

      let layer = pageEl.querySelector(".igp-readonly-annotation-layer");
      if (!layer) {
        layer = document.createElement("div");
        layer.className = "igp-readonly-annotation-layer";
        layer.style.pointerEvents = "none";
        pageEl.appendChild(layer);
      }

      layer.innerHTML = "";
      layer.style.pointerEvents = "none";

      const { width: baseW, height: baseH } = getBasePageSize({
        pdfViewer: pdfViewerRef.current,
        pageNumber,
        pageEl,
        scale: currentScale,
        rotation: rotation,
      });

      const highlights = highlightsByPage.get(pageNumber) || [];
      const mergedHighlights = [];
      const processed = new Set();

      highlights.forEach((rect, index) => {
        if (processed.has(index)) return;

        let merged = { ...rect };
        const mergedIndices = [index];

        highlights.forEach((otherRect, otherIndex) => {
          if (otherIndex === index || processed.has(otherIndex)) return;

          if (
            highlightRectsOverlapWithScale(
              merged,
              otherRect,
              HIGHLIGHT_TRUE_OVERLAP_TOLERANCE_PX,
              currentScale,
            )
          ) {
            merged = mergeHighlightRects(merged, otherRect);
            mergedIndices.push(otherIndex);
          }
        });

        mergedIndices.forEach((idx) => processed.add(idx));
        mergedHighlights.push(merged);
      });

      mergedHighlights.forEach((rect) => {
        const displayRect = rotateRect(rect, baseW, baseH, rotation);
        if (!displayRect.width || !displayRect.height) return;
        const node = document.createElement("div");
        node.className = "igp-ro-highlight";
        node.style.left = `${displayRect.left * currentScale}px`;
        node.style.top = `${displayRect.top * currentScale}px`;
        node.style.width = `${displayRect.width * currentScale}px`;
        node.style.height = `${displayRect.height * currentScale}px`;
        node.style.setProperty("--igp-ro-highlight-color", rect.color);
        node.style.pointerEvents = "none";
        node.style.cursor = "default";
        if (rect.parentId || rect.id) {
          node.setAttribute(
            "data-highlight-id",
            String(rect.parentId || rect.id),
          );
        }
        node.setAttribute("data-page-number", String(pageNumber));
        highlightLayer.appendChild(node);
      });

      const underlines = underlinesByPage.get(pageNumber) || [];
      underlines.forEach((rect) => {
        const displayRect = rotateRect(rect, baseW, baseH, rotation);
        let underlineWidth = displayRect.width * currentScale;
        let underlineHeight = Math.max(1, displayRect.height * currentScale);
        // After 90/270° the thin strip becomes vertical; keep a visible stroke
        // on whichever axis is the "line thickness".
        const minStroke = 2;
        if (underlineWidth >= underlineHeight) {
          underlineHeight = Math.max(minStroke, underlineHeight);
        } else {
          underlineWidth = Math.max(minStroke, underlineWidth);
        }
        if (underlineWidth < 0.5 && underlineHeight < 0.5) return;
        const node = document.createElement("div");
        node.className = "igp-ro-underline";
        node.style.left = `${displayRect.left * currentScale}px`;
        node.style.top = `${displayRect.top * currentScale}px`;
        node.style.width = `${underlineWidth}px`;
        node.style.height = `${underlineHeight}px`;
        node.style.pointerEvents = "none";
        node.style.cursor = "default";
        if (rect.parentId || rect.id) {
          node.setAttribute(
            "data-underline-id",
            String(rect.parentId || rect.id),
          );
        }
        node.setAttribute("data-page-number", String(pageNumber));
        layer.appendChild(node);
      });

      // Selection chrome (frame + menu) is upright-only; skip while rotated.
      if (rotation === 0 && selectedHighlightDetails?.pageNumber === pageNumber) {
        const frame = document.createElement("div");
        frame.className =
          "igp-ro-selection-frame igp-ro-selection-frame-highlight";
        frame.style.left = `${selectedHighlightDetails.bounds.left}px`;
        frame.style.top = `${selectedHighlightDetails.bounds.top}px`;
        frame.style.width = `${selectedHighlightDetails.bounds.width}px`;
        frame.style.height = `${selectedHighlightDetails.bounds.height}px`;
        layer.appendChild(frame);
      }

      if (rotation === 0 && selectedUnderlineDetails?.pageNumber === pageNumber) {
        const frame = document.createElement("div");
        frame.className =
          "igp-ro-selection-frame igp-ro-selection-frame-underline";
        frame.style.left = `${selectedUnderlineDetails.bounds.left}px`;
        frame.style.top = `${selectedUnderlineDetails.bounds.top}px`;
        frame.style.width = `${selectedUnderlineDetails.bounds.width}px`;
        frame.style.height = `${selectedUnderlineDetails.bounds.height}px`;
        layer.appendChild(frame);
      }

      const noteCount = notesByPage.get(pageNumber) || 0;
      if (noteCount > 0) {
        const pageNotes = notesListByPage.get(pageNumber) || [];

        const notesWithCoords = pageNotes.filter(
          (n) =>
            n?.coordinates?.x !== undefined && n?.coordinates?.y !== undefined,
        );
        const notesWithoutCoords = pageNotes.filter(
          (n) =>
            n?.coordinates?.x === undefined || n?.coordinates?.y === undefined,
        );

        if (notesWithoutCoords.length > 0) {
          const badge = document.createElement("div");
          badge.className =
            "igp-ro-note-indicator note-indicator group-indicator top-indicator";
          badge.setAttribute("data-page-number", String(pageNumber));

          const icon = document.createElement("span");
          icon.className = "igp-ro-note-indicator-icon";
          const iconImg = document.createElement("img");
          iconImg.className = "igp-ro-note-indicator-icon-img";
          iconImg.src = notesIcon;
          iconImg.alt = "";
          iconImg.setAttribute("aria-hidden", "true");
          icon.appendChild(iconImg);
          badge.appendChild(icon);

          const hasSelectedNote = notesWithoutCoords.some(
            (n) => n?.noteType === "selected",
          );
          if (hasSelectedNote) {
            const marker = document.createElement("span");
            marker.className = "igp-ro-note-type-indicator";
            marker.textContent = "S";
            badge.appendChild(marker);
          }

          if (notesWithoutCoords.length > 1) {
            const count = document.createElement("span");
            count.className = "igp-ro-note-count";
            count.textContent = String(notesWithoutCoords.length);
            badge.appendChild(count);
          }

          layer.appendChild(badge);
        }

        const pageCanvas = pageEl.querySelector("canvas");
        const pageWidth = pageCanvas?.clientWidth || pageCanvas?.width || 0;
        const pageHeight = pageCanvas?.clientHeight || pageCanvas?.height || 0;

        notesWithCoords.forEach((note) => {
          const badge = document.createElement("div");
          badge.className =
            "igp-ro-note-indicator note-indicator specific-indicator inline-indicator";
          badge.setAttribute("data-page-number", String(pageNumber));
          badge.setAttribute("data-note-id", String(note.id));

          const displayNotePos = rotateNormalizedPoint(
            Math.max(0, Math.min(note.coordinates.x, 1)),
            Math.max(0, Math.min(note.coordinates.y, 1)),
            rotation,
          );
          badge.style.left = `${displayNotePos.x * pageWidth}px`;
          badge.style.top = `${displayNotePos.y * pageHeight}px`;

          const icon = document.createElement("span");
          icon.className = "igp-ro-note-indicator-icon";
          const iconImg = document.createElement("img");
          iconImg.className = "igp-ro-note-indicator-icon-img";
          iconImg.src = notesIcon;
          iconImg.alt = "";
          iconImg.setAttribute("aria-hidden", "true");
          icon.appendChild(iconImg);
          badge.appendChild(icon);

          layer.appendChild(badge);
        });
      }

      const pageSearchResults = isSearchSidebarOpen
        ? searchResultsByPage.get(pageNumber) || []
        : [];
      const textLayer = pageEl.querySelector(".textLayer");
      if (textLayer && pageSearchResults.length) {
        const querySet = new Set(
          pageSearchResults
            .map((r) => (r?.query || searchQuery || "").trim())
            .filter(Boolean),
        );
        const queries = Array.from(querySet);

        const spans = Array.from(
          textLayer.querySelectorAll(
            "span:not(.endOfContent):not(.markedContent)",
          ),
        ).filter((s) => (s.textContent || "").length > 0);

        if (queries.length && spans.length) {
          const texts = spans.map((s) => s.textContent || "");
          const spanStart = [];
          let acc = 0;
          for (let i = 0; i < texts.length; i += 1) {
            spanStart[i] = acc;
            acc += texts[i].length;
            if (i < texts.length - 1 && !texts[i].endsWith(" ")) {
              acc += 1;
            }
          }

          const concatParts = [];
          for (let i = 0; i < texts.length; i += 1) {
            concatParts.push(texts[i]);
            if (i < texts.length - 1 && !texts[i].endsWith(" ")) {
              concatParts.push(" ");
            }
          }
          const concat = concatParts.join("");
          const concatLower = concat.toLowerCase();
          const pageRect = pageEl.getBoundingClientRect();

          queries.forEach((q) => {
            const qLower = q.toLowerCase();
            if (!qLower) return;

            let idx = concatLower.indexOf(qLower);
            while (idx !== -1) {
              let startSpan = 0;
              let startOffset = 0;
              for (let i = 0; i < spanStart.length; i += 1) {
                if (idx >= spanStart[i]) {
                  startSpan = i;
                  startOffset = idx - spanStart[i];
                } else {
                  break;
                }
              }

              const matchEnd = idx + qLower.length;
              let endSpan = spanStart.length - 1;
              let endOffset = texts[endSpan]?.length || 0;
              for (let i = 0; i < spanStart.length; i += 1) {
                const spanLen =
                  texts[i].length +
                  (i < texts.length - 1 && !texts[i].endsWith(" ") ? 1 : 0);
                if (matchEnd <= spanStart[i] + spanLen) {
                  endSpan = i;
                  endOffset = matchEnd - spanStart[i];
                  break;
                }
              }

              const range = document.createRange();
              const startNode = spans[startSpan].firstChild || spans[startSpan];
              const endNode = spans[endSpan].firstChild || spans[endSpan];
              let rects = [];

              try {
                range.setStart(
                  startNode,
                  Math.min(startOffset, startNode.textContent?.length || 0),
                );
                range.setEnd(
                  endNode,
                  Math.min(endOffset, endNode.textContent?.length || 0),
                );
                rects = Array.from(range.getClientRects());
              } catch {
                idx = concatLower.indexOf(qLower, idx + 1);
                continue;
              } finally {
                if (typeof range.detach === "function") {
                  range.detach();
                }
              }

              rects.forEach((r) => {
                if (r.width < 1 || r.height < 1) return;
                const node = document.createElement("div");
                node.className = "igp-ro-search-match";
                node.style.left = `${r.left - pageRect.left}px`;
                node.style.top = `${r.top - pageRect.top}px`;
                node.style.width = `${r.width}px`;
                node.style.height = `${r.height}px`;
                highlightLayer.appendChild(node);
              });

              idx = concatLower.indexOf(qLower, idx + qLower.length);
            }
          });
        }
      }

      const textboxes = textboxesByPage.get(pageNumber) || [];
      const pageCanvas = pageEl.querySelector("canvas");
      const pageWidth = pageCanvas?.clientWidth || pageCanvas?.width || 0;
      const pageHeight = pageCanvas?.clientHeight || pageCanvas?.height || 0;
      textboxes.forEach((tb) => {
        if (!tb?.id) return;
        const isSelected = selectedTextboxId === tb.id;
        const isEditing = editingTextboxId === tb.id;
        const isDragging = activeTextboxDragRef.current?.id === tb.id;
        const node = document.createElement("div");
        node.className = "igp-ro-textbox";
        if (isSelected) {
          node.classList.add("is-active");
        }
        if (isEditing) {
          node.classList.add("is-editing");
        }
        if (isDragging) {
          node.classList.add("is-dragging");
        }
        node.setAttribute("data-textbox-id", String(tb.id));
        node.setAttribute("data-page-number", String(pageNumber));
        const displayTb = rotateNormalizedRect(
          Math.max(0, tb.x),
          Math.max(0, tb.y),
          Math.max(0, tb.width || 0.12),
          Math.max(0, tb.height || 0.025),
          rotation,
        );
        node.style.left = `${displayTb.x * pageWidth}px`;
        node.style.top = `${displayTb.y * pageHeight}px`;
        node.style.minHeight = `${Math.max(20, displayTb.height * pageHeight)}px`;
        node.style.width = "auto";
        node.style.maxWidth = `${Math.max(80, pageWidth - displayTb.x * pageWidth - 4)}px`;

        if (isEditing) {
          node.style.minWidth = `${Math.max(80, displayTb.width * pageWidth)}px`;
        }

        if (isEditing) {
          const editor = document.createElement("div");
          editor.className = "igp-ro-textbox-editor";

          const textarea = document.createElement("textarea");
          textarea.className = "igp-ro-textbox-textarea";
          textarea.setAttribute("data-textbox-id", String(tb.id));
          textarea.value = textboxDraftRef.current;

          const actions = document.createElement("div");
          actions.className = "igp-ro-textbox-editor-actions";

          const saveBtn = document.createElement("button");
          saveBtn.type = "button";
          saveBtn.className = "igp-ro-textbox-save";
          saveBtn.setAttribute("data-textbox-id", String(tb.id));
          saveBtn.textContent = tb._isLocalDraft
            ? t("epub.noteActionCreate")
            : t("epub.noteActionSave");

          actions.appendChild(saveBtn);
          editor.appendChild(textarea);
          editor.appendChild(actions);
          node.appendChild(editor);
        } else {
          const content = document.createElement("div");
          content.className = "igp-ro-textbox-content";
          content.textContent = (tb.content || "").trim() || "\u00A0";
          node.appendChild(content);
        }

        if (isSelected && !isEditing) {
          const dragHandle = document.createElement("button");
          dragHandle.type = "button";
          dragHandle.className = "igp-ro-textbox-drag-handle";
          dragHandle.setAttribute("aria-label", t("floatingToolbar.dragToMove"));
          const grip = document.createElement("span");
          grip.className = "igp-ro-textbox-grip";
          dragHandle.appendChild(grip);

          const deleteBtn = document.createElement("button");
          deleteBtn.type = "button";
          deleteBtn.className = "igp-ro-textbox-delete";
          deleteBtn.setAttribute("data-textbox-id", String(tb.id));
          deleteBtn.setAttribute("aria-label", t("common.remove"));

          const deleteIcon = document.createElement("img");
          deleteIcon.className = "igp-ro-textbox-delete-icon";
          deleteIcon.src = crossIcon;
          deleteIcon.alt = "";
          deleteIcon.setAttribute("aria-hidden", "true");
          deleteBtn.appendChild(deleteIcon);

          node.appendChild(dragHandle);
          node.appendChild(deleteBtn);
        }

        layer.appendChild(node);
      });

      if (pageCanvas) {
        const drawingCanvas = document.createElement("canvas");
        drawingCanvas.className = "igp-ro-drawing-canvas";
        drawingCanvas.setAttribute("data-page-number", String(pageNumber));

        const cssWidth = pageCanvas.clientWidth || pageCanvas.width || 0;
        const cssHeight = pageCanvas.clientHeight || pageCanvas.height || 0;
        const dpr = window.devicePixelRatio || 1;

        drawingCanvas.width = Math.max(1, Math.floor(cssWidth * dpr));
        drawingCanvas.height = Math.max(1, Math.floor(cssHeight * dpr));
        drawingCanvas.style.width = `${cssWidth}px`;
        drawingCanvas.style.height = `${cssHeight}px`;

        if (drawingEnabled) {
          drawingCanvas.style.pointerEvents = "auto";
          drawingCanvas.style.cursor = "crosshair";
        }

        const ctx = drawingCanvas.getContext("2d");
        if (ctx) {
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.clearRect(0, 0, cssWidth, cssHeight);
          const strokes = getCombinedDrawingsForPage(pageNumber);
          strokes.forEach((stroke) => {
            drawStrokeOnCanvas(
              ctx,
              stroke,
              currentScale,
              baseW,
              baseH,
              rotation,
            );
          });
        }

        layer.appendChild(drawingCanvas);

        if (selectedDrawingDetails?.pageNumber === pageNumber) {
          const frame = document.createElement("div");
          frame.className =
            "igp-ro-selection-frame igp-ro-selection-frame-drawing";
          frame.style.left = `${selectedDrawingDetails.bounds.left}px`;
          frame.style.top = `${selectedDrawingDetails.bounds.top}px`;
          frame.style.width = `${selectedDrawingDetails.bounds.width}px`;
          frame.style.height = `${selectedDrawingDetails.bounds.height}px`;
          layer.appendChild(frame);
          appendSelectionDeleteButton(
            layer,
            "drawing",
            selectedDrawing?.strokeId,
            selectedDrawingDetails.bounds,
          );
        }
      }
    });
  }, [
    clearCustomOverlays,
    drawingEnabled,
    drawStrokeOnCanvas,
    getActivePagesRotation,
    getCombinedDrawingsForPage,
    highlightsByPage,
    notesByPage,
    notesListByPage,
    searchQuery,
    isSearchSidebarOpen,
    isSearchSidebarOpen ? searchResultsByPage : new Map(),
    pdfRotation,
    selectedDrawingDetails,
    selectedDrawing,
    selectedHighlightDetails,
    selectedHighlight,
    selectedUnderlineDetails,
    selectedUnderline,
    scale,
    editingTextboxId,
    selectedTextboxId,
    textboxesByPage,
    underlinesByPage,
    t,
  ]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const clearSelections = () => {
      setSelectedDrawing(null);
      setSelectedHighlight(null);
      setSelectedUnderline(null);
      selectedHighlightRef.current = null;
      selectedUnderlineRef.current = null;
    };

    const findAnnotationAtPoint = (pageEl, clientX, clientY) => {
      if (!pageEl) return null;

      const candidates = Array.from(
        pageEl.querySelectorAll(".igp-ro-highlight, .igp-ro-underline"),
      ).reverse();

      for (const candidate of candidates) {
        const rect = candidate.getBoundingClientRect();
        if (
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom
        ) {
          if (candidate.classList.contains("igp-ro-highlight")) {
            return {
              type: "highlight",
              id: candidate.getAttribute("data-highlight-id") || "",
              pageNumber: Number(
                candidate.getAttribute("data-page-number") || 0,
              ),
            };
          }

          if (candidate.classList.contains("igp-ro-underline")) {
            return {
              type: "underline",
              id: candidate.getAttribute("data-underline-id") || "",
              pageNumber: Number(
                candidate.getAttribute("data-page-number") || 0,
              ),
            };
          }
        }
      }

      return null;
    };

    const onClick = async (evt) => {
      if (
        drawingEnabled ||
        textboxEnabled ||
        highlightEnabled ||
        underlineEnabled
      ) {
        return;
      }

      // While rotated: show annotations as paint only — no select / menu / delete.
      if (!isSelectionUiEnabled()) {
        clearSelections();
        return;
      }

      const target = evt.target;
      if (!(target instanceof Element)) {
        clearSelections();
        return;
      }

      if (
        target.closest(".igp-ro-textbox") ||
        target.closest(".igp-ro-note-indicator") ||
        target.closest(".igp-note-editor")
      ) {
        return;
      }

      const deleteBtn = target.closest(".igp-ro-annotation-delete");
      if (deleteBtn) {
        const annotationType =
          deleteBtn.getAttribute("data-annotation-type") || "";
        const annotationId = deleteBtn.getAttribute("data-annotation-id") || "";
        if (!annotationId) return;

        try {
          if (annotationType === "highlight") {
            const actionPayload = selectedHighlightDetails
              ? {
                  apiId: annotationId,
                  highlights: selectedHighlightDetails.highlights || [],
                }
              : null;
            if (actionPayload?.highlights?.length) {
              pushAction(selectedHighlightDetails.pageNumber, {
                type: "delete-highlight",
                payload: actionPayload,
              });
            }
            removeHighlightById(annotationId);
            setSelectedHighlight(null);
          } else if (annotationType === "underline") {
            const actionPayload = selectedUnderlineDetails
              ? {
                  apiId: toApiUnderlineId(annotationId),
                  underlines: selectedUnderlineDetails.underlines || [],
                }
              : null;
            deletePdfUnderline(annotationId);
            if (actionPayload?.underlines?.length) {
              pushAction(selectedUnderlineDetails.pageNumber, {
                type: "delete-underline",
                payload: actionPayload,
              });
            }
            setSelectedUnderline(null);
          } else if (annotationType === "drawing") {
            if (selectedDrawingDetails?.stroke) {
              const stroke = selectedDrawingDetails.stroke;
              pushAction(selectedDrawingDetails.pageNumber, {
                type: "delete-stroke",
                payload: stroke,
              });
              setStrokeHidden(stroke.id, true);
              removePendingStroke(selectedDrawingDetails.pageNumber, stroke.id);
            }
            silentDeleteDrawing(annotationId);
            setSelectedDrawing(null);
            requestAnimationFrame(() => {
              renderReadOnlyOverlaysRef.current();
            });
          }
        } catch (err) {
          console.error("Error deleting selected annotation:", err);
        }
        return;
      }

      const pageElForHitTest = target.closest(".page[data-page-number]");
      const activeSelectionText =
        window.getSelection()?.toString().trim() || "";

      if (pageElForHitTest && !activeSelectionText) {
        const annotationHit = findAnnotationAtPoint(
          pageElForHitTest,
          evt.clientX,
          evt.clientY,
        );

        if (annotationHit?.pageNumber && annotationHit.id) {
          setCurrentPage(annotationHit.pageNumber);
          setSelectedDrawing(null);
          if (annotationHit.type === "highlight") {
            setSelectedUnderline(null);
            selectedUnderlineRef.current = null;
            setSelectedHighlight((prev) => {
              if (
                prev?.pageNumber === annotationHit.pageNumber &&
                prev?.highlightId === annotationHit.id
              ) {
                selectedHighlightRef.current = null;
                setContextMenuVisible(false);
                setSelectionRect(null);
                return null;
              }
              const next = {
                pageNumber: annotationHit.pageNumber,
                highlightId: annotationHit.id,
              };
              selectedHighlightRef.current = next;
              suppressSelectionMenuCloseUntilRef.current = Date.now() + 500;
              return next;
            });
            return;
          }

          if (annotationHit.type === "underline") {
            setSelectedHighlight(null);
            selectedHighlightRef.current = null;
            setSelectedUnderline((prev) => {
              if (
                prev?.pageNumber === annotationHit.pageNumber &&
                prev?.underlineId === annotationHit.id
              ) {
                selectedUnderlineRef.current = null;
                setContextMenuVisible(false);
                setSelectionRect(null);
                return null;
              }
              const next = {
                pageNumber: annotationHit.pageNumber,
                underlineId: annotationHit.id,
              };
              selectedUnderlineRef.current = next;
              suppressSelectionMenuCloseUntilRef.current = Date.now() + 500;
              return next;
            });
            return;
          }
        }
      }

      const pageEl = target.closest(".page[data-page-number]");
      if (pageEl) {
        const pageNumber = Number(pageEl.getAttribute("data-page-number") || 0);
        if (!pageNumber) {
          clearSelections();
          return;
        }

        const pageCanvas = pageEl.querySelector("canvas");
        if (!pageCanvas) {
          clearSelections();
          return;
        }

        const rect = pageCanvas.getBoundingClientRect();
        const localX = evt.clientX - rect.left;
        const localY = evt.clientY - rect.top;
        const stroke = findStrokeAtPoint(pageNumber, localX, localY, 1);
        if (stroke?.id) {
          setCurrentPage(pageNumber);
          setSelectedHighlight(null);
          setSelectedUnderline(null);
          setSelectedDrawing((prev) => {
            if (
              prev?.pageNumber === pageNumber &&
              prev?.strokeId === stroke.id
            ) {
              return null;
            }
            return { pageNumber, strokeId: stroke.id };
          });
          return;
        }
      }

      clearSelections();
    };

    viewer.addEventListener("click", onClick);
    return () => {
      viewer.removeEventListener("click", onClick);
    };
  }, [
    isSelectionUiEnabled,
    drawingEnabled,
    findStrokeAtPoint,
    highlightEnabled,
    pushAction,
    removePendingStroke,
    setStrokeHidden,
    silentDeleteDrawing,
    setCurrentPage,
    textboxEnabled,
    underlineEnabled,
    selectedDrawingDetails,
    selectedHighlightDetails,
    selectedUnderlineDetails,
  ]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !drawingEnabled) return;

    const getCurrentScale = () => {
      const liveScale = Number(pdfViewerRef.current?.currentScale || 0);
      return liveScale > 0 ? liveScale : Number(scaleRef.current) || 1;
    };

    const getStrokeTransform = (pageNumber) => {
      const currentScale = getCurrentScale();
      const rotation = normalizeRotation(
        pdfViewerRef.current?.pagesRotation ?? pdfRotation,
      );
      const pageEl = viewer.querySelector(
        `.page[data-page-number="${pageNumber}"]`,
      );
      const { width: baseW, height: baseH } = getBasePageSize({
        pdfViewer: pdfViewerRef.current,
        pageNumber,
        pageEl,
        scale: currentScale,
        rotation: rotation,
      });
      return { currentScale, rotation, baseW, baseH };
    };

    const toStoredPoint = (displayX, displayY, transform) =>
      unrotatePoint(
        displayX,
        displayY,
        transform.baseW,
        transform.baseH,
        transform.rotation,
      );

    const redrawPageCanvas = (pageNumber, activeStroke = null) => {
      const canvas = viewer.querySelector(
        `.igp-ro-drawing-canvas[data-page-number="${pageNumber}"]`,
      );
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const cssWidth =
        parseFloat(canvas.style.width) || canvas.clientWidth || 0;
      const cssHeight =
        parseFloat(canvas.style.height) || canvas.clientHeight || 0;
      const dpr = window.devicePixelRatio || 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssWidth, cssHeight);

      const strokes = getCombinedDrawingsForPage(pageNumber);
      const { currentScale, rotation, baseW, baseH } =
        getStrokeTransform(pageNumber);
      strokes.forEach((stroke) => {
        drawStrokeOnCanvas(ctx, stroke, currentScale, baseW, baseH, rotation);
      });
      if (activeStroke) {
        drawStrokeOnCanvas(
          ctx,
          activeStroke,
          currentScale,
          baseW,
          baseH,
          rotation,
        );
      }
    };

    const startStroke = (targetCanvas, clientX, clientY, pageNumber) => {
      if (!isFeatureAllowed(drmPolicy, "freehand_drawing")) {
        showFeatureNotAllowedNotification("freehand_drawing");
        return;
      }

      const rect = targetCanvas.getBoundingClientRect();
      const transform = getStrokeTransform(pageNumber);
      const displayPoint = {
        x: (clientX - rect.left) / transform.currentScale,
        y: (clientY - rect.top) / transform.currentScale,
      };
      const storedPoint = toStoredPoint(
        displayPoint.x,
        displayPoint.y,
        transform,
      );
      const stroke = {
        id: `draw_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        points: [storedPoint],
        color: "#ff0000",
        lineWidth: 1,
      };

      activeDrawingRef.current = { pageNumber, stroke, rect, transform };
      setCurrentPage(pageNumber);
      redrawPageCanvas(pageNumber, stroke);
    };

    const handlePointerDown = (evt) => {
      const targetCanvas = evt.target?.closest?.(".igp-ro-drawing-canvas");
      if (!targetCanvas) return;
      if (evt.button !== undefined && evt.button !== 0) return;

      const pageNumber = Number(
        targetCanvas.getAttribute("data-page-number") || 0,
      );
      if (!pageNumber) return;

      evt.preventDefault();
      startStroke(targetCanvas, evt.clientX, evt.clientY, pageNumber);
    };

    const handlePointerMove = (evt) => {
      const active = activeDrawingRef.current;
      if (!active?.stroke) return;

      evt.preventDefault();
      const transform =
        active.transform || getStrokeTransform(active.pageNumber);
      const displayX =
        (evt.clientX - active.rect.left) / transform.currentScale;
      const displayY = (evt.clientY - active.rect.top) / transform.currentScale;
      active.stroke.points.push(toStoredPoint(displayX, displayY, transform));
      redrawPageCanvas(active.pageNumber, active.stroke);
    };

    const finishStroke = () => {
      const active = activeDrawingRef.current;
      if (!active?.stroke) return;

      const { pageNumber, stroke } = active;
      activeDrawingRef.current = null;

      setStrokeHidden(stroke.id, false);
      addPendingStroke(pageNumber, stroke);
      pushAction(pageNumber, { type: "add-stroke", payload: stroke });

      redrawPageCanvas(pageNumber);

      saveFreehandDrawing({
        id: stroke.id,
        page_number: pageNumber,
        points: stroke.points,
      })
        .catch((err) => {
          console.error("Error saving drawing:", err);
        })
        .finally(() => {
          removePendingStroke(pageNumber, stroke.id);

          requestAnimationFrame(() => {
            renderReadOnlyOverlaysRef.current();
          });
        });
    };

    viewer.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointermove", handlePointerMove, {
      passive: false,
    });
    window.addEventListener("pointerup", finishStroke);
    window.addEventListener("pointercancel", finishStroke);

    return () => {
      viewer.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", finishStroke);
      window.removeEventListener("pointercancel", finishStroke);
      activeDrawingRef.current = null;
    };
  }, [
    drawingEnabled,
    addPendingStroke,
    drmPolicy,
    drawStrokeOnCanvas,
    getCombinedDrawingsForPage,
    pdfRotation,
    pushAction,
    removePendingStroke,
    setCurrentPage,
    setStrokeHidden,
  ]);

  useEffect(() => {
    renderReadOnlyOverlaysRef.current = renderReadOnlyOverlays;
  }, [renderReadOnlyOverlays]);

  useEffect(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    emitUndoRedoCapabilities();
  }, [pdfDocument, emitUndoRedoCapabilities]);

  useEffect(() => {
    emitUndoRedoCapabilities();
  }, [emitUndoRedoCapabilities]);

  useEffect(() => {
    const handleUndo = async () => {
      const now = Date.now();
      if (undoRedoInProgressRef.current) return;
      if (now - lastUndoRedoRunRef.current.undo < UNDO_REDO_DEBOUNCE_MS) return;
      if (undoStackRef.current.length === 0) return;

      undoRedoInProgressRef.current = true;
      lastUndoRedoRunRef.current.undo = now;

      try {
        const action = undoStackRef.current.pop();
        if (!action) return;

        redoStackRef.current.push(action);
        emitUndoRedoCapabilities();

        const pageNumber = action.pageNumber || action.payload?.pageNumber || 1;
        if (currentPageRef.current !== pageNumber) {
          currentPageRef.current = pageNumber;
          setCurrentPage(pageNumber);
        }
        clearAnnotationSelection();

        if (action.type === "add-highlight") {
          const group = Array.isArray(action.payload?.highlights)
            ? action.payload.highlights
            : [];
          const apiId =
            action.payload?.apiId || group[0]?.parentId || group[0]?.id;
          if (apiId) {
            removeHighlightById(apiId);
          }

          // Restore highlights that were replaced by overlap-merge on create.
          const removed = Array.isArray(action.payload?.removed)
            ? action.payload.removed
            : [];
          removed.forEach((hl) => {
            if (!hl?.id) return;
            const coordinates = Array.isArray(hl.coordinates)
              ? hl.coordinates
              : hl.coordinates
                ? [hl.coordinates]
                : [];
            if (coordinates.length === 0) return;
            createHighlight({
              id: hl.id,
              page_number: hl.page_number || pageNumber,
              content: hl.content || hl.text || "",
              color: hl.color || "#FFF59D",
              coordinates,
              timestamp: hl.timestamp,
            });
          });
        } else if (action.type === "delete-highlight") {
          const group = Array.isArray(action.payload?.highlights)
            ? action.payload.highlights
            : [];
          if (group.length > 0) {
            const first = group[0];
            const coordinates = group.map((rect) => ({
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
            }));
            const recreatedHighlights = await createHighlight({
              id: action.payload?.apiId || first.parentId || first.id,
              page_number: pageNumber,
              content: first.text || "",
              color: first.color || "#FFF59D",
              coordinates,
            });
            const resolvedApiId = resolveHighlightApiId({
              resolvedHighlights: recreatedHighlights,
              pageNumber,
              content: first.text || "",
              color: first.color || "#FFF59D",
              coordinates,
              fallbackId: action.payload?.apiId || first.parentId || first.id,
            });
            if (action.payload && resolvedApiId) {
              action.payload.apiId = resolvedApiId;
            }
            group.forEach((rect) => {
              rect.id = resolvedApiId || rect.id;
              rect.parentId = resolvedApiId || rect.parentId;
            });
          }
        } else if (action.type === "add-underline") {
          if (action.payload && typeof action.payload === "object") {
            action.payload.wasUndone = true;
          }

          const group = Array.isArray(action.payload?.underlines)
            ? action.payload.underlines
            : [];
          const rawAdded = Array.isArray(action.payload?.added)
            ? action.payload.added
            : group;
          const added = Array.isArray(rawAdded) ? rawAdded : [];
          const removed = Array.isArray(action.payload?.removed)
            ? action.payload.removed
            : [];
          const apiCreatedIds = Array.isArray(action.payload?.apiCreatedIds)
            ? action.payload.apiCreatedIds
            : [];

          const toRemoveIds = new Set(added.map((u) => u?.id).filter(Boolean));

          const matchesCoords = (storeUnderline, rect) => {
            if (Number(storeUnderline?.page_number || 0) !== pageNumber) {
              return false;
            }
            const coords = Array.isArray(storeUnderline?.coordinates)
              ? storeUnderline.coordinates
              : storeUnderline?.coordinates
                ? [storeUnderline.coordinates]
                : [];

            return coords.some((coord) => underlineRectsMatch(coord, rect));
          };

          const store = useReaderStore.getState();
          const currentStoreUnderlines =
            store.annotations?.underlines?.data || [];
          const matchedServerIds = currentStoreUnderlines
            .filter((storeUnderline) => {
              if (!storeUnderline) return false;
              return (
                !toRemoveIds.has(storeUnderline.id) &&
                added.some((a) => matchesCoords(storeUnderline, a))
              );
            })
            .map((storeUnderline) => storeUnderline.id)
            .filter(Boolean);

          const filteredStoreUnderlines = currentStoreUnderlines.filter(
            (storeUnderline) => {
              if (toRemoveIds.has(storeUnderline.id)) return false;
              if (added.some((a) => matchesCoords(storeUnderline, a))) {
                return false;
              }
              return true;
            },
          );

          if (removed.length > 0) {
            removed.forEach((underline) => {
              createPdfUnderline({
                id: toApiUnderlineId(
                  String(underline?.parentId || underline?.id || ""),
                ),
                page_number: pageNumber,
                content: underline?.text || action.payload?.content || "",
                coordinates: {
                  left: Number(underline?.left || 0),
                  top: Number(underline?.top || 0),
                  width: Number(underline?.width || 0),
                  height: Number(underline?.height || 1),
                },
              });
            });
          }

          const idsToDelete =
            apiCreatedIds.length > 0
              ? apiCreatedIds
              : [
                  ...matchedServerIds,
                  ...added.map((ul) => ul?.id).filter(Boolean),
                  ...added.map((ul) => ul?.parentId).filter(Boolean),
                  action.payload?.apiId,
                ].filter(Boolean);

          const normalizedDeleteIds = Array.from(
            new Set(idsToDelete.map((id) => toApiUnderlineId(String(id)))),
          ).filter(Boolean);

          if (normalizedDeleteIds.length > 0) {
            // Service handles both store and API background sync correctly
            normalizedDeleteIds.forEach((id) => deletePdfUnderline(id));
          }
        } else if (action.type === "delete-underline") {
          const group = Array.isArray(action.payload?.underlines)
            ? action.payload.underlines
            : [];
          if (group.length > 0) {
            const first = group[0];
            const coordinates = group.map((rect) => ({
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
            }));
            const recreatedUnderline = await createPdfUnderline({
              id: action.payload?.apiId || first.parentId || first.id,
              page_number: pageNumber,
              content: first.text || action.payload?.content || "",
              coordinates,
            });
            const resolvedApiId = resolveUnderlineApiId({
              resolvedUnderlines: Array.isArray(recreatedUnderline)
                ? recreatedUnderline
                : recreatedUnderline
                  ? [recreatedUnderline]
                  : [],
              pageNumber,
              content: first.text || action.payload?.content || "",
              coordinates,
              fallbackId:
                recreatedUnderline?.id ||
                action.payload?.apiId ||
                first.parentId ||
                first.id,
            });
            if (action.payload && resolvedApiId) {
              action.payload.apiId = resolvedApiId;
              action.payload.apiCreatedIds = resolvedApiId
                ? [resolvedApiId]
                : [];
            }
            group.forEach((rect) => {
              rect.id = resolvedApiId || rect.id;
              rect.parentId = resolvedApiId || rect.parentId;
            });
          }
        } else if (action.type === "add-stroke") {
          const stroke = action.payload;
          if (stroke?.id) {
            setStrokeHidden(stroke.id, true);
            removePendingStroke(pageNumber, stroke.id);
            silentDeleteDrawing(stroke.id);
          }
        } else if (action.type === "delete-stroke") {
          const stroke = action.payload;
          if (
            stroke?.id &&
            Array.isArray(stroke?.points) &&
            stroke.points.length > 0
          ) {
            setStrokeHidden(stroke.id, false);
            addPendingStroke(pageNumber, stroke);
            try {
              await saveFreehandDrawing({
                id: stroke.id,
                page_number: pageNumber,
                points: stroke.points,
              });
            } finally {
              removePendingStroke(pageNumber, stroke.id);
            }
          }
        }
      } catch (err) {
        console.error("Error handling reader:undo in selection core:", err);
      } finally {
        undoRedoInProgressRef.current = false;
        emitUndoRedoCapabilities();
      }
    };

    const handleRedo = async () => {
      const now = Date.now();
      if (undoRedoInProgressRef.current) return;
      if (now - lastUndoRedoRunRef.current.redo < UNDO_REDO_DEBOUNCE_MS) return;
      if (redoStackRef.current.length === 0) return;

      undoRedoInProgressRef.current = true;
      lastUndoRedoRunRef.current.redo = now;

      try {
        const action = redoStackRef.current.pop();
        if (!action) return;

        undoStackRef.current.push(action);
        emitUndoRedoCapabilities();

        const pageNumber = action.pageNumber || action.payload?.pageNumber || 1;
        if (currentPageRef.current !== pageNumber) {
          currentPageRef.current = pageNumber;
          setCurrentPage(pageNumber);
        }
        clearAnnotationSelection();

        if (action.type === "add-highlight") {
          const group = Array.isArray(action.payload?.highlights)
            ? action.payload.highlights
            : [];
          const first = group[0];
          const coordinates =
            group.length > 0
              ? group.map((rect) => ({
                  left: rect.left,
                  top: rect.top,
                  width: rect.width,
                  height: rect.height,
                }))
              : Array.isArray(action.payload?.coordinates)
                ? action.payload.coordinates
                : [];
          if (coordinates.length === 0) {
            return;
          }

          // Re-apply overlap replacement: remove restored predecessors again.
          const removed = Array.isArray(action.payload?.removed)
            ? action.payload.removed
            : [];
          removed.forEach((hl) => {
            if (hl?.id) removeHighlightById(hl.id);
          });

          const recreatedHighlights = await createHighlight({
            id: action.payload?.apiId || first?.parentId || first?.id,
            page_number: pageNumber,
            content: first?.text || action.payload?.content || "",
            color: first?.color || action.payload?.color || "#FFF59D",
            coordinates,
          });
          const resolvedApiId = resolveHighlightApiId({
            resolvedHighlights: recreatedHighlights,
            pageNumber,
            content: first?.text || action.payload?.content || "",
            color: first?.color || action.payload?.color || "#FFF59D",
            coordinates,
            fallbackId: action.payload?.apiId || first?.parentId || first?.id,
          });
          if (action.payload && resolvedApiId) {
            action.payload.apiId = resolvedApiId;
            action.payload.apiCreatedIds = resolvedApiId ? [resolvedApiId] : [];
          }
          if (group.length > 0) {
            group.forEach((rect) => {
              rect.id = resolvedApiId || rect.id;
              rect.parentId = resolvedApiId || rect.parentId;
            });
          } else if (action.payload && resolvedApiId) {
            action.payload.highlights = coordinates.map((coord) => ({
              left: coord.left,
              top: coord.top,
              width: coord.width,
              height: coord.height,
              id: resolvedApiId,
              parentId: resolvedApiId,
              text: action.payload?.content || "",
              color: action.payload?.color || "#FFF59D",
            }));
          }
        } else if (action.type === "delete-highlight") {
          const apiId = action.payload?.apiId;
          if (apiId) {
            removeHighlightById(apiId);
          }
        } else if (action.type === "add-underline") {
          const rawAdded = Array.isArray(action.payload)
            ? action.payload
            : action.payload?.added || [];
          const rawRemoved = Array.isArray(action.payload)
            ? []
            : action.payload?.removed || [];
          const rawRedoRects = action.payload?.redoRects;
          const toAddOnRedo = Array.isArray(rawRedoRects)
            ? rawRedoRects
            : Array.isArray(rawAdded)
              ? rawAdded
              : [rawAdded];
          const removed = Array.isArray(rawRemoved) ? rawRemoved : [rawRemoved];

          const removedIds = removed
            .map((underline) => underline?.id || underline?.parentId)
            .filter(Boolean);
          if (removedIds.length > 0) {
            // Service handles both store and API background sync correctly
            removedIds.forEach((id) => deletePdfUnderline(id));
          }

          const validRedoRects = toAddOnRedo.filter(
            (underline) =>
              underline &&
              typeof underline.left === "number" &&
              typeof underline.top === "number" &&
              typeof underline.width === "number",
          );

          if (validRedoRects.length > 0) {
            const optimisticParentId = toApiUnderlineId(
              String(
                action.payload?.apiId ||
                  action.payload?.apiCreatedIds?.[0] ||
                  `underline-${Date.now()}-${Math.random()}-${pageNumber}`,
              ),
            );
            const optimisticEntries = validRedoRects.map((underline) => ({
              id: optimisticParentId,
              page_number: pageNumber,
              content: underline?.text || action.payload?.content || "",
              coordinates: {
                left: Number(underline.left || 0),
                top: Number(underline.top || 0),
                width: Number(underline.width || 0),
                height: Number(underline.height || 1),
              },
            }));

            // Optimistic store update is now handled within createPdfUnderline
          }

          const apiCreatedIds = Array.isArray(action.payload?.apiCreatedIds)
            ? action.payload.apiCreatedIds
            : [];

          if (apiCreatedIds.length > 0) {
            const coords = (
              Array.isArray(action.payload?.redoRects)
                ? action.payload.redoRects
                : validRedoRects
            ).map((rect) => ({
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
            }));

            if (coords.length > 0) {
              const recreatedUnderline = await createPdfUnderline({
                id: apiCreatedIds[0],
                page_number: pageNumber,
                content: action.payload?.content || "",
                coordinates: coords,
              });

              const resolvedApiId = resolveUnderlineApiId({
                resolvedUnderlines: Array.isArray(recreatedUnderline)
                  ? recreatedUnderline
                  : recreatedUnderline
                    ? [recreatedUnderline]
                    : [],
                pageNumber,
                content: action.payload?.content || "",
                coordinates: coords,
                fallbackId: recreatedUnderline?.id || apiCreatedIds[0],
              });

              if (action.payload && typeof action.payload === "object") {
                action.payload.apiId = resolvedApiId;
                action.payload.apiCreatedIds = resolvedApiId
                  ? [resolvedApiId]
                  : [];
              }

              if (resolvedApiId) {
                const syncParentId = (underline) => {
                  if (underline && typeof underline === "object") {
                    underline.parentId = resolvedApiId;
                    underline.id = resolvedApiId;
                  }
                };

                if (Array.isArray(action.payload?.added)) {
                  action.payload.added.forEach(syncParentId);
                }
                if (Array.isArray(action.payload?.redoRects)) {
                  action.payload.redoRects.forEach(syncParentId);
                }
                if (Array.isArray(action.payload?.underlines)) {
                  action.payload.underlines.forEach(syncParentId);
                }
              }
            }
          } else if (validRedoRects.length > 0) {
            await Promise.all(
              validRedoRects.map((underline) =>
                silentCreateUnderline(pageNumber, underline),
              ),
            );
          }
        } else if (action.type === "delete-underline") {
          const apiId = action.payload?.apiId;
          if (apiId) {
            deletePdfUnderline(apiId);
          }
        } else if (action.type === "add-stroke") {
          const stroke = action.payload;
          if (
            stroke?.id &&
            Array.isArray(stroke?.points) &&
            stroke.points.length > 0
          ) {
            setStrokeHidden(stroke.id, false);
            addPendingStroke(pageNumber, stroke);
            try {
              await saveFreehandDrawing({
                id: stroke.id,
                page_number: pageNumber,
                points: stroke.points,
              });
            } finally {
              removePendingStroke(pageNumber, stroke.id);
            }
          }
        } else if (action.type === "delete-stroke") {
          const stroke = action.payload;
          if (stroke?.id) {
            setStrokeHidden(stroke.id, true);
            removePendingStroke(pageNumber, stroke.id);
            silentDeleteDrawing(stroke.id);
          }
        }
      } catch (err) {
        console.error("Error handling reader:redo in selection core:", err);
      } finally {
        undoRedoInProgressRef.current = false;
        emitUndoRedoCapabilities();
      }
    };

    window.addEventListener("reader:undo", handleUndo);
    window.addEventListener("reader:redo", handleRedo);

    return () => {
      window.removeEventListener("reader:undo", handleUndo);
      window.removeEventListener("reader:redo", handleRedo);
    };
  }, [
    clearAnnotationSelection,
    createUnderlineNoRefresh,
    addPendingStroke,
    emitUndoRedoCapabilities,
    removePendingStroke,
    resolveHighlightApiId,
    resolveUnderlineApiId,
    setStrokeHidden,
    scrollToPage,
    setCurrentPage,
    silentCreateUnderline,
    silentDeleteDrawing,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    const viewer = viewerRef.current;

    if (!container || !viewer || !pdfDocument) {
      return;
    }

    setPdfPagesReady(false);

    const eventBus = new EventBus();
    const linkService = new PDFLinkService({ eventBus });

    const pdfViewer = new PDFViewer({
      container,
      viewer,
      eventBus,
      linkService,
      textLayerMode: 1,
      removePageBorders: false,
    });

    const onPagesInit = () => {
      setPdfPagesReady(true);

      setTotalPages(
        effectivePdfDocument?.numPages || pdfDocument?.numPages || 0,
      );

      if (Number.isFinite(scaleRef.current) && scaleRef.current > 0) {
        syncScaleRef.current = true;
        pdfViewer.currentScale = scaleRef.current;
        requestAnimationFrame(() => {
          syncScaleRef.current = false;
        });
      } else {
        pdfViewer.currentScaleValue = "page-width";
      }

      const storePage = Number(useReaderStore.getState().currentPage);
      const targetPage =
        Number.isInteger(storePage) && storePage > 0
          ? storePage
          : Number(currentPageRef.current) || 1;
      currentPageRef.current = targetPage;

      const viewerPages = Number(
        pdfViewer.pagesCount ||
          effectivePdfDocument?.numPages ||
          pdfDocument?.numPages ||
          0,
      );
      // Skip if target is beyond the first chunk; sync effect runs after proxy.
      if (
        Number.isInteger(targetPage) &&
        targetPage > 0 &&
        (viewerPages === 0 || targetPage <= viewerPages)
      ) {
        syncPageRef.current = true;
        pdfViewer.currentPageNumber = targetPage;
        requestAnimationFrame(() => {
          syncPageRef.current = false;
        });
      }

      renderReadOnlyOverlaysRef.current();
      syncPageLoadingIndicators();
    };

    const onPageChanging = (evt) => {
      if (syncPageRef.current) return;
      const nextPage = evt?.pageNumber;
      if (
        Number.isInteger(nextPage) &&
        nextPage > 0 &&
        nextPage !== currentPageRef.current
      ) {
        setCurrentPage(nextPage);
      }
    };

    const onScaleChanging = (evt) => {
      if (syncScaleRef.current) return;
      const nextScale = evt?.scale;
      if (Number.isFinite(nextScale) && nextScale > 0) {
        setScale(nextScale);
        requestAnimationFrame(() => {
          renderReadOnlyOverlaysRef.current();
          syncPageLoadingIndicators();
        });
      }
    };

    const onUpdateViewArea = () => {
      syncPageLoadingIndicators();
    };

    const onPageRendered = () => {
      syncPageLoadingIndicators();
      renderReadOnlyOverlaysRef.current();
    };

    const onTextLayerRendered = () => {
      syncPageLoadingIndicators();
      renderReadOnlyOverlaysRef.current();
    };

    eventBus.on("pagesinit", onPagesInit);
    eventBus.on("pagechanging", onPageChanging);
    eventBus.on("scalechanging", onScaleChanging);
    eventBus.on("updateviewarea", onUpdateViewArea);
    eventBus.on("pagerendered", onPageRendered);
    eventBus.on("textlayerrendered", onTextLayerRendered);

    linkService.setViewer(pdfViewer);
    linkService.setDocument(effectivePdfDocument, null);
    pdfViewer.setDocument(effectivePdfDocument);
    syncPageLoadingIndicators();

    eventBusRef.current = eventBus;
    linkServiceRef.current = linkService;
    pdfViewerRef.current = pdfViewer;

    const initialReaderViewMode = useReaderStore.getState().readerViewMode;
    if (
      !didApplyInitialFitModeRef.current &&
      (initialReaderViewMode === "fit-to-width" ||
        initialReaderViewMode === "fit-to-page")
    ) {
      didApplyInitialFitModeRef.current = true;
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent(
            initialReaderViewMode === "fit-to-width"
              ? "reader:fit-to-width"
              : "reader:fit-to-page",
          ),
        );
      }, 50);
    } else {
      didApplyInitialFitModeRef.current = true;
    }

    return () => {
      eventBus.off("pagesinit", onPagesInit);
      eventBus.off("pagechanging", onPageChanging);
      eventBus.off("scalechanging", onScaleChanging);
      eventBus.off("updateviewarea", onUpdateViewArea);
      eventBus.off("pagerendered", onPageRendered);
      eventBus.off("textlayerrendered", onTextLayerRendered);

      try {
        pdfViewer.setDocument(null);
        linkService.setDocument(null, null);
      } catch {}

      viewer.innerHTML = "";

      pdfViewerRef.current = null;
      linkServiceRef.current = null;
      eventBusRef.current = null;
      didApplyInitialFitModeRef.current = false;
      setPdfPagesReady(false);
    };
  }, [
    effectivePdfDocument,
    isChunked,
    pdfDocument,
    setCurrentPage,
    setScale,
    setTotalPages,
    syncPageLoadingIndicators,
  ]);

  useEffect(() => {
    const pdfViewer = pdfViewerRef.current;
    if (!pdfViewer || !pdfPagesReady) return;

    const pagesCount = Number(pdfViewer.pagesCount || 0);
    if (!pagesCount) return;

    // SpreadMode: 0 NONE, 1 ODD (1–2…), 2 EVEN (1 alone, then 2–3…)
    const nextSpreadMode = !isDoublePageMode
      ? 0
      : isolateCoverInDoublePage
        ? 2
        : 1;
    if (pdfViewer.spreadMode === nextSpreadMode) return;

    try {
      pdfViewer.spreadMode = nextSpreadMode;
    } catch {
      return;
    }
    requestAnimationFrame(() => {
      renderReadOnlyOverlaysRef.current();
    });
  }, [isDoublePageMode, isolateCoverInDoublePage, pdfPagesReady]);

  useEffect(() => {
    lastAppliedSpreadModeRef.current = null;
  }, [pdfDocument]);

  useEffect(() => {
    if (!pdfPagesReady) return;

    const nextSpreadMode = !isDoublePageMode
      ? 0
      : isolateCoverInDoublePage
        ? 2
        : 1;
    const previousSpreadMode = lastAppliedSpreadModeRef.current;
    lastAppliedSpreadModeRef.current = nextSpreadMode;

    if (previousSpreadMode === null || previousSpreadMode === nextSpreadMode) {
      return;
    }

    if (readerViewMode !== "fit-to-width" && readerViewMode !== "fit-to-page") {
      return;
    }

    const timer = setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent(
          readerViewMode === "fit-to-width"
            ? "reader:fit-to-width"
            : "reader:fit-to-page",
        ),
      );
    }, 50);

    return () => clearTimeout(timer);
  }, [
    isDoublePageMode,
    isolateCoverInDoublePage,
    pdfPagesReady,
    readerViewMode,
  ]);

  // Re-run when viewer is ready / chunked proxy expands page count so a
  // restored store page is applied to PDF.js (not only the page indicator).
  useEffect(() => {
    const pdfViewer = pdfViewerRef.current;
    if (!pdfViewer || !pdfPagesReady) return;
    if (!Number.isInteger(currentPage) || currentPage <= 0) return;

    const viewerPages = Number(pdfViewer.pagesCount || 0);
    if (viewerPages > 0 && currentPage > viewerPages) return;
    if (pdfViewer.currentPageNumber === currentPage) return;

    currentPageRef.current = currentPage;
    syncPageRef.current = true;
    pdfViewer.currentPageNumber = currentPage;
    requestAnimationFrame(() => {
      syncPageRef.current = false;
    });
  }, [currentPage, pdfPagesReady, effectivePdfDocument]);

  useEffect(() => {
    const pdfViewer = pdfViewerRef.current;
    if (!pdfViewer || !pdfDocument || documentType !== "pdf") return;

    const nextRotation = normalizeRotation(pdfRotation);
    if (Math.abs((pdfViewer.pagesRotation || 0) - nextRotation) < 0.001) {
      return;
    }

    // Rotating swaps page width/height, so the same scrollTop maps to a
    // different page. Capture page + in-page offset and restore after reflow.
    const pageNumber =
      Number(pdfViewer.currentPageNumber) ||
      Number(currentPageRef.current) ||
      1;
    const container = containerRef.current;
    let pageOffsetRatio = 0;
    if (container) {
      const pageEl = viewerRef.current?.querySelector(
        `.page[data-page-number="${pageNumber}"]`,
      );
      if (pageEl?.offsetHeight > 0) {
        pageOffsetRatio = Math.max(
          0,
          Math.min(
            1,
            (container.scrollTop - pageEl.offsetTop) / pageEl.offsetHeight,
          ),
        );
      }
    }

    // Ignore pagechanging events while PDF.js recomputes visible pages.
    syncPageRef.current = true;
    // Block overlay paints until canvas/textLayer catch up to pagesRotation.
    rotationSettlingRef.current = true;

    pdfViewer.pagesRotation = nextRotation;
    pdfViewer.update();

    // Bounds / selection UI are invalid after rotate. Clear overlays now;
    // re-paint only after settle so rotateRect matches the visible page.
    setSelectedHighlight(null);
    setSelectedUnderline(null);
    setSelectedDrawing(null);
    selectedHighlightRef.current = null;
    selectedUnderlineRef.current = null;
    setContextMenuVisible(false);
    setSelectionRect(null);
    latestSelectionPayloadRef.current = null;
    closeTranslationPopover();
    handleNoteEditorClose();
    try {
      window.getSelection()?.removeAllRanges();
    } catch {
      // ignore
    }
    clearCustomOverlays();

    const restorePageAfterRotation = () => {
      const viewer = pdfViewerRef.current;
      if (!viewer) return;

      syncPageRef.current = true;

      try {
        if (viewer.currentPageNumber !== pageNumber) {
          viewer.currentPageNumber = pageNumber;
        }
      } catch {
        // currentPageNumber is the primary navigation path.
      }

      const scrollContainer = containerRef.current;
      const pageEl = viewerRef.current?.querySelector(
        `.page[data-page-number="${pageNumber}"]`,
      );
      if (scrollContainer && pageEl?.offsetHeight > 0) {
        scrollContainer.scrollTop =
          pageEl.offsetTop + pageOffsetRatio * pageEl.offsetHeight;
      }

      currentPageRef.current = pageNumber;
      if (useReaderStore.getState().currentPage !== pageNumber) {
        setCurrentPage(pageNumber);
      }
    };

    const refreshOverlays = () => {
      renderReadOnlyOverlaysRef.current();
      syncPageLoadingIndicators();
    };

    // PDF.js re-renders pages asynchronously after pagesRotation changes.
    // Restore scroll immediately; paint overlays only after settle so we
    // never show transformed annotations on a still-unrotated canvas.
    restorePageAfterRotation();
    requestAnimationFrame(() => {
      restorePageAfterRotation();
    });
    const timeoutId = window.setTimeout(() => {
      restorePageAfterRotation();
      rotationSettlingRef.current = false;
      refreshOverlays();
      requestAnimationFrame(() => {
        syncPageRef.current = false;
      });
    }, 120);
    return () => {
      window.clearTimeout(timeoutId);
      syncPageRef.current = false;
      rotationSettlingRef.current = false;
    };
  }, [
    clearCustomOverlays,
    closeTranslationPopover,
    handleNoteEditorClose,
    pdfDocument,
    pdfRotation,
    documentType,
    syncPageLoadingIndicators,
    setCurrentPage,
  ]);

  useEffect(() => {
    const pdfViewer = pdfViewerRef.current;
    if (!pdfViewer || !Number.isFinite(scale) || scale <= 0) {
      return;
    }

    if (Math.abs((pdfViewer.currentScale || 0) - scale) < PAGE_SYNC_EPSILON) {
      return;
    }

    const container = containerRef.current;
    let targetScrollLeft = null;
    let targetScrollTop = null;

    // Wheel/pinch set zoomFocalPointRef. DisplayMenu / keyboard / fit-mode
    // only update scale — synthesize a viewport-center anchor so the page
    // does not jump when PDF.js reflows.
    let zoomPoint = zoomFocalPointRef.current;
    const hasMatchingFocal =
      zoomPoint && Math.abs(zoomPoint.newScale - scale) < 0.001;

    if (!hasMatchingFocal && container) {
      const oldScale =
        Number(pdfViewer.currentScale) > 0 ? Number(pdfViewer.currentScale) : 1;
      zoomPoint = {
        clientX: container.clientWidth / 2,
        clientY: container.clientHeight / 2,
        oldScale,
        newScale: scale,
      };
      zoomFocalPointRef.current = zoomPoint;
    }

    if (
      zoomPoint &&
      Math.abs(zoomPoint.newScale - scale) < 0.001 &&
      container
    ) {
      const { clientX, clientY, oldScale, newScale } = zoomPoint;
      const ratio = newScale / oldScale;

      const absoluteX = container.scrollLeft + clientX;
      const absoluteY = container.scrollTop + clientY;

      const pageElements = Array.from(
        viewerRef.current?.querySelectorAll(".page") || [],
      );
      let targetPage = null;

      for (const pageEl of pageElements) {
        if (
          absoluteY >= pageEl.offsetTop &&
          absoluteY <= pageEl.offsetTop + pageEl.offsetHeight
        ) {
          targetPage = pageEl;
          break;
        }
      }

      if (!targetPage && pageElements.length > 0) {
        targetPage = pageElements.reduce((prev, curr) => {
          return Math.abs(curr.offsetTop + curr.offsetHeight / 2 - absoluteY) <
            Math.abs(prev.offsetTop + prev.offsetHeight / 2 - absoluteY)
            ? curr
            : prev;
        });
      }

      if (targetPage) {
        zoomPoint.pageNumber = targetPage.getAttribute("data-page-number");
        zoomPoint.localX = absoluteX - targetPage.offsetLeft;
        zoomPoint.localY = absoluteY - targetPage.offsetTop;
        zoomPoint.ratio = ratio;
      } else {
        targetScrollLeft = absoluteX * ratio - clientX;
        targetScrollTop = absoluteY * ratio - clientY;
      }
    }

    syncScaleRef.current = true;
    pdfViewer.currentScale = scale;

    if (zoomPoint && zoomPoint.pageNumber && container) {
      const scaledPage = container.querySelector(
        `.page[data-page-number="${zoomPoint.pageNumber}"]`,
      );
      if (scaledPage) {
        targetScrollLeft =
          scaledPage.offsetLeft +
          zoomPoint.localX * zoomPoint.ratio -
          zoomPoint.clientX;
        targetScrollTop =
          scaledPage.offsetTop +
          zoomPoint.localY * zoomPoint.ratio -
          zoomPoint.clientY;
      }
    }

    if (targetScrollLeft !== null && targetScrollTop !== null && container) {
      container.scrollLeft = targetScrollLeft;
      container.scrollTop = targetScrollTop;
      // Keep values for rAF override
      zoomPoint.finalScrollLeft = targetScrollLeft;
      zoomPoint.finalScrollTop = targetScrollTop;
    }

    requestAnimationFrame(() => {
      if (zoomPoint && zoomPoint.finalScrollLeft !== undefined && container) {
        container.scrollLeft = zoomPoint.finalScrollLeft;
        container.scrollTop = zoomPoint.finalScrollTop;
        zoomFocalPointRef.current = null;
      }
      syncScaleRef.current = false;
      renderReadOnlyOverlaysRef.current();
    });
  }, [scale]);

  useEffect(() => {
    if (!isChunked || totalPages > 0 || !pdfDocument) {
      return;
    }
    setTotalPages(pdfDocument.numPages || 0);
  }, [isChunked, totalPages, pdfDocument, setTotalPages]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (evt) => {
      if (!evt.ctrlKey && !evt.metaKey) return;
      evt.preventDefault();

      const delta = -evt.deltaY * 0.002;
      const current =
        Number(scaleRef.current) > 0 ? Number(scaleRef.current) : 1;
      const next = Math.max(minScale, Math.min(5.0, current + delta));

      if (Math.abs(next - current) > 0.001) {
        const rect = container.getBoundingClientRect();
        zoomFocalPointRef.current = {
          clientX: evt.clientX - rect.left,
          clientY: evt.clientY - rect.top,
          oldScale: current,
          newScale: next,
        };
        scaleRef.current = next;
        setScale(next);
      }
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", onWheel);
    };
  }, [minScale, setScale]);

  useEffect(() => {
    const isTouchCapable =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    if (!isTouchCapable) return;

    const container = containerRef.current;
    if (!container) return;

    let startDist = 0;
    let startScale = 1;
    let lastRatio = 1;
    let isPinching = false;
    let pinchCenter = { x: 0, y: 0 };

    const getDistance = (touches) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );

    const onTouchStart = (evt) => {
      if (evt.touches.length !== 2) return;
      isPinching = true;
      startDist = getDistance(evt.touches);
      startScale = Number(scaleRef.current) > 0 ? Number(scaleRef.current) : 1;
      lastRatio = 1;

      const rect = container.getBoundingClientRect();
      pinchCenter = {
        x: (evt.touches[0].clientX + evt.touches[1].clientX) / 2,
        y: (evt.touches[0].clientY + evt.touches[1].clientY) / 2,
      };

      const viewer = viewerRef.current;
      if (viewer) {
        const sx = container.scrollLeft + pinchCenter.x - rect.left;
        const sy = container.scrollTop + pinchCenter.y - rect.top;
        viewer.style.transformOrigin = `${sx}px ${sy}px`;
        viewer.style.transition = "none";
      }
    };

    const onTouchMove = (evt) => {
      if (!isPinching || evt.touches.length !== 2) return;
      if (evt.cancelable) evt.preventDefault();

      const dist = getDistance(evt.touches);
      if (startDist > 0) {
        let rawRatio = dist / startDist;
        const maxRatio = 5.0 / startScale;
        const minRatio = minScale / startScale;
        lastRatio = Math.max(minRatio, Math.min(maxRatio, rawRatio));

        const viewer = viewerRef.current;
        if (viewer) {
          viewer.style.transform = `scale(${lastRatio})`;
        }
      }
    };

    const onTouchEnd = (evt) => {
      if (!isPinching || evt.touches.length >= 2) return;
      isPinching = false;

      const viewer = viewerRef.current;
      if (viewer) {
        viewer.style.transform = "";
        viewer.style.transformOrigin = "";
      }

      const current = startScale;
      const next = Math.max(minScale, Math.min(5.0, startScale * lastRatio));

      if (Math.abs(next - scaleRef.current) > 0.01) {
        const rect = container.getBoundingClientRect();
        zoomFocalPointRef.current = {
          clientX: pinchCenter.x - rect.left,
          clientY: pinchCenter.y - rect.top,
          oldScale: current,
          newScale: next,
        };
        scaleRef.current = next;
        setScale(next);
      }
    };

    const onGestureStart = (evt) => {
      evt.preventDefault();
    };
    const onGestureChange = (evt) => {
      evt.preventDefault();
    };

    container.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);
    container.addEventListener("touchcancel", onTouchEnd);

    container.addEventListener("gesturestart", onGestureStart, {
      passive: false,
    });
    container.addEventListener("gesturechange", onGestureChange, {
      passive: false,
    });
    document.addEventListener("gesturestart", onGestureStart, {
      passive: false,
    });

    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
      container.removeEventListener("gesturestart", onGestureStart);
      container.removeEventListener("gesturechange", onGestureChange);
      document.removeEventListener("gesturestart", onGestureStart);
    };
  }, [minScale, setScale]);

  useEffect(() => {
    renderReadOnlyOverlays();
  }, [renderReadOnlyOverlays, totalPages]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const trackInteractionPage = (evt) => {
      const pageNumber = getPageNumberFromDomNode(evt.target);
      if (pageNumber) {
        lastInteractionPdfPageRef.current = pageNumber;
      }
    };

    viewer.addEventListener("mousedown", trackInteractionPage, true);
    viewer.addEventListener("touchstart", trackInteractionPage, true);

    return () => {
      viewer.removeEventListener("mousedown", trackInteractionPage, true);
      viewer.removeEventListener("touchstart", trackInteractionPage, true);
    };
  }, [pdfPagesReady]);

  useEffect(() => {
    const handleSelectAllWithinPdf = (evt) => {
      if (!(evt.ctrlKey || evt.metaKey) || evt.key?.toLowerCase() !== "a") {
        return;
      }
      if (isEditableKeyboardTarget(evt.target)) return;

      const pdfViewer = pdfViewerRef.current;
      const viewer = viewerRef.current;
      if (!pdfViewer || !viewer) return;
      if (readViewerRotation(pdfViewer, pdfRotation) !== 0) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }

      const pageNumber = resolveSelectAllPageNumber({
        pdfViewer,
        evt,
        lastInteractionPage: lastInteractionPdfPageRef.current,
        currentPageFallback: currentPageRef.current,
      });

      let textLayer = getPdfTextLayerForPage(pdfViewer, viewer, pageNumber);
      if (!textLayer) {
        const visibleLayers = Array.from(
          viewer.querySelectorAll(".textLayer"),
        ).filter((layer) => !layer.hidden);
        textLayer =
          visibleLayers.find(
            (layer) =>
              layer.closest?.(".page")?.getAttribute("data-page-number") ===
              String(pageNumber),
          ) ||
          visibleLayers[0] ||
          null;
      }

      if (!textLayer || textLayer.hidden) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }

      const applied = applySelectAllOnPdfTextLayer(textLayer);
      evt.preventDefault();
      evt.stopPropagation();

      if (!applied) return;

      const repaintSelection = () => {
        textLayer.classList.add("selecting");
        renderLiveSelectionOverlayRef.current?.();
      };
      // PDF.js removes .selecting on keyup; repaint after the key event finishes.
      requestAnimationFrame(repaintSelection);
      setTimeout(repaintSelection, 0);
    };

    window.addEventListener("keydown", handleSelectAllWithinPdf, true);
    return () => {
      window.removeEventListener("keydown", handleSelectAllWithinPdf, true);
    };
  }, [pdfPagesReady, pdfRotation]);

  // Render a merged overlay for live selection so PDF.js span boundaries do not
  // produce overlapping or darker native selection paint.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const onMouseDownInTextLayer = (evt) => {
      const target = evt.target;
      if (!(target instanceof Element)) return;
      if (
        target.classList.contains("textLayer") ||
        target.classList.contains("endOfContent")
      ) {
        evt.preventDefault();
      }
    };

    const scheduleRender = () => {
      window.cancelAnimationFrame(selectionOverlayRafRef.current);
      selectionOverlayRafRef.current = window.requestAnimationFrame(
        renderLiveSelectionOverlay,
      );
    };

    scheduleRender();

    viewer.addEventListener("mousedown", onMouseDownInTextLayer);
    document.addEventListener("selectionchange", scheduleRender);

    const container = containerRef.current;
    const viewport = window.visualViewport;
    container?.addEventListener("scroll", scheduleRender, { passive: true });
    window.addEventListener("resize", scheduleRender);
    viewport?.addEventListener("resize", scheduleRender);
    viewport?.addEventListener("scroll", scheduleRender);

    return () => {
      viewer.removeEventListener("mousedown", onMouseDownInTextLayer);
      document.removeEventListener("selectionchange", scheduleRender);
      container?.removeEventListener("scroll", scheduleRender);
      window.removeEventListener("resize", scheduleRender);
      viewport?.removeEventListener("resize", scheduleRender);
      viewport?.removeEventListener("scroll", scheduleRender);
      window.cancelAnimationFrame(selectionOverlayRafRef.current);
      viewer?.querySelectorAll(".igp-live-selection-layer").forEach((el) => {
        el.innerHTML = "";
      });
    };
  }, [renderLiveSelectionOverlay]);

  // Touch/coarse: neutralize PDF.js endOfContent mutation + lock scroll while a
  // PDF text selection is active so zoomed pages do not pan under handles.
  // Skip fine-pointer desktops (incl. touch-capable laptops using a mouse).
  useEffect(() => {
    const isCoarsePointer =
      typeof window !== "undefined" &&
      Boolean(window.matchMedia?.("(pointer: coarse)")?.matches);
    if (!pdfPagesReady || (!isMobile && !isCoarsePointer)) {
      return undefined;
    }
    if (!isTouchCapableDevice()) return undefined;

    const viewer = viewerRef.current;
    const container = containerRef.current;
    if (!viewer || !container) return undefined;

    const PAN_SLOP_PX = 12;
    let provisionalScroll = null;
    let lockedScroll = null;
    let touchOnText = false;
    let touchStartX = 0;
    let touchStartY = 0;

    const readScroll = () => ({
      left: container.scrollLeft,
      top: container.scrollTop,
    });

    const selectionIsPdfText = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.toString().trim()) {
        return false;
      }
      return (
        isNodeInsidePdfTextLayer(selection.anchorNode) &&
        isNodeInsidePdfTextLayer(selection.focusNode)
      );
    };

    const hardenAll = () => hardenAllTextLayerEndOfContent(viewer);

    hardenAll();

    const onTextLayerRendered = () => {
      requestAnimationFrame(hardenAll);
    };
    const eventBus = eventBusRef.current;
    eventBus?.on?.("textlayerrendered", onTextLayerRendered);

    const onSelectionChange = () => {
      // Run after PDF.js's own selectionchange listener in the same turn.
      hardenAll();
      if (selectionIsPdfText()) {
        lockedScroll = provisionalScroll || lockedScroll || readScroll();
        provisionalScroll = null;
      } else if (!touchOnText) {
        lockedScroll = null;
      }
    };

    const onTouchStart = (evt) => {
      if (evt.touches?.length > 1) {
        // Pinch / multi-touch — never hold scroll lock.
        touchOnText = false;
        provisionalScroll = null;
        lockedScroll = null;
        return;
      }
      const target = evt.target;
      if (!(target instanceof Element) || !target.closest(".textLayer")) {
        touchOnText = false;
        provisionalScroll = null;
        return;
      }
      const touch = evt.touches?.[0];
      touchOnText = true;
      touchStartX = touch?.clientX || 0;
      touchStartY = touch?.clientY || 0;
      provisionalScroll = readScroll();
    };

    const onTouchMove = (evt) => {
      if (!touchOnText || lockedScroll || !provisionalScroll) return;
      if (selectionIsPdfText()) return;
      const touch = evt.touches?.[0];
      if (!touch) return;
      const dx = Math.abs(touch.clientX - touchStartX);
      const dy = Math.abs(touch.clientY - touchStartY);
      if (dx > PAN_SLOP_PX || dy > PAN_SLOP_PX) {
        // One-finger pan starting on text — do not lock.
        provisionalScroll = null;
        touchOnText = false;
      }
    };

    const onTouchEnd = () => {
      touchOnText = false;
      provisionalScroll = null;
      if (!selectionIsPdfText()) {
        lockedScroll = null;
      }
    };

    const onScroll = () => {
      if (!lockedScroll) return;
      if (!selectionIsPdfText()) {
        lockedScroll = null;
        return;
      }
      if (
        container.scrollLeft !== lockedScroll.left ||
        container.scrollTop !== lockedScroll.top
      ) {
        container.scrollLeft = lockedScroll.left;
        container.scrollTop = lockedScroll.top;
      }
    };

    document.addEventListener("selectionchange", onSelectionChange);
    container.addEventListener("scroll", onScroll, { passive: true });
    viewer.addEventListener("touchstart", onTouchStart, {
      passive: true,
      capture: true,
    });
    viewer.addEventListener("touchmove", onTouchMove, {
      passive: true,
      capture: true,
    });
    viewer.addEventListener("touchend", onTouchEnd, {
      passive: true,
      capture: true,
    });
    viewer.addEventListener("touchcancel", onTouchEnd, {
      passive: true,
      capture: true,
    });

    return () => {
      eventBus?.off?.("textlayerrendered", onTextLayerRendered);
      document.removeEventListener("selectionchange", onSelectionChange);
      container.removeEventListener("scroll", onScroll);
      viewer.removeEventListener("touchstart", onTouchStart, true);
      viewer.removeEventListener("touchmove", onTouchMove, true);
      viewer.removeEventListener("touchend", onTouchEnd, true);
      viewer.removeEventListener("touchcancel", onTouchEnd, true);
    };
  }, [pdfPagesReady, isMobile]);

  useEffect(() => {
    const viewer = viewerRef.current;
    const container = containerRef.current;
    if (
      !viewer ||
      !container ||
      textboxEnabled ||
      noteEditorVisible ||
      Boolean(editingTextboxId)
    ) {
      return;
    }

    let isMouseDown = false;
    let isTouchInProgress = false;

    const shouldKeepAnnotationMenu = () =>
      Boolean(
        selectedHighlightRef.current ||
        selectedUnderlineRef.current ||
        Date.now() < suppressSelectionMenuCloseUntilRef.current,
      );

    const onSelectionFinished = () => {
      let attempts = 0;

      const processSelection = () => {
        // While rotated, never open the selection/annotation toolbar.
        if (!isSelectionUiEnabled()) {
          setContextMenuVisible(false);
          setSelectionRect(null);
          latestSelectionPayloadRef.current = null;
          try {
            const blockedSelection = window.getSelection();
            // Only clear PDF-viewer selections; keep AI sidebar / notes / etc.
            if (isSelectionAnchoredIn(blockedSelection, viewer)) {
              blockedSelection.removeAllRanges();
            }
          } catch {
            // ignore
          }
          return;
        }

        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) {
          if (attempts < 2) {
            attempts += 1;
            requestAnimationFrame(processSelection);
            return;
          }
          // Keep annotation selection menu open when clicking existing highlights/underlines
          if (!shouldKeepAnnotationMenu()) {
            setContextMenuVisible(false);
          }
          return;
        }

        const selectedText = selection.toString().trim();
        if (!selectedText) {
          if (attempts < 2) {
            attempts += 1;
            requestAnimationFrame(processSelection);
            return;
          }
          if (!shouldKeepAnnotationMenu()) {
            setContextMenuVisible(false);
          }
          return;
        }

        // Live text selection takes over — clear annotation selection
        selectedHighlightRef.current = null;
        selectedUnderlineRef.current = null;
        setSelectedHighlight(null);
        setSelectedUnderline(null);

        const range = selection.getRangeAt(0);
        if (!isSelectionInsidePdfTextLayer(selection)) {
          // Clear only in-viewer non-text-layer selections (page chrome).
          // Do not steal selections from the AI sidebar or other UI.
          if (isSelectionAnchoredIn(selection, viewer)) {
            selection.removeAllRanges();
          }
          if (!shouldKeepAnnotationMenu()) {
            setContextMenuVisible(false);
            setSelectionRect(null);
          }
          latestSelectionPayloadRef.current = null;
          return;
        }

        const commonNode = range.commonAncestorContainer;
        const commonElement =
          commonNode?.nodeType === Node.ELEMENT_NODE
            ? commonNode
            : commonNode?.parentElement;

        if (!commonElement || !viewer.contains(commonElement)) {
          if (!shouldKeepAnnotationMenu()) {
            setContextMenuVisible(false);
          }
          return;
        }

        const rect = range.getBoundingClientRect();
        if (!rect || rect.width <= 0 || rect.height <= 0) {
          if (!shouldKeepAnnotationMenu()) {
            setContextMenuVisible(false);
          }
          return;
        }

        setSelectionRect({
          left: rect.left,
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height,
        });

        // Use same y-offset logic native iOS/Android tend to like (or just generic +10)
        setContextMenuPosition({
          x: rect.left + rect.width / 2,
          y: rect.bottom + 10,
        });

        const fragments = getSelectionFragments(selection, viewer);
        if (!fragments.length) {
          setContextMenuVisible(false);
          latestSelectionPayloadRef.current = null;
          return;
        }

        latestSelectionPayloadRef.current = { selectedText, fragments };

        // Desktop tool modes still auto-apply. On mobile, selection only opens
        // the bottom-docked toolbar — nothing is applied until the user acts.
        if (
          !isMobile &&
          (notesEnabled || highlightEnabled || underlineEnabled)
        ) {
          if (notesEnabled && !highlightEnabled && !underlineEnabled) {
            suppressNotesClickUntilRef.current = Date.now() + 400;
            createNoteFromSelection(
              { selectedText, fragments },
              {
                anchorRect: {
                  left: rect.left,
                  top: rect.top,
                  right: rect.right,
                  bottom: rect.bottom,
                  width: rect.width,
                  height: rect.height,
                },
              },
            );
          } else {
            const tool = highlightEnabled ? "highlight" : "underline";
            applySelectionTool(tool, { selectedText, fragments });
          }

          const selectionToClear = window.getSelection();
          if (selectionToClear) selectionToClear.removeAllRanges();
          setContextMenuVisible(false);
          setSelectionRect(null);
          latestSelectionPayloadRef.current = null;
          return;
        }

        setSelectedHighlight(null);
        setSelectedUnderline(null);
        setContextMenuVisible(true);
      };

      requestAnimationFrame(processSelection);
    };

    let selectionChangeTimer;
    const onSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.toString().trim()) {
        // Do not dismiss the annotation toolbar when an existing highlight/underline is selected
        if (
          !selectedHighlightRef.current &&
          !selectedUnderlineRef.current &&
          Date.now() >= suppressSelectionMenuCloseUntilRef.current
        ) {
          setContextMenuVisible(false);
        }
        latestSelectionPayloadRef.current = null;
      } else {
        if (isMouseDown && !isTouchInProgress) return;

        clearTimeout(selectionChangeTimer);
        selectionChangeTimer = setTimeout(() => {
          onSelectionFinished();
        }, 600);
      }
    };

    const onViewerScroll = () => {
      // Selection-induced auto-pan on mobile must not tear down the toolbar.
      if (isTouchInProgress) return;
      if (isMobile) {
        const liveSelection = window.getSelection();
        if (
          liveSelection &&
          !liveSelection.isCollapsed &&
          liveSelection.toString().trim() &&
          isSelectionInsidePdfTextLayer(liveSelection)
        ) {
          return;
        }
      }

      setContextMenuVisible(false);
      setSelectionRect(null);
      latestSelectionPayloadRef.current = null;
      setSelectedHighlight(null);
      setSelectedUnderline(null);
      selectedHighlightRef.current = null;
      selectedUnderlineRef.current = null;
      handleNoteEditorClose();
      setSelectedTextboxId(null);
      setEditingTextboxId(null);
      textboxDraftRef.current = "";
    };

    // ── ADD: new handler ──────────────────────────────────────────────────────
    const onMouseDownClearSelection = (evt) => {
      isMouseDown = true;
      const target = evt.target;
      if (!(target instanceof Element)) return;

      // Let ContextMenu's own outside-click handler work undisturbed
      if (target.closest(".context-menu")) return;

      // Annotation hit-testing happens on click; don't race-close the menu here
      if (selectedHighlightRef.current || selectedUnderlineRef.current) {
        return;
      }

      // If clicking a real text span, the browser replaces the selection naturally
      const isTextSpan =
        (target.tagName === "SPAN" || target.tagName === "BR") &&
        !!target.closest(".textLayer") &&
        !target.classList.contains("endOfContent");

      if (!isTextSpan) {
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed) {
          sel.removeAllRanges();
          // selectionchange fires → onSelectionChange clears the menu + overlay
        }
      }
    };
    // ─────────────────────────────────────────────────────────────────────────

    const handleMouseUp = () => {
      isMouseDown = false;
      if (isTouchInProgress) return;
      onSelectionFinished();
    };

    const handleTouchStart = () => {
      isTouchInProgress = true;
      isMouseDown = true;
      // Hide the toolbar while the user drags/adjusts selection handles so it
      // does not sit on top of the native handles. touchend reopens it.
      setContextMenuVisible(false);
    };

    const handleTouchEnd = () => {
      isTouchInProgress = false;
      isMouseDown = false;
      setTimeout(() => {
        onSelectionFinished();
      }, 100);
    };

    viewer.addEventListener("mouseup", handleMouseUp);
    viewer.addEventListener("touchstart", handleTouchStart, { passive: true });
    viewer.addEventListener("touchend", handleTouchEnd, { passive: true });
    viewer.addEventListener("mousedown", onMouseDownClearSelection); // ADD
    container.addEventListener("scroll", onViewerScroll, { passive: true });
    document.addEventListener("selectionchange", onSelectionChange);

    // If text is already selected when we enable the tool, apply it immediately
    // (desktop tool modes only — mobile always uses the selection toolbar).
    if (!isMobile && (highlightEnabled || underlineEnabled)) {
      onSelectionFinished();
    }

    return () => {
      clearTimeout(selectionChangeTimer);
      viewer.removeEventListener("mouseup", handleMouseUp);
      viewer.removeEventListener("touchstart", handleTouchStart);
      viewer.removeEventListener("touchend", handleTouchEnd);
      viewer.removeEventListener("mousedown", onMouseDownClearSelection); // ADD
      container.removeEventListener("scroll", onViewerScroll);
      document.removeEventListener("selectionchange", onSelectionChange);
    };
  }, [
    isSelectionUiEnabled,
    highlightEnabled,
    underlineEnabled,
    notesEnabled,
    isMobile,
    createNoteFromSelection,
    getSelectionFragments,
    isSelectionInsidePdfTextLayer,
    noteEditorVisible,
    textboxEnabled,
    editingTextboxId,
    handleNoteEditorClose,
    applySelectionTool,
  ]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (
      !viewer ||
      !notesEnabled ||
      highlightEnabled ||
      underlineEnabled ||
      drawingEnabled ||
      textboxEnabled ||
      !isSelectionUiEnabled()
    ) {
      return;
    }

    const onViewerClickForNotes = (evt) => {
      if (Date.now() < suppressNotesClickUntilRef.current) return;
      if (noteEditorVisible) return;
      const target = evt.target;
      if (!(target instanceof Element)) return;

      if (
        target.closest(".igp-note-editor") ||
        target.closest(".igp-ro-note-indicator") ||
        target.closest(".note-indicator") ||
        target.closest(".igp-ro-textbox")
      ) {
        return;
      }

      const pageEl = target.closest(".page[data-page-number]");
      if (!pageEl) return;
      const pageNumber = Number(pageEl.getAttribute("data-page-number") || 0);
      if (!pageNumber) return;

      const selection = window.getSelection();
      const selectedText = selection ? selection.toString().trim() : "";
      if (selectedText) {
        const fragments = getSelectionFragments(selection, viewer);
        if (fragments.length > 0) {
          let anchorRect = null;
          try {
            const range = selection.getRangeAt(0);
            const rect = range?.getBoundingClientRect?.();
            if (rect && rect.width > 0 && rect.height > 0) {
              anchorRect = {
                left: rect.left,
                top: rect.top,
                right: rect.right,
                bottom: rect.bottom,
                width: rect.width,
                height: rect.height,
              };
            }
          } catch {
            anchorRect = null;
          }

          createNoteFromSelection({ selectedText, fragments }, { anchorRect });
          suppressNotesClickUntilRef.current = Date.now() + 400;
          selection.removeAllRanges();
          latestSelectionPayloadRef.current = null;
          setSelectionRect(null);
          setContextMenuVisible(false);
          evt.stopPropagation();
          return;
        }
      }

      const canvas = pageEl.querySelector("canvas");
      if (!canvas) return;

      setCurrentPage(pageNumber);
      setContextMenuVisible(false);
      setSelectionRect(null);
      latestSelectionPayloadRef.current = null;
      placeNoteEditor(
        {
          x: evt.clientX,
          y: evt.clientY,
        },
        { centerOnX: false, offsetY: 0 },
      );
      setNoteEditorText("");
      const rect = pageEl.getBoundingClientRect();
      const displayX = (evt.clientX - rect.left) / rect.width;
      const displayY = (evt.clientY - rect.top) / rect.height;
      const rotation = normalizeRotation(
        pdfViewerRef.current?.pagesRotation ?? pdfRotation,
      );
      const coordinates = unrotateNormalizedPoint(displayX, displayY, rotation);
      setNoteEditorPayload({
        selectedText: "",
        fragments: [{ pageNumber }],
        coordinates,
      });
      setNoteEditorMode("create");
      setNoteEditorNoteId(null);
      setNoteEditorPageNumber(pageNumber);
      setNoteEditorSelectedText("");
      setExpandedSelectedText(false);
      setNoteEditorVisible(true);
      evt.stopPropagation();
    };

    viewer.addEventListener("click", onViewerClickForNotes, true);
    return () => {
      viewer.removeEventListener("click", onViewerClickForNotes, true);
    };
  }, [
    isSelectionUiEnabled,
    createNoteFromSelection,
    drawingEnabled,
    getSelectionFragments,
    highlightEnabled,
    placeNoteEditor,
    noteEditorVisible,
    notesEnabled,
    pdfRotation,
    setCurrentPage,
    textboxEnabled,
    underlineEnabled,
  ]);

  return (
    <div
      className={`pdfjs-selection-viewer${isDoublePageMode ? " double-page-mode" : " single-page-mode"}${
        isSafariSelectionGuardEnabled ? " safari-selection-guard" : ""
      }${isPdfRotated ? " is-pdf-rotated" : ""}`}
      ref={containerRef}
    >
      <div className="pdfViewer" ref={viewerRef}></div>
      <ContextMenu
        isVisible={contextMenuVisible && !isPdfRotated}
        position={contextMenuPosition}
        selectionRect={selectionRect}
        onClose={() => {
          setContextMenuVisible(false);
          setSelectionRect(null);
          // Keep payload across handle-drags: outside touchstart hides the menu
          // while the native selection is still active; touchend/selectionchange
          // refresh it. Clear only when the selection is gone.
          const stillSelected = Boolean(
            window.getSelection()?.toString()?.trim(),
          );
          if (!stillSelected) {
            latestSelectionPayloadRef.current = null;
          }
          setSelectedHighlight(null);
          setSelectedUnderline(null);
          selectedHighlightRef.current = null;
          selectedUnderlineRef.current = null;
        }}
        selectionToolbar={selectionToolbar}
      />
      <TranslationPopover
        isVisible={translationState.isVisible && !isPdfRotated}
        position={translationState.position}
        sourceText={translationState.sourceText}
        translatedText={translationState.translatedText}
        isLoading={translationState.isLoading}
        error={translationState.error}
        onClose={closeTranslationPopover}
      />

      <Pullouts enabled={pdfPagesReady} />

      {noteEditorVisible && !isPdfRotated && (
        <div
          className="igp-note-editor note-input-overlay"
          ref={noteEditorPanelRef}
          style={{
            left: `${noteEditorPosition.x}px`,
            top: `${noteEditorPosition.y}px`,
          }}
        >
          <div className="igp-note-editor-header note-input-header">
            <span className="note-title">
              {noteEditorMode === "edit"
                ? t("epub.noteTitleEdit")
                : t("epub.noteTitleAdd")}
              {noteEditorMode === "edit" &&
              noteEditorPageNotes.length > 1 &&
              noteEditorCurrentIndex >= 0
                ? ` (${noteEditorCurrentIndex + 1}/${noteEditorPageNotes.length})`
                : ""}
            </span>
            <button
              type="button"
              className="igp-note-editor-close note-close-icon"
              onClick={handleNoteEditorClose}
              aria-label={t("common.close")}
            >
              <FaTimes />
            </button>
          </div>
          {noteEditorSelectedPreview ? (
            <div className="igp-note-editor-selected-text note-selected-text">
              <div className="note-selected-text-label">
                {t("epub.selectedTextLabel")}
              </div>
              <div className="note-selected-text-content">
                {expandedSelectedText ||
                noteEditorSelectedPreview.length <= 150 ? (
                  `"${noteEditorSelectedPreview}"`
                ) : (
                  <>
                    {`"${noteEditorSelectedPreview.substring(0, 150)}..."`}
                    <button
                      type="button"
                      className="note-selected-text-expand"
                      onClick={(evt) => {
                        evt.stopPropagation();
                        setExpandedSelectedText(true);
                      }}
                    >
                      {t("common.showMore")}
                    </button>
                  </>
                )}
                {expandedSelectedText &&
                noteEditorSelectedPreview.length > 150 ? (
                  <button
                    type="button"
                    className="note-selected-text-collapse"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      setExpandedSelectedText(false);
                    }}
                  >
                    {t("common.showLess")}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
          <textarea
            ref={noteTextareaRef}
            className="igp-note-editor-textarea note-input-textarea"
            value={noteEditorText}
            onChange={(e) => setNoteEditorText(e.target.value)}
            onKeyDown={handleNoteEditorKeyDown}
            placeholder={
              noteEditorSelectedPreview
                ? t("epub.notePlaceholderSelected")
                : t("epub.notePlaceholder")
            }
          />
          <div className="igp-note-editor-actions note-input-footer">
            <div className="note-footer-left">
              {noteEditorMode === "edit" && noteEditorPageNotes.length > 1 ? (
                <div className="note-nav-controls">
                  <button
                    type="button"
                    className="note-nav-icon"
                    onClick={() => handleNavigateNote(-1)}
                    disabled={isNoteSaving}
                    aria-label={t("rightToolbar.previousPage")}
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    type="button"
                    className="note-nav-icon"
                    onClick={() => handleNavigateNote(1)}
                    disabled={isNoteSaving}
                    aria-label={t("rightToolbar.nextPage")}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="note-submit-button"
              onClick={handleNoteSave}
              disabled={isNoteSaving || !noteEditorText.trim()}
            >
              {noteEditorMode === "edit"
                ? t("epub.noteActionSave")
                : t("epub.noteActionCreate")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfViewerNew;
